// @ts-nocheck
import React, { useState, useEffect, useRef, useMemo, useCallback, createContext, useContext } from "react";
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBusiness } from "../contexts/BusinessContext";
import { useAuth } from '../hooks/useAuth';
import { MessageCircle, Mail, Phone, PhoneCall, Clock, User, Edit, Plus, ChevronDown, ChevronRight, Building2, Calendar, CheckCircle, FileText, Eye, ArrowLeft, Search, X, Download, Ban, Shield, Send, Sparkles, Star, PenLine, SquarePen } from "lucide-react";
import { supabase } from "../lib/supabase";
import EditCustomerModal from "../components/manage/EditCustomerModal";
import CustomerModal from "../components/CustomerModal";
import CustomerDetailsModal from "../components/CustomerDetailsModal";
import BookingInfoModal from "../components/BookingInfoModal";
import BetaPaymentsModal from "../components/BetaPaymentsModal";
import ProposalDetailsModal from "../components/ProposalDetailsModal";
import { Customer, findExistingCustomer } from "../lib/customers";
import { agentOutcomeLine, type AgentCollectedData } from "../services/callHistoryService";
import { sendSMS } from "../lib/messaging";
import { sendGmailEmail } from "../lib/email";
import {
  getMessageConversations,
  getUnreadMessageConversations,
  mergeRecentAndUnreadConversations,
  type ConversationSummary,
} from "../lib/messageConversations";
import {
  isCoreMessagesEnabled,
  fetchCoreInbox,
  fetchCoreThread,
  conversationKeyForCard,
  coreMarkItemsRead,
  coreMarkItemsUnread,
  coreMarkConversationRead,
  coreMarkAllRead,
  PHONE_READ_KINDS,
  type CoreReadKind,
  resolveCurrentSender,
  phoneKey,
} from "../lib/messagesCore";

import { EmailReplyComposer } from "../components/email/EmailReplyComposer";
import { EmailDisplay } from "../components/email/EmailDisplay";
import SenderBadge, { senderOf } from "../components/messages/SenderBadge";
import { SMSReplyComposer } from "../components/SMSReplyComposer";
import { parseEmailContent, getEmailPreview } from "../lib/emailParser";
import { getCustomerMilestones, BookingMilestone } from "../lib/bookingMilestones";
import { getBooking, Booking } from "../lib/bookings";
import { getProposal, Proposal } from "../lib/proposals";
import SnippetsModal from "../components/SnippetsModal";
import { useImageViewer, type ViewerImage } from "../components/ImageViewerProvider";
import { mediaUrlsToViewerImages } from "../lib/messageMedia";
import CustomerCreateMenu from "../components/messages/CustomerCreateMenu";
import { GmailReauthBanner, GmailReauthModal } from "../components/messages/GmailReauthPrompt";
import NexusPhoneBalanceNotice from "../components/messages/NexusPhoneBalanceNotice";
import {
  GMAIL_REAUTH_BANNER_CLOSED_KEY,
  GMAIL_REAUTH_BANNER_KEY,
  GMAIL_REAUTH_PENDING_KEY,
  startGmailOAuth,
} from "../lib/gmailConnect";
import TokenPurchaseModal from "../components/TokenPurchaseModal";
import { useKeyboardBottomInset } from "../hooks/useKeyboardBottomInset";
import { registerAssistantSurface } from "../lib/assistant/surface";
import {
  cacheMessages,
  cacheCustomers,
  cacheCallHistory,
  cacheIntegrationStatus, getCachedIntegrationStatus,
  cacheConversationSummaries, getCachedConversationSummaries,
} from "../services/messagesCacheService";
import { getCachedMedia, cacheMedia } from "../services/mediaCacheService";
import {
  getCustomerIdsWithOpenOpportunities,
  getOpenOpportunitiesForCustomerIds,
  getCachedOpenOpportunitiesForCustomerIds,
  primeOpenOpportunitiesCache,
  OPEN_OPPORTUNITIES_CACHE_EVENT,
  resolveCustomerIdsForContact,
  type OpenOpportunitySummary,
} from "../lib/crm/opportunityLinking";
import {
  scheduleOpportunityFollowUp,
  markOpportunityFollowUpComplete,
} from "../lib/crm/opportunityFollowUp";
import { moveOpportunityToStage } from "../lib/crm/opportunityStage";
import { LEAD_CREATED_EVENT } from "../lib/crm/createLead";
import ConversationLeadBanner from "../components/messages/ConversationLeadBanner";
import CallRecordingDetails from "../components/messages/CallRecordingDetails";
import CallbackDetailsModal from "../components/CallbackDetailsModal";
import CrmEmbeddedOpportunityModal from "../components/admin-dashboard/CrmEmbeddedOpportunityModal";
import toast from "react-hot-toast";
import { useLongPress } from "../hooks/useLongPress";
import { hapticMedium } from "../lib/haptics";
import { replaceSnippetVariables as substituteSnippetVariables, contentHasLeadVariables } from './../lib/snippetVariables';

// Conversation composer drafts are cached in-memory per card; we also mirror them
// to sessionStorage so a full page reload (dev HMR, PWA auto-update, etc.) never
// loses what the user was typing.
const CONVERSATION_DRAFT_PREFIX = 'msg-draft:';

function conversationDraftKey(businessId: string | undefined | null, cardId: string): string {
  return `${CONVERSATION_DRAFT_PREFIX}${businessId || 'nb'}:${cardId}`;
}

function readConversationDraft(businessId: string | undefined | null, cardId: string): any | undefined {
  try {
    const raw = sessionStorage.getItem(conversationDraftKey(businessId, cardId));
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
}

function writeConversationDraft(
  businessId: string | undefined | null,
  cardId: string,
  data: unknown,
): void {
  try {
    sessionStorage.setItem(conversationDraftKey(businessId, cardId), JSON.stringify(data));
  } catch {
    // sessionStorage unavailable / quota exceeded — non-critical
  }
}

interface Message {
  id: string;
  business_id: string;
  customer_id: string | null;
  message_type: 'sms' | 'email' | 'call';
  direction: 'inbound' | 'outbound';
  from_number: string;
  to_number: string;
  body: string;
  subject?: string;
  read: 'read' | 'unread';
  created_at: string;
  updated_at: string;
  ai_response?: boolean;
  /** Who sent it (core tables). Bot, resource, owner, automation, phone agent. */
  sender?: { kind: string; id: string | null; name: string | null } | null;
  // Media URLs for SMS messages with photos/videos
  media_urls?: string[] | null;
  // Gmail message id for emails (stored in twilio_message_sid)
  twilio_message_sid?: string | null;
  // Call-specific fields
  call_status?: 'answered' | 'missed' | 'ringing' | 'completed';
  duration_seconds?: number;
  call_sid?: string;
  viewed?: boolean;
  /** AI agent that placed/handled the call (null/undefined = human call) */
  agent_id?: string | null;
  agent_session_id?: string | null;
  agent_outcome?: string | null;
  agent_collected?: AgentCollectedData | null;
  // Call recording & transcription (when the business has recording enabled)
  recording_url?: string | null;
  transcription_text?: string | null;
  transcription_status?: string | null;
}

interface EmailAttachment {
  id: string;
  gmail_id: string;
  filename: string | null;
  content_type: string | null;
  size_bytes: number | null;
  public_url: string;
  is_inline: boolean;
}

interface CommunicationCard {
  id: string; // unique identifier for grouping
  type: 'customer' | 'phone' | 'email';
  customer_id?: string;
  customer_name?: string;
  contact_info: string; // phone or email
  messages: Message[];
  last_message: Message;
  unread_count: number;
  has_unread: boolean;
  /** From conversation summary RPC — available before customers finish loading. */
  is_supplier?: boolean;
  /** True when the card came from a server summary (messages not loaded yet). */
  is_summary?: boolean;
}

// Timeline item that can be either a message or milestone
interface TimelineItem {
  id: string;
  type: 'message' | 'milestone';
  created_at: string;
  message?: Message;
  milestone?: TimelineItem;
}

// In-memory cache for fetched media blob URLs to avoid re-fetching on re-renders
const mediaBlobCache = new Map<string, string>();

/**
 * Build viewer images from raw message media URLs, reusing any already-fetched
 * blob (from {@link mediaBlobCache}) so previously-loaded MMS open instantly.
 */
function buildViewerImages(urls: string[], businessId?: string): ViewerImage[] {
  return mediaUrlsToViewerImages(urls, businessId).map((img) =>
    img.proxy && mediaBlobCache.get(img.proxy)
      ? { src: mediaBlobCache.get(img.proxy)!, download: img.download }
      : img,
  );
}

/** Extract email from "Name <email@domain.com>" or a plain address. */
function extractEmailAddress(contactInfo: string): string {
  const emailMatch = contactInfo.match(/<([^>]+)>/);
  return emailMatch ? emailMatch[1].trim() : contactInfo.trim();
}

// Component to fetch and display media with authentication
const MediaDisplayComponent = ({ 
  proxyUrl, 
  mediaUrl, 
  isImage, 
  index,
  allMediaUrls,
  onOpenModal,
  displayInline = false
}: { 
  proxyUrl: string; 
  mediaUrl: string; 
  isImage: boolean; 
  index: number;
  allMediaUrls: string[];
  onOpenModal: (initialIndex: number) => void;
  displayInline?: boolean;
}) => {
  const cachedSrc = mediaBlobCache.get(proxyUrl) || null;
  const [imageSrc, setImageSrc] = useState<string | null>(cachedSrc);
  const [loading, setLoading] = useState(!cachedSrc);
  const [error, setError] = useState(false);
  const [isActuallyImage, setIsActuallyImage] = useState(isImage);
  const { selectedBusiness } = useBusiness();
  
  // Use a ref to track the last touch time to prevent double-firing
  const lastTouchTime = useRef(0);

  useEffect(() => {
    if (cachedSrc) return;

    // Blob URLs (optimistic messages) and direct public URLs don't need fetching
    const isBlobUrl = proxyUrl.startsWith('blob:');
    const isDirectPublicUrl = proxyUrl.includes('supabase.co/storage') || proxyUrl.includes('supabase.in/storage');
    if (isBlobUrl || isDirectPublicUrl) {
      setImageSrc(proxyUrl);
      setError(false);
      setLoading(false);
      return;
    }

    if (!selectedBusiness?.id) {
      setLoading(false);
      return;
    }

    if (!displayInline && !isImage) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(false);
    setImageSrc(null);
    setIsActuallyImage(isImage);

    let cancelled = false;

    const loadImage = async () => {
      // 1) Try persistent filesystem cache (survives app kill)
      try {
        const diskUrl = await getCachedMedia(proxyUrl);
        if (diskUrl && !cancelled) {
          mediaBlobCache.set(proxyUrl, diskUrl);
          setImageSrc(diskUrl);
          setError(false);
          setLoading(false);
          return;
        }
      } catch { /* cache miss, continue to network */ }

      // 2) Fetch from network
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        const response = await fetch(proxyUrl, {
          headers: {
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
            ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {})
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`);
        }

        const blob = await response.blob();
        if (cancelled) return;

        const contentType = response.headers.get('content-type') || '';
        const actuallyIsImage = contentType.startsWith('image/') || isImage;
        setIsActuallyImage(actuallyIsImage);
        
        if (actuallyIsImage || displayInline) {
          const blobUrl = URL.createObjectURL(blob);
          mediaBlobCache.set(proxyUrl, blobUrl);
          setImageSrc(blobUrl);
          setError(false);
          setLoading(false);
          // Write to persistent cache in background (fire-and-forget)
          cacheMedia(proxyUrl, blob).catch(() => {});
        } else {
          setError(true);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching media:', err);
          setError(true);
          setLoading(false);
        }
      }
    };

    loadImage();
    return () => { cancelled = true; };
  }, [proxyUrl, isImage, selectedBusiness?.id, displayInline, cachedSrc]);

  if (!isImage) {
    return (
      <a
        href={proxyUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-500 hover:underline text-xs flex items-center space-x-1"
        onClick={async (e) => {
          // For non-images, fetch with auth and open in new tab
          e.preventDefault();
          try {
            const { data: { session } } = await supabase.auth.getSession();
            const response = await fetch(proxyUrl, {
              headers: {
                'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
                ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {})
              }
            });
            if (response.ok) {
              const blob = await response.blob();
              const blobUrl = URL.createObjectURL(blob);
              window.open(blobUrl, '_blank');
            }
          } catch (err) {
            console.error('Error opening attachment:', err);
          }
        }}
      >
        <FileText className="h-4 w-4" />
        <span>View attachment {index + 1}</span>
      </a>
    );
  }

  if (loading) {
    return (
      <div className="rounded-lg bg-gray-200 animate-pulse" style={{ width: '240px', height: '180px' }} />
    );
  }

  if (error || !imageSrc) {
    return (
      <a
        href={proxyUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-500 hover:underline text-xs"
      >
        View attachment {index + 1}
      </a>
    );
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    const now = Date.now();
    // Prevent double-tap (within 300ms)
    if (now - lastTouchTime.current < 300) {
      return;
    }
    lastTouchTime.current = now;
    
    e.preventDefault();
    e.stopPropagation();
    onOpenModal(index);
  };

  const handleClick = (e: React.MouseEvent) => {
    // For mouse clicks, check if a touch event just fired (within 300ms)
    const now = Date.now();
    if (now - lastTouchTime.current < 300) {
      // Touch event just fired, ignore this click
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    onOpenModal(index);
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!imageSrc) return;

    const mediaId = mediaUrl.split('/Media/')[1]?.split('/')[0] || `image-${index + 1}`;
    const filename = `photo-${mediaId}.jpg`;

    try {
      const { Capacitor } = await import('@capacitor/core');
      // Native (Capacitor iOS/Android) only — never runs in the browser. Vite still
      // resolves these imports at dev/build time, so @capacitor/filesystem must be
      // installed (see package.json) even when you mostly use web.
      if (Capacitor.isNativePlatform()) {
        const { Filesystem, Directory } = await import('@capacitor/filesystem');
        const { Share } = await import('@capacitor/share');

        // Convert blob URL to base64 for Filesystem
        const response = await fetch(imageSrc);
        const blob = await response.blob();
        const reader = new FileReader();
        const base64 = await new Promise<string>((resolve) => {
          reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
          reader.readAsDataURL(blob);
        });

        const saved = await Filesystem.writeFile({
          path: filename,
          data: base64,
          directory: Directory.Cache,
        });

        await Share.share({
          title: filename,
          url: saved.uri,
        });
        return;
      }
    } catch (err) {
      console.error('Native share failed, falling back:', err);
    }

    // Web fallback
    const link = document.createElement('a');
    link.href = imageSrc;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Always display inline when displayInline is true (we'll check content-type from response)
  if (displayInline) {
    return (
      <div 
        className="rounded-lg overflow-hidden max-w-xs sm:max-w-md mt-2"
        onContextMenu={(e) => e.preventDefault()}
        style={{ 
          touchAction: 'auto',
          WebkitTapHighlightColor: 'transparent'
        }}
      >
        {imageSrc ? (
          <div className="relative" style={{ minHeight: '180px' }}>
            <img
              src={imageSrc}
              alt={`Photo ${index + 1}`}
              className="max-w-full h-auto rounded-lg"
              loading="eager"
              decoding="sync"
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              onError={(e) => {
                console.error('Image failed to load from blob URL:', imageSrc);
                setError(true);
                setLoading(false);
              }}
              onLoad={() => {
                setError(false);
              }}
            />
            {/* Expand button - opens photo preview modal */}
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenModal(index); }}
              className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 active:bg-black/80 rounded-lg transition-colors touch-manipulation"
              title="Expand photo"
              style={{ WebkitTapHighlightColor: 'transparent', minHeight: '36px', minWidth: '36px' }}
            >
              <Eye className="h-4 w-4 text-white" />
            </button>
            {/* Download button */}
            <button
              onClick={handleDownload}
              className="absolute top-2 right-12 p-2 bg-black/50 hover:bg-black/70 active:bg-black/80 rounded-lg transition-colors touch-manipulation"
              title="Download photo"
              style={{ WebkitTapHighlightColor: 'transparent', minHeight: '36px', minWidth: '36px' }}
            >
              <Download className="h-4 w-4 text-white" />
            </button>
          </div>
        ) : loading ? (
          <div className="rounded-lg bg-gray-200 animate-pulse" style={{ width: '240px', height: '180px' }} />
        ) : error ? (
          <div className="rounded-lg bg-gray-100 p-4 text-center text-xs text-gray-500 border border-gray-300">
            <FileText className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p>Failed to load image</p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLoading(true);
                setError(false);
                // Retry fetch
                const fetchImage = async () => {
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    const response = await fetch(proxyUrl, {
                      headers: {
                        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
                        ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {})
                      }
                    });
                    if (response.ok) {
                      const blob = await response.blob();
                      const blobUrl = URL.createObjectURL(blob);
                      setImageSrc(blobUrl);
                      setLoading(false);
                    }
                  } catch (err) {
                    setError(true);
                    setLoading(false);
                  }
                };
                fetchImage();
              }}
              className="mt-2 text-blue-500 hover:underline text-xs"
            >
              Retry
            </button>
          </div>
        ) : !imageSrc ? (
          <div className="rounded-lg bg-gray-100 p-4 text-center text-xs text-gray-500">
            <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center">
              <span>No image loaded</span>
            </div>
          </div>
        ) : isActuallyImage ? (
          <div className="rounded-lg bg-gray-100 p-4 text-center text-xs text-gray-500 border border-gray-300">
            <FileText className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p>Failed to load image</p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLoading(true);
                setError(false);
                // Retry fetch
                const fetchImage = async () => {
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    const response = await fetch(proxyUrl, {
                      headers: {
                        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
                        ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {})
                      }
                    });
                    if (response.ok) {
                      const blob = await response.blob();
                      const blobUrl = URL.createObjectURL(blob);
                      setImageSrc(blobUrl);
                      setLoading(false);
                    }
                  } catch (err) {
                    setError(true);
                    setLoading(false);
                  }
                };
                fetchImage();
              }}
              className="mt-2 text-blue-500 hover:underline text-xs"
            >
              Retry
            </button>
          </div>
        ) : imageSrc ? (
          <img
            src={imageSrc}
            alt={`Photo ${index + 1}`}
            className="max-w-full h-auto rounded-lg hover:opacity-90 transition-opacity"
            style={{ minHeight: '180px' }}
            loading="eager"
            decoding="sync"
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
            onError={() => {
              setError(true);
              setLoading(false);
            }}
            onLoad={() => {
              setError(false);
            }}
          />
        ) : (
          <div className="rounded-lg bg-gray-100 p-4 text-center text-xs text-gray-500">
            <FileText className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p>No image loaded</p>
          </div>
        )}
      </div>
    );
  }

  // Original attachment-style display (for non-images or when displayInline is false)
  if (!isImage) {
    return (
      <a
        href={proxyUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-500 hover:underline text-xs flex items-center space-x-1"
        onClick={async (e) => {
          // For non-images, fetch with auth and open in new tab
          e.preventDefault();
          try {
            const { data: { session } } = await supabase.auth.getSession();
            const response = await fetch(proxyUrl, {
              headers: {
                'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
                ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {})
              }
            });
            if (response.ok) {
              const blob = await response.blob();
              const blobUrl = URL.createObjectURL(blob);
              window.open(blobUrl, '_blank');
            }
          } catch (err) {
            console.error('Error opening attachment:', err);
          }
        }}
      >
        <FileText className="h-4 w-4" />
        <span>View attachment {index + 1}</span>
      </a>
    );
  }

  if (loading) {
    return (
      <div className="rounded-lg bg-gray-200 animate-pulse" style={{ width: '240px', height: '180px' }} />
    );
  }

  if (error || !imageSrc) {
    return (
      <a
        href={proxyUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-500 hover:underline text-xs"
      >
        View attachment {index + 1}
      </a>
    );
  }

  return (
    <div 
      className="rounded-lg overflow-hidden cursor-pointer"
      style={{ minHeight: '180px' }}
      onClick={handleClick}
      onContextMenu={(e) => e.preventDefault()}
    >
      <img
        src={imageSrc}
        alt={`Attachment ${index + 1}`}
        className="max-w-full h-auto rounded-lg hover:opacity-90 transition-opacity"
        loading="eager"
        decoding="sync"
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
      />
    </div>
  );
};

const formatBytes = (bytes?: number | null) => {
  if (!bytes || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, index);
  return `${value.toFixed(value >= 10 || index === 0 ? 0 : 1)} ${units[index]}`;
};

const normalizeEmailSubject = (subject: string) => {
  return decodeEmailSubject(subject)
    .replace(/^(re:|fwd:|fw:)\s*/gi, '')
    .trim()
    .toLowerCase();
};

// Extract just the email address from a "Name <email@domain.com>" string and lowercase it.
// Module-scoped so both the page component and MessagesConversationView can use it.
const normalizeEmail = (email: string): string => {
  if (!email) return '';
  const match = email.match(/<([^>]+)>/);
  if (match) {
    return match[1].toLowerCase().trim();
  }
  return email.toLowerCase().trim();
};

// Stable identity for a conversation card, used to dedupe summary cards against
// the fully-built (last-30-day window) cards so older conversations from the
// server summary aren't shown twice or lost.
const conversationIdentityKey = (card: { type?: string; customer_id?: string | null; contact_info?: string | null }): string => {
  if (card.customer_id) return `c:${card.customer_id}`;
  const contact = card.contact_info || '';
  if (card.type === 'email') return `e:${normalizeEmail(contact)}`;
  const digits = contact.replace(/\D/g, '');
  return `p:${digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits}`;
};

/**
 * Reconcile a locally-built card (messages / calls / callbacks) with the server
 * summary for the same conversation.
 *
 * The inbox no longer bulk-loads SMS/email, so a rebuild triggered by
 * agent_callbacks alone used to "win" over the summary and wipe newer unread
 * SMS previews (e.g. Mark Rose stuck on an Aug 5 callback while today's SMS
 * was unread in the DB). Prefer the newer preview and OR unread flags.
 */
const mergeSummaryIntoBuiltCard = (
  built: CommunicationCard,
  summary: CommunicationCard,
): CommunicationCard => {
  const builtTime = new Date(built.last_message?.created_at || 0).getTime();
  const summaryTime = new Date(summary.last_message?.created_at || 0).getTime();
  const preferSummaryPreview = summaryTime > builtTime;

  return {
    ...built,
    customer_name: built.customer_name || summary.customer_name,
    contact_info: built.contact_info || summary.contact_info,
    is_supplier: built.is_supplier ?? summary.is_supplier,
    last_message: preferSummaryPreview ? summary.last_message : built.last_message,
    has_unread: Boolean(built.has_unread || summary.has_unread),
    unread_count: Math.max(built.unread_count || 0, summary.unread_count || 0),
    // Keep local messages (callbacks etc.) so the open thread can still show them;
    // list preview/unread come from the merge above.
    is_summary: false,
  };
};

// Map a server-side ConversationSummary into the CommunicationCard shape the list
// renders. `messages` stays empty — the thread is lazy-loaded when opened. The
// synthetic `last_message` drives the preview, sort order, and unread dot.
const summaryToCard = (s: ConversationSummary): CommunicationCard => {
  const id = s.customer_id
    ? `customer_${s.customer_id}`
    : s.conversation_type === 'email'
      ? `email_${normalizeEmail(s.contact_info || '')}`
      : `sms_${(s.contact_info || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '')}`;
  const lastMessage: any = {
    id: `summary_${s.conversation_key}`,
    customer_id: s.customer_id || undefined,
    message_type: (s.last_message_type as any) || 'sms',
    direction: (s.last_direction as any) || 'inbound',
    from_number: s.contact_info || '',
    to_number: s.contact_info || '',
    body: s.last_preview || '',
    subject: s.last_subject || '',
    read: s.has_unread ? 'unread' : 'read',
    created_at: s.last_message_at || new Date().toISOString(),
  };
  return {
    id,
    type: s.conversation_type as CommunicationCard['type'],
    customer_id: s.customer_id || undefined,
    customer_name: s.customer_name || undefined,
    contact_info: s.contact_info || '',
    messages: [],
    last_message: lastMessage,
    unread_count: Number(s.unread_count) || 0,
    has_unread: !!s.has_unread,
    is_supplier: !!s.is_supplier,
    is_summary: true,
  };
};

// Map an agent_callbacks row (AI receptionist callback request) into the
// synthetic Message shape the timeline renders. Unread while pending + unviewed.
const callbackToMessage = (cb: any, matchedCustomerId?: string | null): Message => ({
  id: cb.id,
  business_id: cb.business_id,
  customer_id: matchedCustomerId ?? cb.customer_id ?? null,
  message_type: 'callback',
  direction: 'inbound',
  from_number: cb.from_number || '',
  to_number: cb.from_number || '',
  body: `Callback requested${cb.reason ? ` — ${cb.reason}` : ''}`,
  read: 'read',
  created_at: cb.created_at,
  updated_at: cb.updated_at || cb.created_at,
  viewed: !!cb.viewed,
  callback_status: cb.status,
  callback_name: cb.name || null,
  callback_reason: cb.reason || null,
  callback_collected: cb.collected && typeof cb.collected === 'object' ? cb.collected : null,
  callback_summary: cb.summary || null,
});

/** Sentinel: open the newest email thread bubble once the conversation timeline loads. */
const OPEN_LATEST_EMAIL = '__latest_email__';

const decodeEmailSubject = (value: string) => {
  if (!value) return '';
  const decodeMimeWord = (charset: string, encoding: string, text: string) => {
    try {
      if (encoding.toLowerCase() === 'b') {
        const bytes = Uint8Array.from(atob(text), (c) => c.charCodeAt(0));
        return new TextDecoder(charset || 'utf-8').decode(bytes);
      }
      const decoded = text
        .replace(/_/g, ' ')
        .replace(/=([0-9A-F]{2})/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
      return decoded;
    } catch {
      return text;
    }
  };

  const decoded = value.replace(/=\?([^?]+)\?([bqBQ])\?([^?]*)\?=/g, (_, charset, encoding, text) => {
    return decodeMimeWord(charset, encoding, text);
  });

  const cleaned = decoded.replace(/[\u0000-\u001F\u007F]/g, '').trim();
  if (/[Ãâ¤�]/.test(cleaned)) {
    try {
      const bytes = Uint8Array.from(cleaned, (c) => c.charCodeAt(0));
      const utf8Decoded = new TextDecoder('utf-8').decode(bytes).replace(/[\u0000-\u001F\u007F]/g, '').trim();
      if (utf8Decoded) return utf8Decoded;
    } catch {
      // fall through
    }
    try {
      const legacyDecoded = decodeURIComponent(escape(cleaned));
      return legacyDecoded.replace(/[\u0000-\u001F\u007F]/g, '').replace(/[�]/g, '').trim();
    } catch {
      return cleaned.replace(/[�]/g, '').trim();
    }
  }
  return cleaned.replace(/[�]/g, '').trim();
};


type MessagesConversationOutlet = Record<string, unknown>;
const MessagesConversationOutletContext = createContext<MessagesConversationOutlet | null>(null);
function useMessagesConversationOutlet(): MessagesConversationOutlet {
  const ctx = useContext(MessagesConversationOutletContext);
  if (!ctx) throw new Error("useMessagesConversationOutlet outside Provider");
  return ctx;
}

function MessagesConversationView({ 
    card, 
    markMessageAsRead, 
    markMessagesAsRead,
    markMessagesAsUnread,
    locallyReadMessages, 
    fetchMilestones, 
    onMobileBack,
    openMessageId,
    onOpenMessageConsumed
  }: { 
    card: CommunicationCard; 
    markMessageAsRead: (messageId: string) => Promise<void>;
    markMessagesAsRead: (messageIds: string[]) => Promise<void>;
    markMessagesAsUnread?: (messageIds: string[]) => Promise<void>;
    locallyReadMessages: Set<string>;
    fetchMilestones: (customerId: string, setLoading?: (loading: boolean) => void) => Promise<void>;
    onMobileBack?: () => void;
    /** When set, open this message (or its email thread) after timeline loads */
    openMessageId?: string | null;
    onOpenMessageConsumed?: () => void;
  }) {
    const {
      selectedBusiness,
      user,
      customers,
      agentCallbacks,
      setMessages,
      setCommunicationCards,
      conversationTimelineCacheRef,
      mergeWatermarkRef,
      conversationUICacheRef,
      lastFetchedCardIdRef,
      setSelectedCard,
      setMobileModalOpen,
      setActiveTab,
      activeTab,
      unreadSuppliersCount,
      emailAttachmentsByGmailId,
      handleEditCustomer,
      handleCreateCustomer,
      muteEmailSender,
      handleMilestoneClick,
      setShowTokenPurchaseModal,
      markAllMessagesAsRead,
      coreMessages,
      coreThreadVersion,
      hydrateCoreCardMessages,
    } = useMessagesConversationOutlet() as MessagesConversationOutlet & {
      coreMessages: boolean;
      coreThreadVersion: number;
      hydrateCoreCardMessages: (cardId: string, msgs: Message[], append: boolean) => void;
    };
    const navigate = useNavigate();
    const { open: openImageViewer } = useImageViewer();

    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const conversationRootRef = useRef<HTMLDivElement>(null);
    useKeyboardBottomInset(conversationRootRef);
    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const cachedTimeline = conversationTimelineCacheRef.current.get(card.id);
    const isFirstRenderRef = useRef(!cachedTimeline?.items?.length);
    
    // Timeline states — initialise from cache to avoid skeleton flash on remount
    const [timelineItems, setTimelineItems] = useState<TimelineItem[]>(() => cachedTimeline?.items || []);
    const [timelineOffset, setTimelineOffset] = useState(() => cachedTimeline?.offset || 0);
    const [hasMoreTimeline, setHasMoreTimeline] = useState(() => cachedTimeline?.hasMore || false);
    const [loadingMoreTimeline, setLoadingMoreTimeline] = useState(false);
    const [timelineAttachmentsByGmailId, setTimelineAttachmentsByGmailId] = useState<Record<string, EmailAttachment[]>>({});
    /** True once this mount has a timeline we can safely auto-open an email against. */
    const [autoOpenEmailReady, setAutoOpenEmailReady] = useState(
      () => (cachedTimeline?.items?.length ?? 0) > 0
    );
    // Tapping a callback bubble opens the callback modal (details + call
    // button) instead of jumping straight into the dialler.
    const [selectedCallbackId, setSelectedCallbackId] = useState<string | null>(null);

    // AI agent names for the badge on agent-placed calls (agent_id → name/kind)
    const [agentInfoById, setAgentInfoById] = useState<Record<string, { name: string; kind: string }>>({});
    useEffect(() => {
      if (!selectedBusiness?.id || !supabase) return;
      let cancelled = false;
      (async () => {
        const { data } = await supabase
          .from('agents_v2')
          .select('id, name, kind')
          .eq('business_id', selectedBusiness.id);
        if (!cancelled && data) {
          setAgentInfoById(Object.fromEntries(data.map((a: { id: string; name: string; kind: string }) => [a.id, { name: a.name, kind: a.kind }])));
        }
      })();
      return () => { cancelled = true; };
    }, [selectedBusiness?.id]);
    const agentCallBadgeLabel = (agentId?: string | null): string | null => {
      if (!agentId) return null;
      const info = agentInfoById[agentId];
      if (info?.kind === 'feedback') return 'Feedback Agent';
      if (info?.kind === 'receptionist') return 'AI Receptionist';
      return info?.name || 'AI Agent';
    };

    // Persist timeline to cache so remounts don't lose data
    useEffect(() => {
      if (timelineItems.length > 0) {
        conversationTimelineCacheRef.current.set(card.id, { items: timelineItems, offset: timelineOffset, hasMore: hasMoreTimeline });
      }
    }, [timelineItems, timelineOffset, hasMoreTimeline, card.id]);

    // Merge new realtime messages into the timeline without a full refetch.
    // IMPORTANT: card.messages contains ALL messages for this conversation (up to 30 days),
    // but timelineItems only has the most recent page (TIMELINE_PAGE_SIZE). The watermark
    // prevents old messages from card.messages being dumped into the timeline.
    useEffect(() => {
      const watermark = mergeWatermarkRef.current;
      if (!watermark) return;

      setTimelineItems(prev => {
        if (prev.length === 0) return prev;

        const existingIds = new Set(prev.map(item => {
          if (item.type === 'message' && item.message) {
            return item.message.id;
          }
          return item.id;
        }));
        const existingSids = new Set(
          prev
            .filter(item => item.type === 'message' && item.message?.twilio_message_sid)
            .map(item => item.message!.twilio_message_sid)
        );

        let updated = [...prev];
        let changed = false;

        for (const msg of card.messages) {
          if (msg.created_at <= watermark) continue;

          if (existingIds.has(msg.id)) continue;

          if (msg.twilio_message_sid && existingSids.has(msg.twilio_message_sid)) {
            updated = updated.map(item =>
              (item.type === 'message' && item.message?.twilio_message_sid === msg.twilio_message_sid)
                ? { id: `message_${msg.id}`, type: 'message' as const, created_at: msg.created_at, message: msg }
                : item
            );
            changed = true;
            continue;
          }

          // Real DB record arrived for an optimistic SMS — replace instead of duplicating.
          if (msg.direction === 'outbound') {
            const normPhone = (p: string) => (p || '').replace(/\D/g, '').slice(-10);
            const msgPhone = normPhone(msg.to_number);
            const matchIdx = updated.findIndex(item =>
              item.type === 'message' &&
              item.message?.id.startsWith('optimistic_') &&
              normPhone(item.message.to_number) === msgPhone
            );
            if (matchIdx >= 0) {
              updated[matchIdx] = { id: `message_${msg.id}`, type: 'message' as const, created_at: msg.created_at, message: msg };
              changed = true;
              continue;
            }
          }

          updated.push({ id: `message_${msg.id}`, type: 'message' as const, created_at: msg.created_at, message: msg });
          changed = true;
        }

        if (!changed) return prev;

        return updated;
      });
    }, [card.messages]);
    
    // Message input and UI states — initialise from cache to survive remounts,
    // falling back to the sessionStorage draft so a full reload doesn't lose text.
    const cachedUI =
      conversationUICacheRef.current.get(card.id) ||
      readConversationDraft(selectedBusiness?.id, card.id);
    const [messageInput, setMessageInput] = useState(() => cachedUI?.messageInput || '');
    const [isSending, setIsSending] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);
    const [replyMethod, setReplyMethod] = useState<'sms' | 'email'>(() =>
      cachedUI?.replyMethod || (card.last_message.message_type === 'email' ? 'email' : 'sms')
    );
    const [emailComposeModalOpen, setEmailComposeModalOpen] = useState(() => cachedUI?.emailComposeModalOpen || false);
    const [replySubject, setReplySubject] = useState<string>(() => cachedUI?.replySubject || '');
    const [replyToGmailId, setReplyToGmailId] = useState<string | null>(() => cachedUI?.replyToGmailId ?? null);
    const [replyThreadId, setReplyThreadId] = useState<string | null>(() => cachedUI?.replyThreadId ?? null);
    const [replyInReplyTo, setReplyInReplyTo] = useState<string | null>(() => cachedUI?.replyInReplyTo ?? null);
    const [replyReferences, setReplyReferences] = useState<string | null>(() => cachedUI?.replyReferences ?? null);
    const [isReplyMode, setIsReplyMode] = useState(() => cachedUI?.isReplyMode || false);
    const [isUserTyping, setIsUserTyping] = useState(false);
    const [newMessageNotification, setNewMessageNotification] = useState(false);
    const [showScrollToBottom, setShowScrollToBottom] = useState(false);

    // Persist conversation UI state to cache so remounts don't lose typed text / open modals.
    // Also mirror to sessionStorage so a full page reload restores the draft.
    useEffect(() => {
      const uiState = {
        messageInput, replyMethod, emailComposeModalOpen,
        replySubject, replyToGmailId, replyThreadId,
        replyInReplyTo, replyReferences, isReplyMode,
      };
      conversationUICacheRef.current.set(card.id, uiState);
      writeConversationDraft(selectedBusiness?.id, card.id, uiState);
    }, [messageInput, replyMethod, emailComposeModalOpen, replySubject,
        replyToGmailId, replyThreadId, replyInReplyTo, replyReferences,
        isReplyMode, card.id, selectedBusiness?.id]);
    
    // AI Compose states
    const [showAIPrompt, setShowAIPrompt] = useState(false);
    const [aiPrompt, setAIPrompt] = useState('');
    const [isGeneratingAI, setIsGeneratingAI] = useState(false);
    const [aiError, setAIError] = useState<string | null>(null);
    const [aiMode, setAIMode] = useState<'compose' | 'autocomplete' | 'polish' | null>(null);
    const [emailBody, setEmailBody] = useState(''); // Track email body content
    const [currentEmailSubject, setCurrentEmailSubject] = useState(''); // Track email subject
    
    // Snippets modal state (local to this conversation)
    const [localSnippetsModalOpen, setLocalSnippetsModalOpen] = useState(false);
    
    // Store email insertion function in a ref
    const emailInsertRef = useRef<((content: string, replace?: boolean) => void) | null>(null);
    
    // Store subject insertion function in a ref
    const emailSubjectInsertRef = useRef<((subject: string) => void) | null>(null);
    
    const displayName = card.customer_name || card.contact_info;
    
    const TIMELINE_PAGE_SIZE = 15;

    useEffect(() => {
      if (!selectedBusiness?.id || !supabase) return;
      const gmailIds = Array.from(
        new Set(
          timelineItems
            .filter((item) => item.type === 'message' && item.message?.message_type === 'email')
            .map((item) => item.message?.twilio_message_sid)
            .filter((id): id is string => Boolean(id))
        )
      );

      const missingIds = gmailIds.filter((id) => !emailAttachmentsByGmailId[id] && !timelineAttachmentsByGmailId[id]);
      if (missingIds.length === 0) return;

      const loadTimelineAttachments = async () => {
        try {
          const { data, error } = await supabase
            .from('email_attachments')
            .select('id, gmail_id, filename, content_type, size_bytes, public_url, is_inline')
            .eq('business_id', selectedBusiness.id)
            .in('gmail_id', missingIds);

          if (error) throw error;

          const grouped = (data || []).reduce<Record<string, EmailAttachment[]>>((acc, attachment) => {
            if (!acc[attachment.gmail_id]) acc[attachment.gmail_id] = [];
            acc[attachment.gmail_id].push(attachment);
            return acc;
          }, {});

          setTimelineAttachmentsByGmailId((prev) => ({ ...prev, ...grouped }));
        } catch (err) {
          console.error('Error loading timeline attachments:', err);
        }
      };

      loadTimelineAttachments();
    }, [timelineItems, selectedBusiness?.id]);

    // When replying, look up thread_id from received_emails by gmail_id so the reply stays in the same Gmail thread
    useEffect(() => {
      if (!replyToGmailId || !selectedBusiness?.id || !supabase || !emailComposeModalOpen || !isReplyMode) {
        return;
      }
      let cancelled = false;
      (async () => {
        try {
          const { data, error } = await supabase
            .from('received_emails')
            .select('thread_id, rfc_message_id, references_header')
            .eq('business_id', selectedBusiness.id)
            .eq('gmail_id', replyToGmailId)
            .maybeSingle();
          if (cancelled) return;
          if (error) {
            console.warn('Failed to fetch reply thread context:', error);
            return;
          }
          if (data) {
            setReplyThreadId(data.thread_id ?? null);
            // In-Reply-To must be the Message-ID of the email we're replying to so the recipient's inbox threads it
            setReplyInReplyTo(data.rfc_message_id ?? null);
            // References = previous chain + Message-ID of message we're replying to (so recipient's client threads correctly)
            const refs = data.references_header?.trim();
            const msgId = data.rfc_message_id?.trim();
            setReplyReferences(refs && msgId ? `${refs} ${msgId}` : msgId ?? refs ?? null);
          }
        } catch (err) {
          if (!cancelled) console.warn('Error fetching reply thread:', err);
        }
      })();
      return () => { cancelled = true; };
    }, [replyToGmailId, selectedBusiness?.id, emailComposeModalOpen, isReplyMode]);

    // Memoized customer lookup to prevent repeated find operations
    const resolvedCustomerIds = useMemo(
      () => resolveCustomerIdsForContact(card.customer_id, card.contact_info, customers),
      [card.customer_id, card.contact_info, customers],
    );

    const currentCustomer = useMemo(() => {
      if (resolvedCustomerIds.length === 0) return null;
      return customers.find((customer) => customer.id === resolvedCustomerIds[0]) || null;
    }, [resolvedCustomerIds, customers]);

    // Lead banner data: read the business-wide cache synchronously so the
    // banner paints in the SAME frame as the conversation (instant, like
    // messages). Falls back to a fetch only if the cache isn't primed yet,
    // and re-reads whenever the cache is re-primed (realtime updates).
    const [openOpportunities, setOpenOpportunities] = useState<OpenOpportunitySummary[]>(() =>
      selectedBusiness?.id && resolvedCustomerIds.length > 0
        ? getCachedOpenOpportunitiesForCustomerIds(selectedBusiness.id, resolvedCustomerIds) || []
        : [],
    );
    // Opportunity opened in-place from the Lead banner (no route change).
    const [embeddedOpportunityId, setEmbeddedOpportunityId] = useState<number | null>(null);

    useEffect(() => {
      if (!selectedBusiness?.id || resolvedCustomerIds.length === 0) {
        setOpenOpportunities([]);
        return;
      }

      let cancelled = false;

      const readFromCacheOrFetch = async () => {
        const cached = getCachedOpenOpportunitiesForCustomerIds(selectedBusiness.id, resolvedCustomerIds);
        if (cached) {
          setOpenOpportunities(cached);
          return;
        }
        // Cache not primed yet (first ever open) — fetch just this customer.
        const opportunities = await getOpenOpportunitiesForCustomerIds(
          selectedBusiness.id,
          resolvedCustomerIds,
        );
        if (!cancelled) setOpenOpportunities(opportunities);
      };

      void readFromCacheOrFetch();

      // Stay in sync when the business-wide cache is re-primed (realtime).
      const onCacheUpdated = () => {
        const cached = getCachedOpenOpportunitiesForCustomerIds(selectedBusiness.id, resolvedCustomerIds);
        if (cached && !cancelled) setOpenOpportunities(cached);
      };
      window.addEventListener(OPEN_OPPORTUNITIES_CACHE_EVENT, onCacheUpdated);

      return () => {
        cancelled = true;
        window.removeEventListener(OPEN_OPPORTUNITIES_CACHE_EVENT, onCacheUpdated);
      };
    }, [selectedBusiness?.id, resolvedCustomerIds.join(',')]);

    // Refresh the Lead banner immediately after a lead is created from the
    // conversation's "+" menu (no navigation/modal — just shows the banner).
    // Re-priming the cache also fires the cache-updated event, which the
    // effect above uses to refresh this conversation's banner.
    useEffect(() => {
      const handler = () => {
        if (!selectedBusiness?.id || resolvedCustomerIds.length === 0) return;
        void primeOpenOpportunitiesCache(selectedBusiness.id);
        getOpenOpportunitiesForCustomerIds(selectedBusiness.id, resolvedCustomerIds)
          .then(setOpenOpportunities)
          .catch(() => {});
      };
      window.addEventListener(LEAD_CREATED_EVENT, handler);
      return () => window.removeEventListener(LEAD_CREATED_EVENT, handler);
    }, [selectedBusiness?.id, resolvedCustomerIds.join(',')]);

    const handleLeadFollowUpSave = useCallback(
      async (opportunityId: number, followUpAt: string | null) => {
        if (!selectedBusiness?.id) return;
        const opp = openOpportunities.find((o) => o.id === opportunityId);
        try {
          await scheduleOpportunityFollowUp({
            businessId: selectedBusiness.id,
            opportunityId,
            customerId: opp?.customer_id ?? currentCustomer?.id ?? null,
            followUpAt,
            previousFollowUpAt: opp?.next_follow_up_at ?? null,
          });
          setOpenOpportunities((prev) =>
            prev.map((o) =>
              o.id === opportunityId ? { ...o, next_follow_up_at: followUpAt } : o,
            ),
          );
          toast.success(followUpAt ? 'Follow-up scheduled' : 'Follow-up cleared');
        } catch (error) {
          console.error('Error saving lead follow-up:', error);
          toast.error('Could not save follow-up');
        }
      },
      [selectedBusiness?.id, openOpportunities, currentCustomer?.id],
    );

    const handleLeadFollowUpComplete = useCallback(
      async (opportunityId: number) => {
        if (!selectedBusiness?.id) return;
        const opp = openOpportunities.find((o) => o.id === opportunityId);
        if (!opp?.next_follow_up_at) return;
        try {
          await markOpportunityFollowUpComplete({
            businessId: selectedBusiness.id,
            opportunityId,
            customerId: opp.customer_id ?? currentCustomer?.id ?? null,
            previousFollowUpAt: opp.next_follow_up_at,
          });
          setOpenOpportunities((prev) =>
            prev.map((o) =>
              o.id === opportunityId ? { ...o, next_follow_up_at: null } : o,
            ),
          );
          toast.success('Follow-up marked complete');
        } catch (error) {
          console.error('Error completing lead follow-up:', error);
          if (error instanceof Error && error.message === 'activity-log-failed') {
            toast.error(
              'Could not save to activity log. Apply migration 20260518180000_opportunity_activity_follow_up_completed.sql in Supabase.',
            );
          } else {
            toast.error('Could not complete follow-up');
          }
        }
      },
      [selectedBusiness?.id, openOpportunities, currentCustomer?.id],
    );

    const handleLeadStageChange = useCallback(
      async (opportunityId: number, newStageId: number) => {
        if (!selectedBusiness?.id) return;
        const opp = openOpportunities.find((o) => o.id === opportunityId);
        if (!opp || opp.stage_id === newStageId) return;
        const oldStageId = opp.stage_id;
        const oldStageName = opp.stage_name;
        const newStageName =
          opp.stages.find((s) => s.id === newStageId)?.name ?? '';
        // Optimistic update
        setOpenOpportunities((prev) =>
          prev.map((o) =>
            o.id === opportunityId
              ? { ...o, stage_id: newStageId, stage_name: newStageName }
              : o,
          ),
        );
        try {
          await moveOpportunityToStage({
            businessId: selectedBusiness.id,
            opportunityId,
            customerId: opp.customer_id ?? currentCustomer?.id ?? null,
            oldStageId,
            newStageId,
            oldStageName,
            newStageName,
          });
          toast.success(newStageName ? `Moved to ${newStageName}` : 'Stage updated');
        } catch (error) {
          console.error('Error changing lead stage:', error);
          setOpenOpportunities((prev) =>
            prev.map((o) =>
              o.id === opportunityId
                ? { ...o, stage_id: oldStageId, stage_name: oldStageName }
                : o,
            ),
          );
          toast.error('Could not change stage');
        }
      },
      [selectedBusiness?.id, openOpportunities, currentCustomer?.id],
    );

    // Replace variables in snippet with customer + open lead data (shared helper).
    // Lead variables fill from the most recent open lead; when there is none they
    // are blanked and we warn so the user can adjust before sending.
    const replaceSnippetVariables = (content: string) => {
      const { text, missingLeadVariables } = substituteSnippetVariables(content, {
        customer: currentCustomer,
        lead: openOpportunities[0] || null,
      });
      if (missingLeadVariables.length > 0) {
        toast(`No open lead for this customer — ${missingLeadVariables.join(', ')} left blank`, { icon: '⚠️' });
      }
      return text;
    };
    
    // Handle snippet selection
    const handleSnippetSelect = (snippet: any) => {
      // Replace variables with actual customer data
      const processedContent = replaceSnippetVariables(snippet.content);

      // Target the email composer when its modal is open; otherwise the SMS input.
      if (emailComposeModalOpen) {
        // Insert into email composer
        if (emailInsertRef.current) {
          console.log('📧 Inserting into email body');
          emailInsertRef.current(processedContent);
        }
      } else {
        // Insert into SMS
        console.log('💬 Inserting into SMS, current value:', messageInput);
        setMessageInput(prev => {
          const newValue = prev ? `${prev}\n\n${processedContent}` : processedContent;
          console.log('💬 New value:', newValue);
          return newValue;
        });
      }
    };

    // NEW: Unified timeline fetching function with proper pagination
    const fetchTimelineItems = async (offset: number = 0, limit: number = TIMELINE_PAGE_SIZE): Promise<{ items: TimelineItem[], hasMore: boolean }> => {
      try {
        if (!selectedBusiness?.id || !supabase) {
          return { items: [], hasMore: false };
        }

        // Core tables: one call returns the page already merged and ordered.
        if (coreMessages) {
          const page = await fetchCoreThread(selectedBusiness.id, conversationKeyForCard(card), { limit, offset });
          console.log(`📅 Timeline (core): ${page.items.length} items (offset: ${offset}, hasMore: ${page.hasMore})`);
          // The card's message list backs email thread views, reply addresses and
          // unread checks, so keep it filled from the same call.
          hydrateCoreCardMessages(card.id, page.messages, offset > 0);
          return { items: page.items, hasMore: page.hasMore };
        }

        // Get messages for this conversation with pagination
        // For customer cards, also fetch unlinked messages by phone/email
        // (records that arrived before the customer was created still have customer_id = NULL)
        let messageQueries: Promise<any>[] = [];

        if (card.type === 'customer' && card.customer_id) {
          const byCustomerId = supabase
            .from('all_messages')
            .select('*')
            .eq('business_id', selectedBusiness.id)
            .eq('customer_id', card.customer_id)
            .order('created_at', { ascending: false })
            .range(offset, offset + limit - 1);
          messageQueries.push(byCustomerId);

          const customer = customers.find(c => c.id === card.customer_id);
          const custPhone = customer?.phone;
          const custEmail = customer?.email;

          if (custPhone) {
            const digits = custPhone.replace(/\D/g, '');
            const phoneCandidates = new Set([custPhone]);
            if (digits.length === 10) {
              phoneCandidates.add(`+1${digits}`);
              phoneCandidates.add(`1${digits}`);
              phoneCandidates.add(digits);
            } else if (digits.length === 11 && digits.startsWith('1')) {
              phoneCandidates.add(`+${digits}`);
              phoneCandidates.add(digits);
              phoneCandidates.add(digits.slice(1));
            }
            const phoneOrParts = Array.from(phoneCandidates)
              .flatMap(p => [`from_number.eq.${p}`, `to_number.eq.${p}`]);

            const byPhone = supabase
              .from('all_messages')
              .select('*')
              .eq('business_id', selectedBusiness.id)
              .is('customer_id', null)
              .or(phoneOrParts.join(','))
              .order('created_at', { ascending: false })
              .range(offset, offset + limit - 1);
            messageQueries.push(byPhone);
          }

          if (custEmail) {
            const email = custEmail.toLowerCase().trim();
            const byEmail = supabase
              .from('all_messages')
              .select('*')
              .eq('business_id', selectedBusiness.id)
              .is('customer_id', null)
              .or(`from_number.ilike.%${email}%,to_number.ilike.%${email}%`)
              .order('created_at', { ascending: false })
              .range(offset, offset + limit - 1);
            messageQueries.push(byEmail);
          }
        } else {
          let messageQuery = supabase
            .from('all_messages')
            .select('*')
            .eq('business_id', selectedBusiness.id)
            .is('customer_id', null)
            .order('created_at', { ascending: false })
            .range(offset, offset + limit - 1);

          if (card.type === 'phone') {
            messageQuery = messageQuery.eq('message_type', 'sms')
                                     .or(`from_number.eq.${card.contact_info},to_number.eq.${card.contact_info}`);
          } else if (card.type === 'email') {
            messageQuery = messageQuery.eq('message_type', 'email')
                                      .or(`from_number.ilike.%${card.contact_info}%,to_number.ilike.%${card.contact_info}%,from_number.eq.${card.contact_info},to_number.eq.${card.contact_info}`);
          }
          messageQueries.push(messageQuery);
        }

        // Kick off messages + milestones now (lazy query builders), then await
        // them together with the call queries (built below). This collapses the
        // previous 3 sequential round-trips (messages → milestones → calls) into 1.
        const msgPromise = Promise.all(messageQueries);
        const milestonesPromise: Promise<BookingMilestone[]> =
          (card.type === 'customer' && card.customer_id)
            ? getCustomerMilestones(card.customer_id).catch((error) => {
                console.error('Error fetching milestones:', error);
                return [] as BookingMilestone[];
              })
            : Promise.resolve([] as BookingMilestone[]);

        // Get call history for this conversation
        // Only include calls for customer or phone conversations, NOT for email conversations
        let callQueries: Promise<any>[] = [];
        
        if (card.type === 'email') {
          // Email conversations should NOT include calls
          callQueries.push(Promise.resolve({ data: [], error: null }));
        } else if (card.type === 'customer' && card.customer_id) {
          // For customer cards, run TWO call queries and merge:
          // 1) Calls linked by customer_id
          // 2) Calls matching the customer's phone (any customer_id — a CRM
          //    trigger may have stamped a different/wrong id; phone still wins)
          const customer = customers.find(c => c.id === card.customer_id);
          const custPhone = customer?.phone;

          const byCustomerId = supabase
            .from('call_history')
            .select('*')
            .eq('business_id', selectedBusiness.id)
            .eq('customer_id', card.customer_id)
            .order('call_time', { ascending: false });
          callQueries.push(byCustomerId);

          if (custPhone) {
            const digits = custPhone.replace(/\D/g, '');
            const last10 = digits.length >= 10 ? digits.slice(-10) : digits;
            const phoneCandidates = new Set([custPhone]);
            if (last10.length === 10) {
              phoneCandidates.add(`+1${last10}`);
              phoneCandidates.add(`1${last10}`);
              phoneCandidates.add(last10);
              phoneCandidates.add(`+${last10}`);
            }
            if (digits.length === 11 && digits.startsWith('1')) {
              phoneCandidates.add(`+${digits}`);
              phoneCandidates.add(digits);
            }
            const phoneOrParts = Array.from(phoneCandidates)
              .flatMap(p => [`from_number.eq.${p}`, `to_number.eq.${p}`]);

            const byPhone = supabase
              .from('call_history')
              .select('*')
              .eq('business_id', selectedBusiness.id)
              .or(phoneOrParts.join(','))
              .order('call_time', { ascending: false });
            callQueries.push(byPhone);
          }
        } else if (card.type === 'phone') {
          const digits = (card.contact_info || '').replace(/\D/g, '');
          const last10 = digits.length >= 10 ? digits.slice(-10) : digits;
          const phoneCandidates = new Set([card.contact_info].filter(Boolean) as string[]);
          if (last10.length === 10) {
            phoneCandidates.add(`+1${last10}`);
            phoneCandidates.add(`1${last10}`);
            phoneCandidates.add(last10);
            phoneCandidates.add(`+${last10}`);
          }
          if (digits.length === 11 && digits.startsWith('1')) {
            phoneCandidates.add(`+${digits}`);
            phoneCandidates.add(digits);
          }
          const phoneOrParts = Array.from(phoneCandidates)
            .flatMap(p => [`from_number.eq.${p}`, `to_number.eq.${p}`]);
          const byPhone = supabase
            .from('call_history')
            .select('*')
            .eq('business_id', selectedBusiness.id)
            .or(phoneOrParts.join(','))
            .order('call_time', { ascending: false });
          callQueries.push(byPhone);
        }

        // AI receptionist callback requests for this conversation (same
        // customer/phone matching as calls; email conversations have none).
        const callbackQueries: Promise<any>[] = [];
        if (card.type !== 'email') {
          const phoneSource = card.type === 'customer' && card.customer_id
            ? customers.find(c => c.id === card.customer_id)?.phone
            : card.contact_info;

          if (card.type === 'customer' && card.customer_id) {
            callbackQueries.push(
              supabase
                .from('agent_callbacks')
                .select('*')
                .eq('business_id', selectedBusiness.id)
                .eq('customer_id', card.customer_id)
                .order('created_at', { ascending: false })
            );
          }
          if (phoneSource) {
            const digits = phoneSource.replace(/\D/g, '');
            const last10 = digits.length >= 10 ? digits.slice(-10) : digits;
            const phoneCandidates = new Set([phoneSource]);
            if (last10.length === 10) {
              phoneCandidates.add(`+1${last10}`);
              phoneCandidates.add(`1${last10}`);
              phoneCandidates.add(last10);
              phoneCandidates.add(`+${last10}`);
            }
            if (digits.length === 11 && digits.startsWith('1')) {
              phoneCandidates.add(`+${digits}`);
              phoneCandidates.add(digits);
            }
            callbackQueries.push(
              supabase
                .from('agent_callbacks')
                .select('*')
                .eq('business_id', selectedBusiness.id)
                .or(Array.from(phoneCandidates).map(p => `from_number.eq.${p}`).join(','))
                .order('created_at', { ascending: false })
            );
          }
        }

        // ── Await messages, calls, callbacks, and milestones CONCURRENTLY (1 round-trip). ──
        const [msgResults, callResults, callbackResults, allMilestones] = await Promise.all([
          msgPromise,
          Promise.all(callQueries),
          Promise.all(callbackQueries),
          milestonesPromise,
        ]);

        // Merge/dedupe messages
        const seenMsgIds = new Set<string>();
        const mergedMessages: any[] = [];
        for (const result of msgResults) {
          if (!result.error && result.data) {
            for (const msg of result.data) {
              if (!seenMsgIds.has(msg.id)) {
                seenMsgIds.add(msg.id);
                mergedMessages.push(msg);
              }
            }
          }
        }
        mergedMessages.sort((a: any, b: any) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        const allMessages = mergedMessages.slice(0, limit);
        const messagesHasMore = mergedMessages.length >= limit;

        // Merge/dedupe calls
        const seenCallIds = new Set<string>();
        const allCalls: any[] = [];
        for (const result of callResults) {
          if (!result.error && result.data) {
            for (const call of result.data) {
              if (!seenCallIds.has(call.id)) {
                seenCallIds.add(call.id);
                allCalls.push(call);
              }
            }
          }
        }
        const callError = callResults.find(r => r.error)?.error;
        if (callError) console.error('Error fetching calls:', callError);

        // Batch-fetch agent session outcomes for AI agent calls (badge + outcome line)
        let agentSessionsById: Record<string, { outcome: string | null; collected: AgentCollectedData | null }> = {};
        const agentSessionIds = allCalls.map(c => c.agent_session_id).filter(Boolean);
        if (agentSessionIds.length > 0) {
          const { data: agentSessions } = await supabase
            .from('agent_call_sessions')
            .select('id, outcome, collected')
            .in('id', agentSessionIds);
          agentSessionsById = Object.fromEntries(
            (agentSessions || []).map((s: any) => [s.id, { outcome: s.outcome, collected: s.collected }])
          );
        }

        // Merge/dedupe callback requests
        const seenCallbackIds = new Set<string>();
        const allCallbacks: any[] = [];
        for (const result of callbackResults) {
          if (!result.error && result.data) {
            for (const cb of result.data) {
              if (!seenCallbackIds.has(cb.id)) {
                seenCallbackIds.add(cb.id);
                allCallbacks.push(cb);
              }
            }
          }
        }
        const callbackError = callbackResults.find(r => r.error)?.error;
        if (callbackError) console.error('Error fetching callbacks:', callbackError);

        const hasMoreMessages = messagesHasMore;

        // Combine messages, calls, and milestones into unified timeline
        const combinedTimeline: TimelineItem[] = [];

        // Map all messages to Message interface
        const mappedMessages: Message[] = allMessages.map((message: any) => ({
          ...message,
          body: message.body || message.content || '',
          media_urls: message.media_urls
            ? (Array.isArray(message.media_urls) ? message.media_urls : JSON.parse(message.media_urls || '[]'))
            : null
        }));

        // Partition into email and non-email
        const emailMessages = mappedMessages.filter((m) => m.message_type === 'email');
        const nonEmailMessages = mappedMessages.filter((m) => m.message_type !== 'email');

        // Non-email: one timeline item per message (SMS, etc.)
        nonEmailMessages.forEach((message) => {
          combinedTimeline.push({
            id: `message_${message.id}`,
            type: 'message',
            created_at: message.created_at,
            message
          });
        });

        // Email: one timeline item per thread — bubble shows latest message (who sent last + subject)
        // Use a fallback subject so emails with no subject (or just "Re:") group with the rest of their thread
        const fallbackThreadSubject = emailMessages.find((m) => normalizeEmailSubject(m.subject || ''))?.subject || '';
        const emailThreadByKey = new Map<string, Message>();
        for (const msg of emailMessages) {
          const key = normalizeEmailSubject(msg.subject || '') || normalizeEmailSubject(fallbackThreadSubject) || `__no_subject_${msg.id}`;
          const existing = emailThreadByKey.get(key);
          if (!existing || new Date(msg.created_at).getTime() > new Date(existing.created_at).getTime()) {
            emailThreadByKey.set(key, msg);
          }
        }
        emailThreadByKey.forEach((latestMessage) => {
          combinedTimeline.push({
            id: `email_thread_${latestMessage.id}`,
            type: 'message',
            created_at: latestMessage.created_at,
            message: latestMessage
          });
        });

        // Add calls to timeline (convert to message format)
        allCalls.forEach(call => {
          // Match customer by phone number if customer_id is not set
          let matchedCustomerId = call.customer_id;
          
          if (!matchedCustomerId) {
            const callPhoneNumber = call.direction === 'inbound' ? call.from_number : call.to_number;
            const matchedCustomer = customers.find(customer => {
              if (!customer.phone) return false;
              const customerPhone = customer.phone.replace(/\D/g, '');
              const callPhone = callPhoneNumber.replace(/\D/g, '');
              return customerPhone === callPhone || 
                     customerPhone === callPhone.substring(1) || 
                     `1${customerPhone}` === callPhone;
            });
            
            if (matchedCustomer) {
              matchedCustomerId = matchedCustomer.id;
            }
          }

          const callMessage: Message = {
            id: call.id,
            business_id: call.business_id,
            customer_id: matchedCustomerId,
            message_type: 'call',
            direction: call.direction,
            from_number: call.from_number,
            to_number: call.to_number,
            body: call.direction === 'outbound' ? 'Outbound call' : 
              (call.status === 'answered' ? 'Call answered' : 'Missed call'),
            read: 'read',
            created_at: call.call_time,
            updated_at: call.updated_at || call.call_time,
            call_status: call.status,
            duration_seconds: call.duration_seconds,
            call_sid: call.call_sid,
            viewed: call.viewed,
            recording_url: call.recording_url || null,
            transcription_text: call.transcription_text || null,
            transcription_status: call.transcription_status || null,
            agent_id: call.agent_id || null,
            agent_session_id: call.agent_session_id || null,
            agent_outcome: call.agent_session_id ? agentSessionsById[call.agent_session_id]?.outcome ?? null : null,
            agent_collected: call.agent_session_id ? agentSessionsById[call.agent_session_id]?.collected ?? null : null
          };

          combinedTimeline.push({
            id: `call_${call.id}`,
            type: 'message',
            created_at: call.call_time,
            message: callMessage
          });
        });

        // Add AI receptionist callback requests to timeline
        allCallbacks.forEach(cb => {
          combinedTimeline.push({
            id: `callback_${cb.id}`,
            type: 'message',
            created_at: cb.created_at,
            message: callbackToMessage(cb, card.customer_id || cb.customer_id)
          });
        });

        // Add milestones to timeline
        allMilestones.forEach(milestone => {
          combinedTimeline.push({
            id: `milestone_${milestone.id}`,
            type: 'milestone',
            created_at: milestone.created_at,
            milestone
          });
        });

        // Sort timeline by created_at (newest first)
        combinedTimeline.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        
        // Return the timeline items (already paginated from database)
        // hasMore is determined by whether we got a full page of messages
        console.log(`📅 Timeline: Loaded ${combinedTimeline.length} items (offset: ${offset}, hasMore: ${hasMoreMessages})`);
        
        return { items: combinedTimeline, hasMore: hasMoreMessages };
      } catch (error) {
        console.error('Error fetching timeline items:', error);
        return { items: [], hasMore: false };
      }
    };

    // Load initial timeline items when conversation opens
    useEffect(() => {
      const isSameConversation = lastFetchedCardIdRef.current === card.id;
      const hasCachedData = (conversationTimelineCacheRef.current.get(card.id)?.items?.length ?? 0) > 0;

      if (isSameConversation && hasCachedData) {
        // This is a remount of the same conversation (caused by parent re-render
        // redefining ConversationView). State was already restored from cache in
        // useState initialisers, so skip the destructive DB round-trip.
        setAutoOpenEmailReady(true);
        return;
      }

      const loadingForCardId = card.id;
      lastFetchedCardIdRef.current = loadingForCardId;
      setAutoOpenEmailReady(false);

      const loadInitialTimeline = async () => {
        console.log('🔄 Loading initial timeline for:', loadingForCardId);
        const result = await fetchTimelineItems(0, TIMELINE_PAGE_SIZE);

        // Ignore stale responses if the user already switched away (ref updated by a newer mount/effect).
        if (lastFetchedCardIdRef.current !== loadingForCardId) return;
        
        // Set the merge watermark to the newest item so the merge effect only
        // adds messages that arrive AFTER this point (via realtime).
        // result.items is sorted newest-first before we reverse it.
        if (result.items.length > 0) {
          mergeWatermarkRef.current = result.items[0].created_at;
        } else {
          mergeWatermarkRef.current = new Date().toISOString();
        }

        // Reverse items for display (oldest first in UI)
        const reversedItems = [...result.items].reverse();
        setTimelineItems(reversedItems);
        setTimelineOffset(TIMELINE_PAGE_SIZE);
        setHasMoreTimeline(result.hasMore);
        setAutoOpenEmailReady(true);
        
        // Ensure scroll to bottom after timeline loads
        setTimeout(() => {
          if (lastFetchedCardIdRef.current !== loadingForCardId) return;
          if (messagesContainerRef.current) {
            console.log('📜 Auto-scrolling to bottom after timeline load');
            messagesContainerRef.current.scrollIntoView({ behavior: 'instant', block: 'end' });
          }
        }, 100);
      };

      loadInitialTimeline();
    }, [card.id, selectedBusiness?.id]);

    // Load more timeline items function
    const loadMoreTimeline = async () => {
      if (loadingMoreTimeline || !hasMoreTimeline) return;
      
      setLoadingMoreTimeline(true);
      
      try {
        console.log('📅 Loading more timeline items...');
        const result = await fetchTimelineItems(timelineOffset, TIMELINE_PAGE_SIZE);
        
        if (result.items.length > 0) {
          // Reverse new items and prepend to current timeline (oldest first for display)
          const reversedNewItems = [...result.items].reverse();
          setTimelineItems(prev => [...reversedNewItems, ...prev]);
          setTimelineOffset(prev => prev + TIMELINE_PAGE_SIZE);
          setHasMoreTimeline(result.hasMore);
          
          console.log(`✅ Added ${result.items.length} more timeline items`);
        } else {
          setHasMoreTimeline(false);
        }
      } catch (error) {
        console.error('Error loading more timeline items:', error);
      } finally {
        setLoadingMoreTimeline(false);
      }
    };

    // Seamless message refresh function (updated for timeline approach)
    const refreshConversationMessages = async () => {
      if (!selectedBusiness?.id || !supabase) return;
      
      // Skip refresh if user is actively typing to avoid disruption
      if (isUserTyping) {
        console.log('[REFRESH] ⏸️ Skipping refresh - user is typing');
        return;
      }
      
      try {
        // Refresh current timeline items
        const result = await fetchTimelineItems(0, timelineOffset);
        const reversedItems = [...result.items].reverse();
        
        // Check for new items
        const currentItemIds = new Set(timelineItems.map(item => item.id));
        const newItems = result.items.filter(item => !currentItemIds.has(item.id));
        
        if (newItems.length > 0) {
          console.log('[REFRESH] 🔄 Found new timeline items:', newItems.length);
          setTimelineItems(reversedItems);
          setHasMoreTimeline(result.hasMore);
          
          // Show notification for new inbound messages only
          const newInboundMessages = newItems.filter(item => 
            item.type === 'message' && item.message?.direction === 'inbound'
          );
          
          if (newInboundMessages.length > 0) {
            setNewMessageNotification(true);
            setTimeout(() => setNewMessageNotification(false), 3000);
          }
        }
      } catch (error) {
        console.error('[REFRESH] Error in refresh function:', error);
      }
    };

    // Core tables: a realtime event re-pulled the inbox; re-pull this thread's page too.
    useEffect(() => {
      if (!coreMessages || !coreThreadVersion) return;
      if (lastFetchedCardIdRef.current !== card.id) return;
      refreshConversationMessages();
    }, [coreThreadVersion]);

    // Consolidated conversation initialization when card changes
    const prevCardIdRef = useRef(card.id);
    useEffect(() => {
      const hasCached = conversationTimelineCacheRef.current.has(card.id);
      const isConversationSwitch = prevCardIdRef.current !== card.id;
      prevCardIdRef.current = card.id;

      isFirstRenderRef.current = !hasCached;
      setNewMessageNotification(false);
      setShowScrollToBottom(false);
      setLoadingMoreTimeline(false);

      if (isConversationSwitch) {
        // Actually switching conversations — clear inputs and reset composer state
        setMessageInput('');
        setSendError(null);
        setEmailComposeModalOpen(false);
        setIsReplyMode(false);
        setReplySubject('');
        setReplyToGmailId(null);
        setReplyThreadId(null);
        setReplyInReplyTo(null);
        setReplyReferences(null);
        setReplyMethod(card.last_message.message_type);
      }
      // On remount (same card) — leave inputs and composer untouched (restored from cache)
    }, [card.id, card.last_message.message_type]);

    // Handle scroll to detect if user is at top (since we're using column-reverse)
    const handleScroll = () => {
      if (messagesContainerRef.current) {
        const container = messagesContainerRef.current;
        // With column-reverse, being at "top" means scrollTop is near 0
        const isNearTop = container.scrollTop <= 100;
        
        if (isNearTop) {
          setShowScrollToBottom(false);
        } else {
          setShowScrollToBottom(true);
        }
      }
    };

    // Scroll to bottom function (which is actually scrollTop = 0 with column-reverse)
    const scrollToBottom = () => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = 0;
        setShowScrollToBottom(false);
      }
    };

    // Add scroll listener to messages container
    useEffect(() => {
      const container = messagesContainerRef.current;
      if (container) {
        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
      }
    }, []);

    // Handle typing detection for SMS input
    const handleMessageInputChange = (value: string) => {
      setMessageInput(value);
      
      // Set typing state
      setIsUserTyping(true);
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Set new timeout to stop typing state after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        setIsUserTyping(false);
      }, 2000);
    };

    // Clear error when user starts typing
    useEffect(() => {
      if (messageInput && sendError) {
        setSendError(null);
      }
    }, [messageInput, sendError]);

    // Handle SMS sending
    const handleSendSMS = async (aiGenerated: boolean = false, mediaFiles?: File[]) => {
      if (!messageInput.trim() && (!mediaFiles || mediaFiles.length === 0)) {
        setSendError('Please enter a message or attach a photo');
        return;
      }

      if (!selectedBusiness?.id) {
        setSendError('No business selected');
        return;
      }

      // Determine the phone number to send to (with fallbacks for remount race conditions)
      let phoneNumber = '';
      if (card.type === 'customer') {
        phoneNumber = currentCustomer?.phone || card.contact_info || '';
      } else if (card.type === 'phone') {
        phoneNumber = card.contact_info;
      }
      if (!phoneNumber) {
        const smsMsg = card.messages.find(m => m.message_type === 'sms');
        if (smsMsg) {
          phoneNumber = smsMsg.direction === 'inbound' ? smsMsg.from_number : smsMsg.to_number;
        }
      }

      if (!phoneNumber) {
        setSendError('No phone number available for this contact');
        return;
      }

      setIsSending(true);
      setSendError(null);

      // Who is sending (cached after the first call), so the bubble shows the right avatar immediately.
      const currentSender = await resolveCurrentSender(selectedBusiness.id);

      // Optimistically add message to timeline so it appears instantly
      const optimisticId = `optimistic_${Date.now()}`;
      // Create local blob URLs so attached images render immediately in the bubble
      const optimisticMediaUrls = mediaFiles && mediaFiles.length > 0
        ? mediaFiles.map(f => URL.createObjectURL(f))
        : null;
      const optimisticMessage: Message = {
        id: optimisticId,
        business_id: selectedBusiness.id,
        customer_id: card.customer_id || null,
        message_type: 'sms' as const,
        direction: 'outbound' as const,
        from_number: '',
        to_number: phoneNumber,
        body: messageInput.trim(),
        read: 'read',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        media_urls: optimisticMediaUrls,
        sender: currentSender,
      };
      setTimelineItems(prev => [
        ...prev,
        { id: `message_${optimisticId}`, type: 'message' as const, created_at: optimisticMessage.created_at, message: optimisticMessage }
      ]);
      // Scroll to bottom so the new message is visible (scrollTop=0 with flex-col-reverse)
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = 0;
        }
      }, 50);
      const savedInput = messageInput.trim();
      setMessageInput('');

      try {
        const result = await sendSMS({
          businessId: selectedBusiness.id,
          to: phoneNumber,
          message: savedInput,
          aiGenerated,
          mediaFiles,
          customerId: card.customer_id || undefined
        });

        if (result.success) {
          // Replace the optimistic bubble with the real DB row so the timeline
          // has the correct id / twilio_message_sid without needing a refetch.
          if (result.message) {
            const realMsg = result.message as Message;
            setTimelineItems(prev =>
              prev.map(item =>
                item.id === `message_${optimisticId}`
                  ? { id: `message_${realMsg.id}`, type: 'message' as const, created_at: realMsg.created_at, message: realMsg }
                  : item
              )
            );
          }
          // A text reply clears the phone side (texts, missed calls, callbacks)
          // and leaves any unread email threads alone.
          markAllMessagesAsRead(card, PHONE_READ_KINDS);
        } else {
          // Remove optimistic message on failure, restore input
          setTimelineItems(prev => prev.filter(item => item.id !== `message_${optimisticId}`));
          const cached = conversationTimelineCacheRef.current.get(card.id);
          if (cached) {
            conversationTimelineCacheRef.current.set(card.id, { ...cached, items: cached.items.filter(i => i.id !== `message_${optimisticId}`) });
          }
          setMessageInput(savedInput);
          setSendError(result.error || 'Failed to send SMS');
        }
      } catch (error) {
        console.error('Error sending SMS:', error);
        setTimelineItems(prev => prev.filter(item => item.id !== `message_${optimisticId}`));
        const cached = conversationTimelineCacheRef.current.get(card.id);
        if (cached) {
          conversationTimelineCacheRef.current.set(card.id, { ...cached, items: cached.items.filter(i => i.id !== `message_${optimisticId}`) });
        }
        setMessageInput(savedInput);
        setSendError(error instanceof Error ? error.message : 'Failed to send SMS');
      } finally {
        setIsSending(false);
      }
    };

    // Handle email sending success
    const handleEmailSendSuccess = async (messageId: string, details?: { subject: string; body: string; to: string }) => {
      setSendError(null);
      const currentSender = selectedBusiness?.id ? await resolveCurrentSender(selectedBusiness.id) : null;
      
      // If replying to an email thread, mark only thread emails as read
      if (isReplyMode && replySubject) {
        const threadSubject = normalizeEmailSubject(replySubject);
        const threadEmailIds = card.messages
          .filter(msg => {
            if (msg.message_type !== 'email' || msg.direction !== 'inbound' || msg.read === 'read') {
              return false;
            }
            const msgSubject = normalizeEmailSubject(msg.subject || '');
            return msgSubject === threadSubject;
          })
          .map(msg => msg.id);
        
        if (threadEmailIds.length > 0) {
          markMessagesAsRead(threadEmailIds);
        }
      }
      // A brand-new email starts its own thread, so nothing else is marked read.

      // Optimistic UI — inject the sent email into the timeline immediately.
      // Emails are grouped by normalized subject (one timeline item per thread),
      // so we must replace the existing thread item rather than appending a duplicate.
      //
      // IMPORTANT: setCommunicationCards triggers a parent re-render which
      // redefines ConversationView (inner function) → React remounts it.
      // On remount, state initializes from caches. So we must compute new
      // timeline items synchronously and write ALL caches BEFORE any setState
      // that could trigger a remount.
      if (details && selectedBusiness?.id) {
        // Derive the business email (from_number) from existing outbound messages
        // in the card, or from the to_number of inbound messages.
        const businessEmail = (() => {
          const outbound = card.messages.find(m => m.message_type === 'email' && m.direction === 'outbound' && m.from_number);
          if (outbound?.from_number) return normalizeEmail(outbound.from_number);
          const inbound = card.messages.find(m => m.message_type === 'email' && m.direction === 'inbound' && m.to_number);
          if (inbound?.to_number) return normalizeEmail(inbound.to_number);
          return '';
        })();

        const optimisticMsg: Message = {
          id: messageId,
          business_id: selectedBusiness.id,
          customer_id: card.customer_id || null,
          message_type: 'email',
          direction: 'outbound',
          from_number: businessEmail,
          to_number: details.to,
          body: details.body,
          subject: details.subject,
          read: 'read',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          media_urls: null,
          twilio_message_sid: messageId,
          sender: currentSender,
        };

        const sentSubjectKey = normalizeEmailSubject(details.subject || '');
        const optimisticTimelineItem: TimelineItem = {
          id: `email_thread_${messageId}`,
          type: 'message',
          created_at: optimisticMsg.created_at,
          message: optimisticMsg,
        };

        // Compute new timeline items synchronously so we can write to cache
        const currentItems = timelineItems;
        const existingIdx = currentItems.findIndex(item =>
          item.type === 'message' &&
          item.message?.message_type === 'email' &&
          normalizeEmailSubject(item.message.subject || '') === sentSubjectKey
        );
        let newTimelineItems: TimelineItem[];
        if (existingIdx !== -1) {
          newTimelineItems = [...currentItems];
          newTimelineItems[existingIdx] = optimisticTimelineItem;
        } else {
          newTimelineItems = [...currentItems, optimisticTimelineItem];
        }

        // Write ALL caches synchronously before any state updates
        conversationTimelineCacheRef.current.set(card.id, {
          items: newTimelineItems,
          offset: timelineOffset,
          hasMore: hasMoreTimeline,
        });
        conversationUICacheRef.current.set(card.id, {
          messageInput: '',
          replyMethod,
          emailComposeModalOpen: false,
          replySubject: '',
          replyToGmailId: null,
          replyThreadId: null,
          replyInReplyTo: null,
          replyReferences: null,
          isReplyMode: false,
        });

        // Now apply state updates (may trigger remount, but caches are ready).
        // Add to both messages (so card rebuilds from realtime keep the optimistic
        // message) and communicationCards (for immediate thread count + last_message).
        // The realtime handler deduplicates by twilio_message_sid when the real
        // DB record arrives, replacing this optimistic entry cleanly.
        //
        // RACE CONDITION GUARD: The realtime INSERT from the edge function's DB
        // write may arrive BEFORE the API response reaches the client. If so, the
        // real record is already in messages — skip adding a duplicate optimistic.
        setTimelineItems(newTimelineItems);
        setMessages(prev => {
          const alreadyExists = prev.some(msg =>
            msg.id === messageId ||
            msg.twilio_message_sid === messageId
          );
          if (alreadyExists) {
            console.log('[OPTIMISTIC] ⚠️ Real record arrived before optimistic — skipping duplicate');
            return prev;
          }
          return [...prev, optimisticMsg];
        });
        setCommunicationCards(prev => prev.map(c => {
          if (c.id !== card.id) return c;
          return {
            ...c,
            messages: [...c.messages, optimisticMsg],
            last_message: optimisticMsg,
          };
        }));
      }
    };


    // Handle email sending error
    const handleEmailSendError = (error: string) => {
      console.error('📧 Email send failed:', error);
      setSendError(error);
    };

    // Handle AI email generation
    const handleGenerateAIEmail = async (mode: 'compose' | 'autocomplete' | 'polish') => {
      console.log('🤖 AI Generate called with mode:', mode);
      console.log('🤖 Current emailBody:', emailBody);
      console.log('🤖 Current emailSubject:', currentEmailSubject);
      
      let promptToUse = '';
      
      // Build the prompt based on the mode
      if (mode === 'compose') {
        if (!aiPrompt.trim()) {
          setAIError('Please enter a prompt');
          return;
        }
        promptToUse = aiPrompt;
      } else if (mode === 'autocomplete') {
        promptToUse = `Continue and complete this email message: "${emailBody}"`;
        console.log('🤖 Auto-complete prompt:', promptToUse);
      } else if (mode === 'polish') {
        // Polish can have optional instructions
        if (aiPrompt.trim()) {
          promptToUse = `Polish and enhance this email message with the following instructions: "${aiPrompt}". Original message: "${emailBody}"`;
        } else {
          promptToUse = `Polish and enhance this email message, making it more professional and clear: "${emailBody}"`;
        }
        console.log('🤖 Polish prompt:', promptToUse);
      }

      setIsGeneratingAI(true);
      setAIError(null);

      try {
        const { data, error } = await supabase.functions.invoke('ai-compose-email', {
          body: {
            prompt: promptToUse,
            customerName: card.customer_name || undefined,
            businessName: selectedBusiness?.business_name || undefined,
            context: card.type === 'customer' ? 'existing customer' : 'new contact',
            mode: mode, // Pass the mode to the edge function
            hasSubject: currentEmailSubject.trim().length > 0, // Tell AI if subject already exists
            businessId: selectedBusiness?.id // Pass business ID for token tracking
          }
        });

        // Check for insufficient tokens FIRST (even if there's an error)
        if (data?.insufficientTokens) {
          setShowTokenPurchaseModal(true);
          setAIError('Insufficient tokens. Please purchase more to continue.');
          return;
        }

        if (error) throw error;

        if (!data?.success) {
          throw new Error(data?.error || 'Failed to generate email');
        }

        console.log('🤖 AI Response:', data);

        // Insert the generated email into the composer
        if (emailInsertRef.current) {
          if (mode === 'autocomplete') {
            // For autocomplete, just append the completion (AI already has the context)
            emailInsertRef.current(data.email, true); // Replace with the full completed version
          } else if (mode === 'polish') {
            // For polish, replace with the polished version
            emailInsertRef.current(data.email, true);
          } else {
            // For compose, replace with new content
            emailInsertRef.current(data.email, true);
          }
        }

        // If AI generated a subject (only happens in compose mode with no subject), insert it
        if (data.subject && emailSubjectInsertRef.current) {
          console.log('🤖 Inserting AI generated subject:', data.subject);
          emailSubjectInsertRef.current(data.subject);
        }

        // Reset AI prompt state
        setShowAIPrompt(false);
        setAIPrompt('');
        setAIMode(null);
      } catch (error) {
        console.error('Error generating AI email:', error);
        setAIError(error instanceof Error ? error.message : 'Failed to generate email');
      } finally {
        setIsGeneratingAI(false);
      }
    };

    // Handle AI auto-reply generation based on email thread
    const handleAIAutoReply = async () => {
      console.log('🤖 AI Auto Reply triggered');
      
      setIsGeneratingAI(true);
      setAIError(null);

      try {
        // Use the same thread as the UI: all emails in the thread we're replying to (by normalized subject)
        const emailMessages = (() => {
          const allEmailInCard = card.messages.filter((msg) => msg.message_type === 'email');
          if (allEmailInCard.length === 0) return [];
          if (replySubject?.trim()) {
            const threadSubj = normalizeEmailSubject(replySubject);
            return allEmailInCard
              .filter((msg) => normalizeEmailSubject(msg.subject || '') === threadSubj)
              .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
          }
          // Fallback: all email messages in conversation, chronological
          return [...allEmailInCard].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        })();

        if (emailMessages.length === 0) {
          throw new Error('No email messages found in this thread');
        }

        // Build thread for AI context (same order as thread view: oldest first)
        const emailThread = emailMessages.map(msg => ({
          direction: msg.direction,
          sender: msg.direction === 'outbound' ? (selectedBusiness?.business_name || msg.from_number) : msg.from_number,
          recipient: msg.to_number,
          subject: msg.subject || null,
          content: msg.body,
          created_at: msg.created_at
        }));

        console.log('🤖 Sending', emailThread.length, 'messages to AI for auto-reply');

        const { data, error } = await supabase.functions.invoke('ai-auto-reply', {
          body: {
            businessId: selectedBusiness?.id,
            businessName: selectedBusiness?.business_name || undefined,
            customerName: card.customer_name || undefined,
            emailThread: emailThread,
            additionalContext: card.type === 'customer' ? 'existing customer' : 'new contact'
          }
        });

        // Check for insufficient tokens FIRST (even if there's an error)
        if (data?.insufficientTokens) {
          setShowTokenPurchaseModal(true);
          setAIError('Insufficient tokens. Please purchase more to continue.');
          return;
        }

        if (error) throw error;

        if (!data?.success) {
          throw new Error(data?.error || 'Failed to generate reply');
        }

        console.log('🤖 AI Auto-Reply generated:', data);

        // Insert the generated reply into the composer
        if (emailInsertRef.current) {
          emailInsertRef.current(data.reply, true); // Replace with AI reply
        }

      } catch (error) {
        console.error('Error generating AI auto-reply:', error);
        setAIError(error instanceof Error ? error.message : 'Failed to generate reply');
        alert(error instanceof Error ? error.message : 'Failed to generate reply');
      } finally {
        setIsGeneratingAI(false);
      }
    };

    // Handle AI SMS reply generation
    const handleAISMSReply = async () => {
      console.log('📱 AI SMS Reply triggered');
      
      setIsGeneratingAI(true);
      setAIError(null);

      try {
        // Get SMS messages from timeline
        const smsMessages = timelineItems
          .filter(item => item.type === 'message' && item.message?.message_type === 'sms')
          .map(item => item.message!)
          .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()); // Sort chronologically

        if (smsMessages.length === 0) {
          throw new Error('No SMS messages found in this conversation');
        }

        // Get the most recent message
        const mostRecentMessage = smsMessages[smsMessages.length - 1];

        // Get milestones for context
        const conversationMilestones = timelineItems
          .filter(item => item.type === 'milestone')
          .map(item => ({
            id: item.id,
            type: item.milestone?.type || 'unknown',
            title: item.milestone?.title || '',
            description: item.milestone?.description,
            created_at: item.created_at,
            booking_id: item.milestone?.booking_id,
            proposal_id: item.milestone?.proposal_id
          }));

        console.log('🤖 Sending AI SMS reply request with', smsMessages.length, 'messages and', conversationMilestones.length, 'milestones');

        const { data, error } = await supabase.functions.invoke('ai-sms-reply', {
          body: {
            businessId: selectedBusiness?.id,
            businessName: selectedBusiness?.business_name || undefined,
            customerId: card.customer_id,
            customerName: card.customer_name || card.contact_info,
            recentMessage: {
              id: mostRecentMessage.id,
              direction: mostRecentMessage.direction,
              body: mostRecentMessage.body,
              created_at: mostRecentMessage.created_at
            },
            additionalContext: card.type === 'customer' ? 'existing customer' : 'new contact',
            milestones: conversationMilestones
          }
        });

        // Check for insufficient tokens FIRST
        if (data?.insufficientTokens) {
          setShowTokenPurchaseModal(true);
          setAIError('Insufficient tokens. Please purchase more to continue.');
          return;
        }

        if (error) throw error;

        if (!data?.success) {
          throw new Error(data?.error || 'Failed to generate SMS reply');
        }

        console.log('📱 AI SMS Reply generated:', data);
        console.log('📊 Context used:', data.contextUsed);

        // Set the generated reply in the message input
        setMessageInput(data.reply);

      } catch (error) {
        console.error('Error generating AI SMS reply:', error);
        setAIError(error instanceof Error ? error.message : 'Failed to generate SMS reply');
        alert(error instanceof Error ? error.message : 'Failed to generate SMS reply');
      } finally {
        setIsGeneratingAI(false);
      }
    };

    // Get email address for sending
    const getEmailAddress = () => {
      if (card.type === 'customer') {
        return currentCustomer?.email || card.contact_info || '';
      } else if (card.type === 'email') {
        return card.contact_info;
      }
      if (!card.contact_info) {
        const emailMsg = card.messages.find(m => m.message_type === 'email');
        if (emailMsg) {
          return emailMsg.direction === 'inbound' ? emailMsg.from_number : emailMsg.to_number;
        }
      }
      return '';
    };

    // Generate default email subject based on conversation
    const getDefaultEmailSubject = () => {
      // Get email messages from current timeline
      const emailMessages = timelineItems
        .filter(item => item.type === 'message' && item.message?.message_type === 'email' && item.message?.subject)
        .map(item => item.message!)
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      
      const lastEmailMessage = emailMessages[0];
      
      if (lastEmailMessage?.subject) {
        const subject = decodeEmailSubject(lastEmailMessage.subject);
        return subject.replace(/^(re:|fwd:|fw:)\s*/gi, '').trim() || subject;
      }
      
      return 'Reply from your booking';
    };

    // Get available reply methods based on card type and customer contact info
    const getAvailableReplyMethods = () => {
      if (card.type === 'customer') {
        const methods = [];
        if (currentCustomer?.phone) methods.push('sms');
        if (currentCustomer?.email) methods.push('email');
        return methods;
      } else if (card.type === 'phone') {
        return ['sms'];
      } else if (card.type === 'email') {
        return ['email'];
      }
      return [];
    };

    const availableReplyMethods = getAvailableReplyMethods();
    const canSms = availableReplyMethods.includes('sms');
    const canEmail = availableReplyMethods.includes('email');

    // Update reply method if current selection is not available
    useEffect(() => {
      if (availableReplyMethods.length > 0 && !availableReplyMethods.includes(replyMethod)) {
        // Set to the last message type if available, otherwise first available method
        if (availableReplyMethods.includes(card.last_message.message_type)) {
          setReplyMethod(card.last_message.message_type);
        } else {
          setReplyMethod(availableReplyMethods[0] as 'sms' | 'email');
        }
      }
    }, [card.id, availableReplyMethods, replyMethod, card.last_message.message_type]);

    useEffect(() => {
      if (activeTab === 'unread_suppliers' && unreadSuppliersCount === 0) {
        setActiveTab('unread');
      }
    }, [activeTab, unreadSuppliersCount]);

    // Email composer props
    const emailAddress = getEmailAddress();
    const emailSubject = getDefaultEmailSubject();
    const isEmailDisabled = !selectedBusiness?.id || !emailAddress;

          return (
        <div
          ref={conversationRootRef}
          className="messages-conversation-view flex flex-col h-full min-h-0 bg-white overflow-hidden"
        >
        <CallbackDetailsModal
          callbackId={selectedCallbackId}
          onClose={() => setSelectedCallbackId(null)}
        />
        {/* Conversation Header — iMessage style: centered circle avatar with
            initials, name underneath. (z-50 so the "+" dropdown clears the
            Lead banner.) */}
        <div className="flex-shrink-0 relative z-50 px-3 py-2 sm:px-4 border-b border-gray-200 bg-gray-50">
          <div className="relative flex items-center justify-between min-h-[64px]">
            {/* Left: mobile back button (44px target; onTouchEnd for iOS) */}
            <div className="flex items-center z-10">
              {onMobileBack && (
                <button
                  type="button"
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onMobileBack();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onMobileBack();
                  }}
                  className="md:hidden p-2 hover:bg-gray-100 rounded-xl transition-all duration-200 active:scale-[0.98] flex-shrink-0 touch-manipulation cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center -ml-1"
                  style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
                  aria-label="Back to conversations"
                >
                  <span className="sr-only">Back to conversations</span>
                  ←
                </button>
              )}
            </div>

            {/* Center: avatar + name column (tap opens customer details) */}
            <button
              type="button"
              onClick={() => {
                if (card.type === 'customer') {
                  if (currentCustomer) {
                    handleEditCustomer(currentCustomer);
                    return;
                  }
                  if (card.customer_id) {
                    const fullCustomer = customers.find(c => c.id === card.customer_id);
                    if (fullCustomer) handleEditCustomer(fullCustomer);
                  }
                } else {
                  handleCreateCustomer(card);
                }
              }}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center max-w-[55%] touch-manipulation active:opacity-70 transition-opacity duration-150"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              title={card.type === 'customer' ? 'View customer details' : 'Create customer'}
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-b from-gray-300 to-gray-400 text-white shadow-sm overflow-hidden">
                {(() => {
                  const nameSource =
                    card.type === 'customer'
                      ? displayName
                      : card.type === 'email'
                        ? extractEmailAddress(card.contact_info)
                        : '';
                  const initials = (nameSource || '')
                    .split(/[\s@._-]+/)
                    .filter((part: string) => /[a-zA-Z]/.test(part))
                    .slice(0, 2)
                    .map((part: string) => part[0]!.toUpperCase())
                    .join('');
                  if (initials) {
                    return <span className="text-[14px] font-semibold leading-none">{initials}</span>;
                  }
                  return card.type === 'phone' ? (
                    <Phone className="h-[18px] w-[18px]" />
                  ) : (
                    <Mail className="h-[18px] w-[18px]" />
                  );
                })()}
              </span>
              <span className="mt-1 flex items-center gap-0.5 max-w-full">
                <span className="truncate text-[12px] font-semibold text-gray-900 leading-tight">
                  {card.type === 'customer'
                    ? displayName
                    : card.type === 'email'
                      ? extractEmailAddress(card.contact_info)
                      : card.contact_info}
                </span>
                <ChevronRight className="h-3 w-3 text-gray-400 flex-shrink-0" strokeWidth={2.5} />
              </span>
              {currentCustomer?.is_supplier && (
                <span className="mt-0.5 flex items-center gap-1 bg-orange-100 text-orange-700 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                  <Building2 className="h-3 w-3" />
                  Supplier
                </span>
              )}
            </button>

            {/* Right: contextual actions + create menu + desktop close */}
            <div className="flex items-center gap-1 z-10">
              {card.type === 'email' && (
                <button
                  onClick={async () => {
                    if (window.confirm(`Mark ${extractEmailAddress(card.contact_info)} as junk? All future emails from this sender will be hidden from your unread pipeline.`)) {
                      try {
                        await muteEmailSender(card.contact_info);
                      } catch (error) {
                        console.error('Error muting sender:', error);
                        alert('Failed to mark sender as junk. Please try again.');
                      }
                    }
                  }}
                  className="p-2 hover:bg-red-50 rounded-xl transition-all duration-200 active:scale-[0.98]"
                  title="Mark as Junk"
                >
                  <Ban className="h-4 w-4 sm:h-5 sm:w-5 text-red-500 hover:text-red-700" />
                </button>
              )}
              {/* Blue + create dropdown — same UI as the CRM opportunity-details modal */}
              <CustomerCreateMenu customer={currentCustomer || null} />
              {/* Desktop Close Button */}
              <button
                onClick={() => setSelectedCard(null)}
                className="hidden md:block p-2 hover:bg-gray-100 rounded-xl transition-all duration-200 active:scale-[0.98] flex-shrink-0"
              >
                <span className="sr-only">Close conversation</span>
                ✕
              </button>
            </div>
          </div>
        </div>

        <NexusPhoneBalanceNotice businessId={selectedBusiness?.id} alertOnly />

        <ConversationLeadBanner
          opportunities={openOpportunities}
          onOpenOpportunity={(opportunityId) => setEmbeddedOpportunityId(opportunityId)}
          onSaveFollowUp={handleLeadFollowUpSave}
          onCompleteFollowUp={handleLeadFollowUpComplete}
          onChangeStage={handleLeadStageChange}
        />

        {embeddedOpportunityId !== null ? (
          <CrmEmbeddedOpportunityModal
            opportunityId={embeddedOpportunityId}
            onClose={() => setEmbeddedOpportunityId(null)}
            onUpdated={() => {
              if (!selectedBusiness?.id || resolvedCustomerIds.length === 0) return;
              getOpenOpportunitiesForCustomerIds(selectedBusiness.id, resolvedCustomerIds)
                .then(setOpenOpportunities)
                .catch(() => {});
            }}
          />
        ) : null}

        {/* Messages - Scrollable with iOS Style */}
        <div ref={messagesContainerRef} className="messages-scrollable flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-4 sm:px-6 sm:py-6 relative flex flex-col-reverse">
          {/* Skeleton loading during initial render */}
          {isFirstRenderRef.current && timelineItems.length === 0 && (
            <div className="space-y-6 animate-pulse">
              {/* Skeleton messages */}
              {[1, 2, 3].map((i) => (
                <div key={`skeleton-${i}`} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-xs lg:max-w-md px-5 py-4 rounded-2xl ${
                    i % 2 === 0 ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    <div className="space-y-3">
                      <div className="h-3 bg-gray-300 rounded w-16"></div>
                      <div className="h-4 bg-gray-300 rounded w-full"></div>
                      <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-300 rounded w-20"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Actual content */}
          {(!isFirstRenderRef.current || timelineItems.length > 0) && (
            <>
              {/* New Message Notification */}
              {newMessageNotification && (
                <div className="absolute top-6 left-1/2 transform -translate-x-1/2 z-10 bg-blue-500 text-white px-5 py-3 rounded-2xl shadow-lg animate-pulse">
                  <div className="flex items-center space-x-2">
                    <MessageCircle className="h-5 w-5" />
                    <span className="text-sm font-semibold">New message received</span>
                  </div>
                </div>
              )}
              
              {/* Milestone loading is now completely transparent and non-blocking - no UI indicators needed */}
              
              {/* Timeline items in reverse order for column-reverse layout */}
              {(() => {
              const latestEmailMessageId = (() => {
                let latestId: string | null = null;
                let latestTs = -1;
                for (const ti of timelineItems) {
                  if (ti.type !== 'message' || ti.message?.message_type !== 'email') continue;
                  const ts = new Date(ti.message.created_at).getTime();
                  if (!Number.isNaN(ts) && ts >= latestTs) {
                    latestTs = ts;
                    latestId = ti.message.id;
                  }
                }
                return latestId;
              })();
              return [...timelineItems].reverse().map((item, index) => {
                if (item.type === 'message' && item.message) {
                  const message = item.message;
                  const isOutbound = message.direction === 'outbound';
                  const messageTime = new Date(message.created_at).toLocaleString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  });

                  const subjectForDisplay = message.subject ? decodeEmailSubject(message.subject).replace(/^(re:|fwd:|fw:)\s*/gi, '').trim() : undefined;
                  const emailAttachments = message.message_type === 'email' && message.twilio_message_sid
                    ? (
                        emailAttachmentsByGmailId[message.twilio_message_sid] ||
                        timelineAttachmentsByGmailId[message.twilio_message_sid] ||
                        []
                      )
                    : [];
                  const inlineAttachments = emailAttachments.filter((attachment) => attachment.is_inline);
                  const downloadableAttachments = emailAttachments.filter((attachment) => !attachment.is_inline);
                  const attachmentsForDisplay = emailAttachments.filter(
                    (attachment) => !attachment.is_inline || Boolean(attachment.filename)
                  );
                  const threadMessages = message.message_type === 'email'
                    ? (() => {
                        const emailMessages = card.messages.filter((msg) => msg.message_type === 'email');
                        const fallbackSubject = emailMessages.find((msg) => msg.subject)?.subject || '';
                        const threadSubject = normalizeEmailSubject(
                          message.subject || card.last_message.subject || fallbackSubject
                        );
                        if (!threadSubject) {
                          return emailMessages;
                        }
                        return emailMessages.filter((msg) => {
                          const subjectValue = msg.subject || '';
                          return normalizeEmailSubject(subjectValue) === threadSubject;
                        });
                      })()
                    : [];
                  
                  // Callback status can change after the timeline was fetched
                  // (call placed, marked done, auto-completed) — prefer the
                  // live realtime-synced row over the timeline snapshot.
                  const liveCallbackStatus = message.message_type === 'callback'
                    ? ((agentCallbacks as any[]).find((cb) => cb.id === message.id)?.status ?? message.callback_status)
                    : undefined;

                  // Check if message is unread (not outbound AND not read in DB AND not locally marked as read)
                  // For email threads, check ALL messages in the thread, not just the latest
                  const isUnread = message.message_type === 'email'
                    ? threadMessages.some(
                        (m) => m.direction !== 'outbound' && m.read === 'unread' && !locallyReadMessages.has(m.id)
                      )
                    : (!isOutbound && (
                        (message.read === 'unread' && !locallyReadMessages.has(message.id)) ||
                        (message.message_type === 'call' && message.call_status === 'missed' && message.viewed === false) ||
                        (message.message_type === 'callback' && liveCallbackStatus === 'pending' && message.viewed === false && !locallyReadMessages.has(message.id))
                      ));
                  
                  const senderBadge = (
                    <SenderBadge
                      businessId={selectedBusiness?.id}
                      sender={senderOf(message)}
                      direction={isOutbound ? 'outbound' : 'inbound'}
                      aiResponse={message.ai_response}
                      isPhoneAgent={message.message_type === 'call' && !!message.agent_id}
                      customerName={card.customer_name || null}
                    />
                  );

                  return (
                    <div
                      key={item.id}
                      className={`flex items-end gap-1.5 ${isOutbound ? 'justify-end' : 'justify-start'} mb-4 sm:mb-6`}
                    >
                      {!isOutbound && senderBadge}
                      <div className={`relative ${message.message_type === 'email' ? 'flex-shrink-0 max-w-[90%] sm:max-w-[85%] lg:max-w-[75%]' : 'flex-shrink-0'}`}>
                        <div
                          className={`${
                            message.message_type === 'email'
                              ? 'w-full px-0 py-0 rounded-none shadow-none bg-transparent border-0'
                              : 'max-w-[280px] sm:max-w-xs lg:max-w-md px-4 py-3 sm:px-5 sm:py-4 rounded-2xl shadow-sm'
                          } ${
                            message.message_type === 'email'
                              ? ''
                              : message.message_type === 'callback'
                                ? 'bg-teal-50 text-gray-900 border border-teal-200'
                                : (isOutbound
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-white text-gray-900 border border-gray-200')
                          } ${(message.message_type === 'call' || message.message_type === 'callback') ? 'cursor-pointer hover:opacity-95 active:scale-[0.99] transition-all' : ''}`}
                          {...((message.message_type === 'call' || message.message_type === 'callback')
                            ? {
                                role: 'button' as const,
                                tabIndex: 0,
                                title: message.message_type === 'callback' ? 'View callback' : 'Tap to call',
                                onClick: (e: React.MouseEvent) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  // Callbacks always open the callback modal first —
                                  // the call itself starts from the modal's button.
                                  if (message.message_type === 'callback') {
                                    setSelectedCallbackId(message.id);
                                    return;
                                  }
                                  const phoneNumber = message.direction === 'inbound' ? message.from_number : message.to_number;
                                  if (!phoneNumber) return;
                                  window.dispatchEvent(new CustomEvent('closeAllModals'));
                                  setTimeout(() => {
                                    window.dispatchEvent(new CustomEvent('openDialler', {
                                      detail: {
                                        phoneNumber,
                                        customerId: currentCustomer?.id || card.customer_id || undefined,
                                        firstName: currentCustomer?.first_name,
                                        lastName: currentCustomer?.last_name,
                                      },
                                    }));
                                  }, 100);
                                },
                                onKeyDown: (e: React.KeyboardEvent) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    if (message.message_type === 'callback') {
                                      setSelectedCallbackId(message.id);
                                      return;
                                    }
                                    const phoneNumber = message.direction === 'inbound' ? message.from_number : message.to_number;
                                    if (!phoneNumber) return;
                                    window.dispatchEvent(new CustomEvent('closeAllModals'));
                                    setTimeout(() => {
                                      window.dispatchEvent(new CustomEvent('openDialler', {
                                        detail: {
                                          phoneNumber,
                                          customerId: currentCustomer?.id || card.customer_id || undefined,
                                          firstName: currentCustomer?.first_name,
                                          lastName: currentCustomer?.last_name,
                                        },
                                      }));
                                    }, 100);
                                  }
                                },
                              }
                            : {})}
                        >
                          {/* Message Type & Subject - More compact on mobile */}
                          <div className="flex items-center space-x-1.5 sm:space-x-2 mb-1.5 sm:mb-2">
                            {message.message_type === 'sms' ? (
                              <MessageCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                            ) : message.message_type === 'call' ? (
                              <Phone className={`h-3 w-3 sm:h-4 sm:w-4 ${
                                message.direction === 'outbound' ? 'text-white' : 
                                (message.call_status === 'answered' ? 'text-green-500' : 'text-red-500')
                              }`} />
                            ) : message.message_type === 'callback' ? (
                              <PhoneCall className="h-3 w-3 sm:h-4 sm:w-4 text-teal-600" />
                            ) : (
                              <Mail className="h-3 w-3 sm:h-4 sm:w-4" />
                            )}
                            <span className={`text-xs font-semibold uppercase tracking-wider ${message.message_type === 'callback' ? 'text-teal-700' : ''}`}>
                              {message.message_type === 'call' ? 
                                (message.direction === 'outbound' ? 'Call' : 
                                 (message.call_status === 'answered' ? 'Call' : 'Missed')) : 
                                message.message_type === 'callback' ? 'Callback' :
                                message.message_type}
                            </span>
                            {message.message_type === 'callback' && (
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                                liveCallbackStatus === 'completed'
                                  ? 'bg-green-100 text-green-700 border-green-200'
                                  : liveCallbackStatus === 'dismissed'
                                    ? 'bg-gray-100 text-gray-600 border-gray-200'
                                    : 'bg-amber-100 text-amber-700 border-amber-200'
                              }`}>
                                {liveCallbackStatus === 'completed' ? 'Call back completed' :
                                 liveCallbackStatus === 'dismissed' ? 'Dismissed' : 'Needs call back'}
                              </span>
                            )}
                            {/* AI agent badge — which agent placed/handled this call */}
                            {message.message_type === 'call' && message.agent_id && (
                              <span className="inline-flex items-center gap-1 bg-sky-100 text-sky-700 text-xs font-semibold px-2 py-0.5 rounded-full border border-sky-200">
                                <Sparkles className="h-3 w-3" />
                                {agentCallBadgeLabel(message.agent_id)}
                              </span>
                            )}
                            {/* AI Label - only show for outbound messages that used AI */}
                            {isOutbound && message.ai_response && (
                              <span className="bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700 text-xs font-semibold px-2 py-0.5 sm:px-3 sm:py-1 rounded-full border border-blue-200">
                                AI
                              </span>
                            )}
                          </div>
                          
                          {/* Email Subject handled inside EmailDisplay */}
                          {message.message_type !== 'email' && message.subject && (
                            <div className="font-semibold text-sm sm:text-base mb-1.5 sm:mb-2">
                              {message.subject}
                            </div>
                          )}
                          
                          {/* Message Body */}
                          {message.message_type === 'email' ? (
                            (() => {
                              const threadContainsOpenMessage = !!(
                                openMessageId &&
                                openMessageId !== OPEN_LATEST_EMAIL &&
                                threadMessages.some((m) => m.id === openMessageId)
                              );
                              const openLatestEmail =
                                openMessageId === OPEN_LATEST_EMAIL &&
                                autoOpenEmailReady &&
                                !!latestEmailMessageId &&
                                message.id === latestEmailMessageId;
                              const shouldOpenFullEmail = !!(
                                autoOpenEmailReady &&
                                openMessageId &&
                                message.message_type === 'email' &&
                                (openLatestEmail ||
                                  message.id === openMessageId ||
                                  threadContainsOpenMessage)
                              );
                              return (
                                <>
                                  <EmailDisplay 
                                    content={message.body}
                                    subject={subjectForDisplay}
                                    createdAt={message.created_at}
                                    className="text-xs sm:text-sm"
                                    isOutbound={isOutbound}
                                    compactView={true}
                                    replyTo={emailAddress}
                                    replySubject={
                                      message.subject
                                        ? decodeEmailSubject(message.subject).replace(/^(re:|fwd:|fw:)\s*/gi, '').trim()
                                        : ''
                                    }
                                    businessId={selectedBusiness?.id || ''}
                                    conversationId={card.id}
                                    customerId={card.customer_id}
                                    onSendSuccess={handleEmailSendSuccess}
                                    onSendError={handleEmailSendError}
                                    isEmailDisabled={isEmailDisabled}
                                    onReplyClick={(replyTo, replySubjectVal, gmailId) => {
                                      setReplySubject(replySubjectVal);
                                      setReplyToGmailId(gmailId ?? null);
                                      setReplyThreadId(null);
                                      setReplyInReplyTo(null);
                                      setReplyReferences(null);
                                      setIsReplyMode(true);
                                      setEmailComposeModalOpen(true);
                                    }}
                                    replyToGmailId={message.twilio_message_sid ?? (message as { external_id?: string | null }).external_id ?? null}
                                    inlineAttachments={inlineAttachments.map((attachment) => ({
                                      content_id: attachment.content_id || null,
                                      public_url: attachment.public_url
                                    }))}
                                    attachments={attachmentsForDisplay}
                                    threadMessages={threadMessages}
                                    userEmail={user?.email ?? undefined}
                                    initialOpenFullEmail={shouldOpenFullEmail}
                                    onFullEmailOpened={shouldOpenFullEmail ? onOpenMessageConsumed : undefined}
                                    locallyReadMessages={locallyReadMessages}
                                    onMarkAsRead={markMessagesAsRead}
                                    onBackToUnread={() => {
                                      setMobileModalOpen(false);
                                      setSelectedCard(null);
                                      setActiveTab('unread');
                                    }}
                                  />
                                </>
                              );
                            })()
                          ) : message.message_type === 'call' ? (
                            <div className="text-xs sm:text-sm leading-relaxed">
                              <span>{message.body}</span>
                              {message.call_status === 'answered' && message.duration_seconds != null && message.duration_seconds >= 0 && (
                                <span className="ml-1.5 opacity-90">
                                  ({Math.floor(message.duration_seconds / 60)}:{(message.duration_seconds % 60).toString().padStart(2, '0')})
                                </span>
                              )}
                              {/* Agent call outcome — rating captured, no answer, voicemail, etc. */}
                              {message.agent_id && agentOutcomeLine(message.agent_outcome, message.agent_collected) && (
                                <div className="mt-1 text-xs opacity-90 flex items-center gap-1">
                                  {message.agent_outcome === 'feedback_collected' && (
                                    <Star className="h-3 w-3 fill-current flex-shrink-0" />
                                  )}
                                  <span>{agentOutcomeLine(message.agent_outcome, message.agent_collected)}</span>
                                </div>
                              )}
                            </div>
                          ) : message.message_type === 'callback' ? (
                            // Compact, missed-call sized — full details live in the
                            // callback modal that opens on tap.
                            <div className="text-xs sm:text-sm leading-relaxed">
                              <span className="font-semibold text-gray-900">
                                {message.callback_name || 'Caller'} asked for a call back
                              </span>
                              {message.callback_reason && (
                                <span className="text-gray-600"> — {message.callback_reason}</span>
                              )}
                            </div>
                          ) : (
                            <>
                              {message.body && (
                                <div className="text-xs sm:text-sm leading-relaxed whitespace-pre-wrap break-words mb-2">
                              {message.body}
                            </div>
                              )}
                              {/* Display media (photos/videos) if present */}
                              {message.media_urls && Array.isArray(message.media_urls) && message.media_urls.length > 0 && (
                                <div className="space-y-2 mt-2">
                                  {message.media_urls.map((mediaUrl, idx) => {
                                    // Check if it's a Twilio URL or Supabase Storage URL
                                    const isTwilioUrl = mediaUrl.includes('api.twilio.com');
                                    const isSupabaseUrl = mediaUrl.includes('supabase.co') || mediaUrl.includes('supabase');
                                    
                                    let proxyUrl: string;
                                    if (isTwilioUrl) {
                                      // Extract the path from Twilio URL (everything after https://api.twilio.com)
                                      // mediaUrl format: https://api.twilio.com/2010-04-01/Accounts/.../Media/...
                                      const mediaPath = mediaUrl.replace('https://api.twilio.com', '');
                                      
                                      // Construct proxy URL - fetch via Supabase client with auth headers
                                      proxyUrl = selectedBusiness?.id
                                        ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/twilio-media-proxy?path=${encodeURIComponent(mediaPath)}&businessId=${selectedBusiness.id}`
                                        : mediaUrl;
                                    } else if (isSupabaseUrl) {
                                      // For Supabase Storage URLs, use them directly (they're already public)
                                      proxyUrl = mediaUrl;
                                    } else {
                                      // Fallback: use URL directly
                                      proxyUrl = mediaUrl;
                                    }
                                    
                                    const isImage = /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i.test(mediaUrl);
                                    const isBlobUrl = mediaUrl.startsWith('blob:');
                                    const mightBeImage = isImage || mediaUrl.includes('/Media/') || isSupabaseUrl || isBlobUrl;
                                    
                                    return (
                                      <MediaDisplayComponent
                                        key={idx}
                                        proxyUrl={proxyUrl}
                                        mediaUrl={mediaUrl}
                                        isImage={mightBeImage}
                                        index={idx}
                                        allMediaUrls={message.media_urls || []}
                                        onOpenModal={(initialIndex) => {
                                          openImageViewer({
                                            images: buildViewerImages(message.media_urls || [], selectedBusiness?.id),
                                            index: initialIndex,
                                            editable: false,
                                          });
                                        }}
                                        displayInline={true}
                                      />
                                    );
                                  })}
                                </div>
                              )}
                            </>
                          )}
                          
                          {/* Timestamp */}
                          <div className={`text-xs font-medium mt-2 sm:mt-3 ${
                            message.message_type === 'email'
                              ? 'text-gray-500'
                              : (isOutbound ? 'text-blue-100' : 'text-gray-500')
                          }`}>
                            {messageTime}
                          </div>
                        </div>

                        {/* Call recording + transcript — separate bubbles below the
                            call bubble (the call bubble itself is tap-to-redial) */}
                        {message.message_type === 'call' && (
                          <CallRecordingDetails
                            recordingUrl={message.recording_url}
                            transcriptionText={message.transcription_text}
                            transcriptionStatus={message.transcription_status}
                            businessId={selectedBusiness?.id}
                            isOutbound={isOutbound}
                          />
                        )}

                        {/* Unread Badge - "Unread" text badge positioned outside the message bubble */}
                        {isUnread && (
                          <button
                            onClick={() => {
                              if (message.message_type === 'email' && threadMessages.length > 0) {
                                const unreadIds = threadMessages
                                  .filter((m) => m.direction !== 'outbound' && m.read === 'unread' && !locallyReadMessages.has(m.id))
                                  .map((m) => m.id);
                                if (unreadIds.length > 0) {
                                  markMessagesAsRead(unreadIds);
                                }
                              } else {
                                markMessageAsRead(message.id, message.message_type);
                              }
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-all duration-200 active:scale-[0.98] cursor-pointer shadow-lg"
                            title="Mark as read"
                          >
                            Unread
                          </button>
                        )}
                      </div>
                      {isOutbound && senderBadge}
                    </div>
                  );
                } else if (item.type === 'milestone' && item.milestone) {
                  const milestone = item.milestone;
                  const milestoneTime = new Date(milestone.created_at).toLocaleString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  });
                  
                  // Helper function to get milestone icon - professional neutral styling
                  const getMilestoneIcon = (type: string, title?: string) => {
                    const iconClass = "h-5 w-5 text-slate-600";
                    
                    switch (type) {
                      case 'booking_scheduled':
                        return <Calendar className={iconClass} />;
                      case 'booking_completed':
                        return <CheckCircle className={iconClass} />;
                      case 'proposal_created':
                        return <FileText className={iconClass} />;
                      case 'custom':
                        // Check milestone title to determine icon
                        if (title?.includes('Invoice Sent')) {
                          return <Mail className={iconClass} />;
                        } else if (title?.includes('Proposal Sent')) {
                          return <Mail className={iconClass} />;
                        } else if (title?.includes('Proposal Viewed')) {
                          return <Eye className={iconClass} />;
                        } else if (title?.includes('Proposal Approved')) {
                          return <CheckCircle className={iconClass} />;
                        }
                        return <CheckCircle className={iconClass} />;
                      default:
                        return <CheckCircle className={iconClass} />;
                    }
                  };
                  
                  // Helper function to get milestone color - professional neutral styling
                  const getMilestoneColor = () => {
                    return 'bg-slate-50 text-slate-700 border-slate-200 shadow-sm hover:shadow-md';
                  };
                  
                  return (
                    <div key={item.id} className="flex justify-end mb-4 sm:mb-6">
                      <div 
                        className={`max-w-[280px] sm:max-w-sm px-4 py-3 sm:px-5 sm:py-4 rounded-2xl border cursor-pointer transition-all duration-200 active:scale-[0.98] ${getMilestoneColor()}`}
                        onClick={() => handleMilestoneClick(milestone)}
                        title={milestone.milestone.metadata?.entity_type === 'proposal' ? 'Click to view proposal details' : 'Click to view booking details'}
                      >
                        <div className="flex items-start space-x-3 sm:space-x-4">
                          <div className="flex-shrink-0 mt-0.5 sm:mt-1">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-slate-100 rounded-xl sm:rounded-2xl flex items-center justify-center border border-slate-200">
                              {getMilestoneIcon(milestone.milestone.type, milestone.milestone.title)}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm sm:text-base text-slate-800 mb-1.5 sm:mb-2">
                              {milestone.milestone.title}
                            </div>
                            {milestone.milestone.description && (
                              <div className="text-xs sm:text-sm text-slate-600 leading-relaxed mb-2 sm:mb-3">
                                {milestone.milestone.description}
                              </div>
                            )}
                            <div className="text-xs sm:text-sm text-slate-500 font-semibold">
                              {milestoneTime}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                
                return null;
              });
              })()}
              
              {/* Scroll to Bottom Button - Hidden on mobile when conversation is open */}
              {showScrollToBottom && (
                <button
                  onClick={scrollToBottom}
                  className={`fixed bottom-32 right-8 bg-blue-500 hover:bg-blue-600 text-white p-4 rounded-2xl shadow-lg z-20 transition-all duration-200 active:scale-95 animate-bounce hidden md:block`}
                  title="Scroll to bottom"
                >
                  <ChevronDown className="h-6 w-6" />
                </button>
              )}
              
              {/* Show More Messages Button - at END of DOM to appear at TOP visually with column-reverse */}
              {hasMoreTimeline && (
                <div className="flex justify-center py-4">
                  <button
                    onClick={loadMoreTimeline}
                    disabled={loadingMoreTimeline}
                    className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-6 py-3 rounded-2xl text-sm font-semibold transition-all duration-200 active:scale-95 flex items-center space-x-3 shadow-lg"
                  >
                    {loadingMoreTimeline ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        <span>Loading...</span>
                      </>
                    ) : (
                      <span>Show More Messages</span>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Message Input Area - iOS Style (bottom safe area handled by mobile container) */}
        <div className="messages-input-area flex-shrink-0 px-3 py-3 sm:px-4 border-t border-gray-200 bg-white">
          {/* Toolbar above the composer pill, matching the agent chat layout */}
          <div>
            {/* Snippets Toolbar — shown whenever SMS composing is available */}
            {canSms && (
            <div className="mb-2 flex items-center gap-2">
                  {/* AI Reply Button for SMS */}
                  <button
                    type="button"
                    onClick={async () => {
                      await handleAISMSReply();
                    }}
                    disabled={isGeneratingAI}
                    className="inline-flex items-center justify-center px-2.5 py-1.5 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Generate AI SMS reply"
                    aria-label="Generate AI SMS reply"
                  >
                    {isGeneratingAI ? (
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setLocalSnippetsModalOpen(true);
                    }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all duration-200 active:scale-[0.98]"
                    title="Insert snippet"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Snippets</span>
                  </button>

                  {/* New Email — opens the full email composer (replaces the old SMS/Email toggle) */}
                  {canEmail && (
                    <button
                      type="button"
                      onClick={() => setEmailComposeModalOpen(true)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-200 rounded-xl hover:bg-red-50 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 transition-all duration-200 active:scale-[0.98]"
                      title="Compose a new email"
                    >
                      <Mail className="h-4 w-4" />
                      <span>Email</span>
                    </button>
                  )}
            </div>
            )}
            
            {/* Main Input Area — SMS composer by default; email-only contacts get a compose button */}
            <div className="relative">
            {canSms ? (
                /* SMS Reply Input with AI Suggestions */
                <SMSReplyComposer
                  businessId={selectedBusiness?.id || ''}
                  value={messageInput}
                  onChange={handleMessageInputChange}
                  onSend={handleSendSMS}
                  isSending={isSending}
                  error={sendError}
                  conversationId={card.id}
                  customerId={card.customer_id}
                  phoneNumber={card.type === 'phone' ? card.contact_info : undefined}
                  shouldFetchAI={false}
                  className="w-full"
                />
              ) : (
              <div className="w-full p-4">
                <button
                  type="button"
                  onClick={() => setEmailComposeModalOpen(true)}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  Compose New Email
                </button>
              </div>
            )}
            </div>
          </div>
        </div>

        {emailComposeModalOpen && createPortal(
          <div
            className="fixed inset-0 z-[100070] transition-opacity duration-200 bg-black/50"
            style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
            onClick={() => {
              setEmailComposeModalOpen(false);
              setIsReplyMode(false);
              setReplySubject('');
              setReplyToGmailId(null);
              setReplyThreadId(null);
              setReplyInReplyTo(null);
              setReplyReferences(null);
              setShowAIPrompt(false);
              setAIPrompt('');
              setAIMode(null);
              setAIError(null);
              setIsGeneratingAI(false);
            }}
          >
            <div
              className="bg-white transform transition-all duration-200 fixed inset-y-0 right-0 w-full sm:w-[560px] shadow-2xl flex flex-col translate-x-0"
              style={{
                top: 'env(safe-area-inset-top, 0px)',
                // Rise above the iOS keyboard (resize:none) so long drafts stay
                // scrollable — the keyboard otherwise covers the editor bottom.
                bottom: 'max(env(safe-area-inset-bottom, 0px), var(--keyboard-inset, 0px))',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50/60 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="text-sm font-semibold text-gray-800">{isReplyMode ? 'Reply' : 'New Message'}</div>
                  {/* AI Auto Reply Button - Only show in reply mode */}
                  {isReplyMode && (
                    <button
                      type="button"
                      onClick={async () => {
                        await handleAIAutoReply();
                      }}
                      disabled={isGeneratingAI}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Generate AI reply based on email thread"
                    >
                      {isGeneratingAI ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Generating...</span>
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4" />
                          <span>AI Reply</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setLocalSnippetsModalOpen(true);
                    }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all duration-200 active:scale-[0.98]"
                    title="Insert snippet"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Snippets</span>
                  </button>
                <button
                  onClick={() => {
                    setEmailComposeModalOpen(false);
                    setIsReplyMode(false);
                    setReplySubject('');
                    setReplyToGmailId(null);
                    setReplyThreadId(null);
                    setReplyInReplyTo(null);
                    setReplyReferences(null);
                    setShowAIPrompt(false);
                    setAIPrompt('');
                    setAIMode(null);
                    setAIError(null);
                    setIsGeneratingAI(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-1.5 rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
                </div>
              </div>
              <div className="px-4 flex-shrink-0">
                <div className="flex items-center gap-2 border-b border-gray-200 py-2">
                  <div className="text-base text-gray-500 w-10">To</div>
                  <input
                    className="flex-1 text-base text-gray-800 bg-transparent outline-none"
                    style={{ fontSize: '16px' }}
                    value={emailAddress}
                    readOnly
                  />
                </div>
                {isReplyMode && replySubject && (
                  <div className="flex items-center gap-2 border-b border-gray-200 py-2">
                    <div className="text-base text-gray-500 w-10">Subject</div>
                    <input
                      className="flex-1 text-base text-gray-800 bg-gray-50 outline-none cursor-not-allowed"
                      style={{ fontSize: '16px' }}
                      value={replySubject}
                      readOnly
                    />
                  </div>
                )}
              </div>
              <div className="flex-1 min-h-0 flex flex-col relative">
                <EmailReplyComposer
                  businessId={selectedBusiness?.id || ''}
                  to={emailAddress}
                  defaultSubject={isReplyMode ? replySubject : ""}
                  placeholder="Compose"
                  onSendSuccess={(data, details) => {
                    handleEmailSendSuccess(data, details);
                    setEmailComposeModalOpen(false);
                    setIsReplyMode(false);
                    setReplySubject('');
                    setReplyToGmailId(null);
                    setReplyThreadId(null);
                    setReplyInReplyTo(null);
                    setReplyReferences(null);
                    setShowAIPrompt(false);
                    setAIPrompt('');
                    setAIMode(null);
                    setAIError(null);
                    setIsGeneratingAI(false);
                  }}
                  onSendError={handleEmailSendError}
                  disabled={isEmailDisabled}
                  className="flex-1 min-h-0"
                  variant="gmail"
                  conversationId={card.id}
                  customerId={card.customer_id}
                  shouldFetchAI={false}
                  threadId={replyThreadId ?? undefined}
                  inReplyTo={replyInReplyTo ?? undefined}
                  references={replyReferences ?? undefined}
                  showSubjectInput={!isReplyMode}
                  subjectReadOnly={isReplyMode}
                  toolbarPosition="bottom"
                  onInsertContent={(insertFn) => {
                    emailInsertRef.current = insertFn;
                  }}
                  onSubjectInsert={(insertFn) => {
                    emailSubjectInsertRef.current = insertFn;
                  }}
                  onAICompose={(currentBody, currentSubject) => {
                    setEmailBody(currentBody);
                    setCurrentEmailSubject(currentSubject);
                    setShowAIPrompt(true);
                  }}
                  isAIGenerating={isGeneratingAI}
                />
                
                {showAIPrompt && emailBody.trim() && !aiMode && (
                  <div className="absolute bottom-16 right-4 z-50">
                    <div className="bg-gray-800 rounded-2xl shadow-2xl border border-gray-700 py-2 min-w-[200px]">
                      <button type="button" onClick={() => setAIMode('compose')} className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-gray-700 flex items-center gap-2 transition-colors">
                        <Sparkles className="h-4 w-4 text-blue-400" /><span>Compose</span>
                      </button>
                      <button type="button" onClick={() => { setShowAIPrompt(false); handleGenerateAIEmail('autocomplete'); }} disabled={isGeneratingAI} className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-gray-700 flex items-center gap-2 transition-colors disabled:opacity-50">
                        <svg className="h-4 w-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg><span>Auto Complete</span>
                      </button>
                      <button type="button" onClick={() => setAIMode('polish')} className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-gray-700 flex items-center gap-2 transition-colors">
                        <svg className="h-4 w-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg><span>Polish</span>
                      </button>
                      <div className="border-t border-gray-700 my-1"></div>
                      <button type="button" onClick={() => { setShowAIPrompt(false); setAIPrompt(''); setAIMode(null); setAIError(null); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-400 hover:bg-gray-700 hover:text-gray-300 transition-colors">Cancel</button>
                    </div>
                  </div>
                )}

                {showAIPrompt && (aiMode === 'compose' || aiMode === 'polish' || !emailBody.trim()) && (
                  <div className="absolute bottom-16 left-3 right-3 z-50 p-4 bg-gray-800 rounded-2xl shadow-2xl">
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        value={aiPrompt}
                        onChange={(e) => setAIPrompt(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !isGeneratingAI) {
                            if ((aiMode === 'compose' || !emailBody.trim()) && !aiPrompt.trim()) return;
                            e.preventDefault();
                            handleGenerateAIEmail(aiMode || 'compose');
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            setShowAIPrompt(false); setAIPrompt(''); setAIMode(null); setAIError(null);
                          }
                        }}
                        placeholder={aiMode === 'polish' ? 'Optional: e.g. make it longer, more formal...' : 'E.g. write a thank you email...'}
                        className="flex-1 bg-transparent text-white placeholder-gray-400 text-base focus:outline-none py-1"
                        style={{ fontSize: '16px' }}
                        autoFocus
                        autoComplete="off"
                        disabled={isGeneratingAI}
                      />
                      {!isGeneratingAI ? (
                        <button
                          type="button"
                          onClick={() => { const mode = aiMode || 'compose'; if ((mode === 'compose' || !emailBody.trim()) && !aiPrompt.trim()) return; handleGenerateAIEmail(mode); }}
                          disabled={(aiMode === 'compose' || !emailBody.trim()) && !aiPrompt.trim()}
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >Generate</button>
                      ) : (
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      )}
                    </div>
                    {aiError && (
                      <div className="mt-3 px-3 py-2 bg-red-500/20 border border-red-400/30 text-red-200 text-sm rounded-lg">{aiError}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Snippets Modal - Local to this conversation */}
        {selectedBusiness && (
          <SnippetsModal
            isOpen={localSnippetsModalOpen}
            onClose={() => {
              console.log('🚪 Closing snippets modal');
              setLocalSnippetsModalOpen(false);
            }}
            onSelectSnippet={(snippet) => {
              console.log('✅✅✅ SNIPPET SELECTED:', snippet.title);
              handleSnippetSelect(snippet);
            }}
          />
        )}

      </div>
    );
}
export default function Messages() {
  const { selectedBusiness } = useBusiness();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'unread' | 'read' | 'unread_suppliers'>('unread');
  const [showMutedView, setShowMutedView] = useState(false); // Toggle for muted senders view (now called "Show Spam")
  const [mutedSenders, setMutedSenders] = useState<Set<string>>(new Set()); // Set of muted email addresses
  const [mutedPhones, setMutedPhones] = useState<Set<string>>(new Set()); // Ten-digit keys of spam numbers
  const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);
  const actionsDropdownRef = useRef<HTMLDivElement>(null);
  const [pipelineSearchQuery, setPipelineSearchQuery] = useState('');
  // Messages on the core tables (messages.data_source = 'core' for this business).
  // State drives renders; the ref lets async handlers read the latest answer.
  const [coreMessages, setCoreMessages] = useState(false);
  const coreMessagesRef = useRef(false);
  // Bumped after each core inbox reload so the open thread re-pulls its page.
  const [coreThreadVersion, setCoreThreadVersion] = useState(0);
  const [messages, setMessages] = useState<Message[]>([]);
  const [emailAttachmentsByGmailId, setEmailAttachmentsByGmailId] = useState<Record<string, EmailAttachment[]>>({});
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customersWithOpenOpportunities, setCustomersWithOpenOpportunities] = useState<Set<string>>(new Set());
  const [callHistory, setCallHistory] = useState<any[]>([]);
  // AI receptionist callback requests (agent_callbacks) — rendered in the
  // thread as labeled "Callback requested" items, unread until handled.
  const [agentCallbacks, setAgentCallbacks] = useState<any[]>([]);
  const [communicationCards, setCommunicationCards] = useState<CommunicationCard[]>([]);
  // Instant-paint cards from the server-side summary RPC; merged with / superseded
  // by the fully-built (30-day window) cards once the heavy fetch completes.
  const summaryCardsRef = useRef<CommunicationCard[]>([]);
  // Conversations read THIS session (by opening them). They stay visible in the
  // Unread pipeline until a reload (so the list doesn't yank out from under you),
  // but are written to the cache as read so re-opening the app shows them correctly.
  const readInSessionRef = useRef<Set<string>>(new Set());
  const [loading, setLoading] = useState(false); // Start with false for instant display
  
  // URL parameter handling for customer selection
  const [urlCustomerName, setUrlCustomerName] = useState<string | null>(null);
  const [urlCustomerId, setUrlCustomerId] = useState<string | null>(null);
  const [urlContact, setUrlContact] = useState<string | null>(null);
  const [cameFromCRM, setCameFromCRM] = useState(false);
  const [cameFromBooking, setCameFromBooking] = useState(false);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [sourcePage, setSourcePage] = useState<string | null>(null);

  // Parse URL parameters on component mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const customerId = searchParams.get('customerId');
    const contact = searchParams.get('contact');
    const source = searchParams.get('source');
    const proposalId = searchParams.get('proposalId');
    
    if (customerId) {
      setUrlCustomerId(customerId);
    }
    if (contact) {
      setUrlContact(contact);
    }
    
    if (source) {
      setSourcePage(source);
      setCameFromCRM(source === 'crm');
      setCameFromBooking(source === 'bookings' || source === 'calendar');
    }
    
    if (proposalId) {
      // Handle proposal context if needed
    }
  }, [location.search]);
  
  // Check for Gmail connections that need re-authentication.
  // Dismiss keeps a Messages-only banner. Closing that banner stays quiet
  // until the next app session. A refresh returns here (web and iOS).
  const gmailReturnHandledRef = useRef<string | null>(null);
  const applyGmailReauthPrompt = useCallback((row: { email?: string | null } | null) => {
    if (!row) {
      try {
        sessionStorage.removeItem(GMAIL_REAUTH_BANNER_KEY);
        sessionStorage.removeItem(GMAIL_REAUTH_BANNER_CLOSED_KEY);
        sessionStorage.removeItem(GMAIL_REAUTH_PENDING_KEY);
      } catch { /* ignore */ }
      setGmailReauthEmail(null);
      setShowGmailReauthModal(false);
      setShowGmailReauthBanner(false);
      return;
    }

    setGmailReauthEmail(row.email || null);

    let banner = false;
    let closed = false;
    let pending = false;
    try {
      banner = sessionStorage.getItem(GMAIL_REAUTH_BANNER_KEY) === '1';
      closed = sessionStorage.getItem(GMAIL_REAUTH_BANNER_CLOSED_KEY) === '1';
      pending = sessionStorage.getItem(GMAIL_REAUTH_PENDING_KEY) === '1';
    } catch { /* ignore */ }

    if (pending) {
      try { sessionStorage.removeItem(GMAIL_REAUTH_PENDING_KEY); } catch { /* ignore */ }
      setShowGmailReauthBanner(false);
      setShowGmailReauthModal(true);
      return;
    }
    if (closed) {
      setShowGmailReauthModal(false);
      setShowGmailReauthBanner(false);
      return;
    }
    if (banner) {
      setShowGmailReauthModal(false);
      setShowGmailReauthBanner(true);
      return;
    }
    setShowGmailReauthBanner(false);
    setShowGmailReauthModal(true);
  }, []);

  const recheckGmailReauth = useCallback(async () => {
    if (!selectedBusiness?.id) return undefined;
    try {
      const { data, error } = await supabase
        .from('gmail_connections')
        .select('id, email, needs_reauth')
        .eq('business_id', selectedBusiness.id)
        .eq('is_active', true)
        .eq('needs_reauth', true)
        .limit(1);
      if (error) {
        console.error('Error checking Gmail re-auth status:', error);
        return undefined;
      }
      const row = data && data.length > 0 ? data[0] : null;
      applyGmailReauthPrompt(row);
      return row;
    } catch (error) {
      console.error('Error checking Gmail re-auth:', error);
      return undefined;
    }
  }, [selectedBusiness?.id, applyGmailReauthPrompt]);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams(location.search);
    const justConnected = params.get('success') === 'gmail_connected';
    const oauthDenied = params.get('error') === 'oauth_denied';

    if ((justConnected || oauthDenied) && gmailReturnHandledRef.current !== location.search) {
      gmailReturnHandledRef.current = location.search;
      if (justConnected) {
        try {
          sessionStorage.removeItem(GMAIL_REAUTH_BANNER_KEY);
          sessionStorage.removeItem(GMAIL_REAUTH_BANNER_CLOSED_KEY);
          sessionStorage.removeItem(GMAIL_REAUTH_PENDING_KEY);
        } catch { /* ignore */ }
        setShowGmailReauthModal(false);
        setShowGmailReauthBanner(false);
        setGmailReauthEmail(null);
        if (window.opener) {
          try {
            window.opener.postMessage({ type: 'GMAIL_OAUTH_SUCCESS' }, window.location.origin);
          } catch { /* opener may already be gone */ }
          window.close();
        } else {
          toast.success('Gmail connection refreshed');
        }
      } else {
        try { sessionStorage.removeItem(GMAIL_REAUTH_PENDING_KEY); } catch { /* ignore */ }
        toast.error('Gmail connection was not refreshed');
      }
      params.delete('success');
      params.delete('error');
      const next = params.toString();
      navigate({ pathname: location.pathname, search: next ? `?${next}` : '' }, { replace: true });
      return;
    }

    if (!selectedBusiness?.id || justConnected) return;

    (async () => {
      try {
        const { data, error } = await supabase
          .from('gmail_connections')
          .select('id, email, needs_reauth')
          .eq('business_id', selectedBusiness.id)
          .eq('is_active', true)
          .eq('needs_reauth', true)
          .limit(1);
        if (cancelled || error) {
          if (error) console.error('Error checking Gmail re-auth status:', error);
          return;
        }
        applyGmailReauthPrompt(data && data.length > 0 ? data[0] : null);
      } catch (error) {
        console.error('Error checking Gmail re-auth:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedBusiness?.id, location.search, location.pathname, navigate, applyGmailReauthPrompt]);
  
  /** Twilio + Gmail: messaging needs at least one (same rules as Integrations page).
   *  Default to 'ready' / true so the UI renders instantly from cache.
   *  The real check runs in the parallelized data fetch below. */
  // Start in 'loading' so we NEVER first-paint the full inbox UI before the
  // Twilio/Gmail check resolves. Brand-new businesses (no integrations, no
  // data) used to briefly render the whole inbox with empty state — any
  // render error in that path white-screened the app before the "Connect
  // messaging" gate could appear. Cached status (native) resolves instantly;
  // the network check takes a few hundred ms at most.
  const [messagingIntegrationsStatus, setMessagingIntegrationsStatus] = useState<'loading' | 'ready'>('loading');
  const [canUseMessaging, setCanUseMessaging] = useState(true);

  
  // Add dedicated loading state for communication cards
  const [cardsLoading, setCardsLoading] = useState(false); // Start false for instant display
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  
  const [selectedCard, setSelectedCard] = useState<CommunicationCard | null>(null);
  /** When set, ConversationView should open this message (or its email thread) after load */
  const [openMessageIdWhenSelected, setOpenMessageIdWhenSelected] = useState<string | null>(null);

  // "make THIS a customer", "reply to this" — the assistant resolves
  // conversation:"onscreen" through this surface to whatever thread is open.
  const aiMessagesBridgeRef = useRef<{ selectedCard: CommunicationCard | null }>({ selectedCard: null });
  aiMessagesBridgeRef.current = { selectedCard };
  useEffect(() => {
    return registerAssistantSurface('messages', {
      activeThread: () => {
        const card = aiMessagesBridgeRef.current.selectedCard;
        if (!card) return 'error: no conversation is open on the Messages screen right now — ask the user who they mean.';
        return JSON.stringify({
          type: card.type,
          contact_info: card.contact_info,
          customer_id: card.customer_id || '',
          customer_name: card.customer_name || '',
        });
      },
    });
  }, []);
  const [editCustomerModalOpen, setEditCustomerModalOpen] = useState(false);
  const [createCustomerModalOpen, setCreateCustomerModalOpen] = useState(false);
  const [customerDetailsModalOpen, setCustomerDetailsModalOpen] = useState(false);
  const [selectedCustomerForEdit, setSelectedCustomerForEdit] = useState<Customer | null>(null);
  const [selectedCustomerForDetails, setSelectedCustomerForDetails] = useState<Customer | null>(null);
  const [createCustomerInitialData, setCreateCustomerInitialData] = useState<{ phone?: string; email?: string }>({});

  // Track locally marked-as-read messages for instant UI updates
  const [locallyReadMessages, setLocallyReadMessages] = useState<Set<string>>(new Set());
  
  // Milestones for customers
  const [customerMilestones, setCustomerMilestones] = useState<{[customerId: string]: BookingMilestone[]}>({});
  
  // Booking info modal state
  const [bookingInfoModalOpen, setBookingInfoModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  
  // Beta payments modal state
  const [betaPaymentsModalOpen, setBetaPaymentsModalOpen] = useState(false);
  
  // Gmail re-authentication modal state
  const [showGmailReauthModal, setShowGmailReauthModal] = useState(false);
  const [showGmailReauthBanner, setShowGmailReauthBanner] = useState(false);
  const [gmailReauthEmail, setGmailReauthEmail] = useState<string | null>(null);
  const [gmailReauthConnecting, setGmailReauthConnecting] = useState(false);

  const dismissGmailReauth = () => {
    setShowGmailReauthModal(false);
    setShowGmailReauthBanner(true);
    setGmailReauthConnecting(false);
    try {
      sessionStorage.setItem(GMAIL_REAUTH_BANNER_KEY, '1');
      sessionStorage.removeItem(GMAIL_REAUTH_BANNER_CLOSED_KEY);
    } catch { /* ignore */ }
  };

  const closeGmailReauthBanner = () => {
    setShowGmailReauthBanner(false);
    setShowGmailReauthModal(false);
    try {
      sessionStorage.removeItem(GMAIL_REAUTH_BANNER_KEY);
      sessionStorage.setItem(GMAIL_REAUTH_BANNER_CLOSED_KEY, '1');
    } catch { /* ignore */ }
  };

  const refreshGmailConnection = async () => {
    if (!selectedBusiness?.id || gmailReauthConnecting) return;
    setGmailReauthConnecting(true);
    try {
      const mode = await startGmailOAuth({
        businessId: selectedBusiness.id,
        returnPath: '/messages',
      });
      if (mode === 'popup') {
        const stillNeedsReauth = await recheckGmailReauth();
        if (stillNeedsReauth === null) toast.success('Gmail connection refreshed');
      }
    } catch (error) {
      console.error('Error starting Gmail re-auth:', error);
      toast.error('Could not start Gmail sign-in');
    } finally {
      setGmailReauthConnecting(false);
    }
  };

  const [selectedBookingForPayments, setSelectedBookingForPayments] = useState<Booking | null>(null);
  
  // Proposal details modal state
  const [proposalDetailsModalOpen, setProposalDetailsModalOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);
  
  // Message organization setting (only affects unread pipeline)
  const [messageOrganization, setMessageOrganization] = useState<'chronological' | 'sms_first' | 'email_first'>('chronological');

  // Listen for setting changes from AppSettingsModal
  useEffect(() => {
    const handler = (e: Event) => {
      const val = (e as CustomEvent).detail?.value;
      if (['chronological', 'sms_first', 'email_first'].includes(val)) {
        setMessageOrganization(val);
      }
    };
    window.addEventListener('message-organization-changed', handler);
    return () => window.removeEventListener('message-organization-changed', handler);
  }, []);

  // Pipeline pagination state
  const CARDS_PAGE_SIZE = 15; // Load 15 conversations at a time
  const [pipelineOffset, setPipelineOffset] = useState(CARDS_PAGE_SIZE);
  const [loadingMoreCards, setLoadingMoreCards] = useState(false);
  
  // Message pagination state
  const INITIAL_MESSAGE_DAYS = 30; // Load last 30 days initially
  const MESSAGES_PAGE_SIZE = 500; // Load 500 messages at a time when loading more
  const CALL_HISTORY_DAYS = 90;
  const CALL_HISTORY_LIMIT = 200;
  const [messagesLoadedUntil, setMessagesLoadedUntil] = useState<Date | null>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileModalOpen, setMobileModalOpen] = useState(false);
  
  // Compose new message modal state
  const [composeModalOpen, setComposeModalOpen] = useState(false);
  const [composeType, setComposeType] = useState<'sms' | 'email' | null>(null);
  const [composeContact, setComposeContact] = useState('');
  const [matchedCustomer, setMatchedCustomer] = useState<Customer | null>(null);
  const [isLookingUpCustomer, setIsLookingUpCustomer] = useState(false);
  const [matchingCustomers, setMatchingCustomers] = useState<Customer[]>([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  
  // Standalone email compose modal state (for new emails from compose button)
  const [standaloneEmailComposeOpen, setStandaloneEmailComposeOpen] = useState(false);
  const [standaloneEmailAddress, setStandaloneEmailAddress] = useState('');
  const [standaloneEmailCustomer, setStandaloneEmailCustomer] = useState<Customer | null>(null);
  const [standaloneEmailBody, setStandaloneEmailBody] = useState('');
  const [standaloneEmailSubject, setStandaloneEmailSubject] = useState('');
  const [standaloneShowAIPrompt, setStandaloneShowAIPrompt] = useState(false);
  const [standaloneAIPrompt, setStandaloneAIPrompt] = useState('');
  const [standaloneIsGeneratingAI, setStandaloneIsGeneratingAI] = useState(false);
  const [standaloneAIError, setStandaloneAIError] = useState<string | null>(null);
  const [standaloneAIMode, setStandaloneAIMode] = useState<'compose' | 'autocomplete' | 'polish' | null>(null);
  const standaloneEmailInsertRef = useRef<((content: string, replace?: boolean) => void) | null>(null);
  const standaloneEmailSubjectInsertRef = useRef<((subject: string) => void) | null>(null);
  const [standaloneSnippetsModalOpen, setStandaloneSnippetsModalOpen] = useState(false);

  // Handle AI email generation for standalone email compose
  const handleStandaloneGenerateAIEmail = async (mode: 'compose' | 'autocomplete' | 'polish') => {
    console.log('🤖 Standalone AI Generate called with mode:', mode);
    
    let promptToUse = '';
    
    // Build the prompt based on the mode
    if (mode === 'compose') {
      if (!standaloneAIPrompt.trim()) {
        setStandaloneAIError('Please enter a prompt');
        return;
      }
      promptToUse = standaloneAIPrompt;
    } else if (mode === 'autocomplete') {
      promptToUse = `Continue and complete this email message: "${standaloneEmailBody}"`;
    } else if (mode === 'polish') {
      if (standaloneAIPrompt.trim()) {
        promptToUse = `Polish and enhance this email message with the following instructions: "${standaloneAIPrompt}". Original message: "${standaloneEmailBody}"`;
      } else {
        promptToUse = `Polish and enhance this email message, making it more professional and clear: "${standaloneEmailBody}"`;
      }
    }

    setStandaloneIsGeneratingAI(true);
    setStandaloneAIError(null);

    try {
      const customerName = standaloneEmailCustomer 
        ? `${standaloneEmailCustomer.first_name} ${standaloneEmailCustomer.last_name}`.trim()
        : undefined;

      const { data, error } = await supabase.functions.invoke('ai-compose-email', {
        body: {
          prompt: promptToUse,
          customerName: customerName,
          businessName: selectedBusiness?.business_name || undefined,
          context: standaloneEmailCustomer ? 'existing customer' : 'new contact',
          mode: mode,
          hasSubject: standaloneEmailSubject.trim().length > 0,
          businessId: selectedBusiness?.id
        }
      });

      // Check for insufficient tokens FIRST (even if there's an error)
      if (data?.insufficientTokens) {
        setShowTokenPurchaseModal(true);
        setStandaloneAIError('Insufficient tokens. Please purchase more to continue.');
        return;
      }

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to generate email');
      }

      console.log('🤖 Standalone AI Response:', data);

      // Insert the generated email into the composer
      if (standaloneEmailInsertRef.current) {
        standaloneEmailInsertRef.current(data.email, true);
      }

      // If AI generated a subject, insert it
      if (data.subject && standaloneEmailSubjectInsertRef.current) {
        standaloneEmailSubjectInsertRef.current(data.subject);
      }

      // Reset AI prompt state
      setStandaloneShowAIPrompt(false);
      setStandaloneAIPrompt('');
      setStandaloneAIMode(null);
    } catch (error) {
      console.error('Error generating standalone AI email:', error);
      setStandaloneAIError(error instanceof Error ? error.message : 'Failed to generate email');
    } finally {
      setStandaloneIsGeneratingAI(false);
    }
  };
  
  // Token purchase modal state
  const [showTokenPurchaseModal, setShowTokenPurchaseModal] = useState(false);



  // Check if a conversation has actual unread messages (real-time check)
  const hasUnreadMessages = (card: CommunicationCard): boolean => {
    const unreadMessages = card.messages.filter(msg => 
      msg.read === 'unread' && !locallyReadMessages.has(msg.id)
    );
    
    console.log('🤖 Real-time unread check for', card.id, {
      totalMessages: card.messages.length,
      unreadCount: unreadMessages.length,
      locallyReadCount: locallyReadMessages.size,
      hasUnread: unreadMessages.length > 0
    });
    
    return unreadMessages.length > 0;
  };



  // Mark message as read (for individual message unread badges)
  const markMessageAsRead = async (messageId: string, messageType?: string) => {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      console.log('[UNREAD BADGE] Marking message/call as read:', messageId);

      // Check if this is a callback request, a call, or a message
      const isCallback = messageType === 'callback' || agentCallbacks.some(cb => cb.id === messageId);
      const callMessage = [...callHistory].find(call => call.id === messageId);

      if (coreMessagesRef.current && selectedBusiness?.id) {
        // Core tables: one call marks the row (text, email, missed call, or callback).
        setLocallyReadMessages(prev => new Set([...prev, messageId]));
        if (isCallback) {
          setAgentCallbacks(prev =>
            prev.map(cb => (cb.id === messageId
              ? { ...cb, viewed: true, status: cb.status === 'pending' ? 'completed' : cb.status }
              : cb))
          );
        }
        try {
          await coreMarkItemsRead(selectedBusiness.id, [messageId]);
        } catch (error) {
          setLocallyReadMessages(prev => {
            const next = new Set(prev);
            next.delete(messageId);
            return next;
          });
          throw error;
        }
        window.dispatchEvent(new CustomEvent('unreadMessagesChanged'));
        return;
      }

      if (isCallback) {
        console.log('[UNREAD BADGE] Marking callback as read + completed:', messageId);
        // Marking a callback read means it's been handled — complete it too so
        // it clears from Actions Required and shows "Called back".
        setLocallyReadMessages(prev => new Set([...prev, messageId]));
        setAgentCallbacks(prev =>
          prev.map(cb => (cb.id === messageId
            ? { ...cb, viewed: true, status: cb.status === 'pending' ? 'completed' : cb.status }
            : cb))
        );

        const { error } = await supabase
          .from('agent_callbacks')
          .update({
            viewed: true,
            status: 'completed',
            completed_at: new Date().toISOString(),
            completed_by: user?.id || null,
          })
          .eq('id', messageId)
          .eq('status', 'pending');

        if (error) {
          console.error('Failed to mark callback as read:', error);
          throw error;
        }
      } else if (callMessage) {
        // This is a call - update call_history table
        console.log('[UNREAD BADGE] Marking call as viewed:', messageId);
        
        const { error } = await supabase
          .from('call_history')
          .update({ viewed: true })
          .eq('id', messageId);

        if (error) {
          console.error('Failed to mark call as viewed:', error);
          throw error;
        }

        // Update local call history state
        setCallHistory(prevHistory => 
          prevHistory.map(call => 
            call.id === messageId ? { ...call, viewed: true } : call
          )
        );

        console.log('[UNREAD BADGE] Successfully marked call as viewed:', messageId);
      } else {
        // This is a regular message - update all_messages table
        console.log('[UNREAD BADGE] Marking message as read:', messageId);

        // Immediately add to locally read set for instant UI update
        setLocallyReadMessages(prev => new Set([...prev, messageId]));

        // Update database
        const { error } = await supabase
          .from('all_messages')
          .update({ read: 'read' })
          .eq('id', messageId);

        if (error) {
          console.error('Database update failed, reverting local state:', error);
          // Remove from locally read set if database update fails
          setLocallyReadMessages(prev => {
            const newSet = new Set(prev);
            newSet.delete(messageId);
            return newSet;
          });
          throw error;
        }

        console.log('[UNREAD BADGE] Successfully marked message as read:', messageId);
      }
      
      // Dispatch custom event to notify navigation badge
      window.dispatchEvent(new CustomEvent('unreadMessagesChanged'));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  // Mark multiple messages as read (for thread emails)
  // Since the inbox list is now driven by server summaries (no bulk message
  // load), the card-build no longer recomputes unread on read. Clear a card's
  // unread state directly when a conversation is read, and keep the summary ref
  // (used for merge + cache) in sync so it survives reloads.
  const clearCardUnread = useCallback((cardId: string | null) => {
    if (!cardId) return;
    const patch = (c: CommunicationCard) =>
      c.id === cardId ? { ...c, has_unread: false, unread_count: 0 } : c;
    setCommunicationCards(prev => prev.map(patch));
    summaryCardsRef.current = summaryCardsRef.current.map(patch);
  }, []);

  const clearAllCardsUnread = useCallback(() => {
    const patch = (c: CommunicationCard) =>
      c.has_unread ? { ...c, has_unread: false, unread_count: 0 } : c;
    setCommunicationCards(prev => prev.map(patch));
    summaryCardsRef.current = summaryCardsRef.current.map(patch);
  }, []);

  // Core tables: re-pull the inbox in one call and repaint the list.
  // Core tables: messages loaded for each open thread, keyed by card id. Summary
  // cards start empty; the thread call fills them so everything that reads
  // card.messages (email thread view, reply address, unread checks) keeps working.
  const coreThreadMessagesRef = useRef<Map<string, Message[]>>(new Map());
  const withCoreMessages = useCallback((cards: CommunicationCard[]): CommunicationCard[] => (
    cards.map(c => {
      const msgs = coreThreadMessagesRef.current.get(c.id);
      return msgs && msgs.length ? { ...c, messages: msgs } : c;
    })
  ), []);
  const hydrateCoreCardMessages = useCallback((cardId: string, msgs: Message[], append: boolean) => {
    const existing = append ? (coreThreadMessagesRef.current.get(cardId) || []) : [];
    const byId = new Map<string, Message>();
    for (const m of existing) byId.set(m.id, m);
    for (const m of msgs) byId.set(m.id, m);
    const merged = Array.from(byId.values())
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    coreThreadMessagesRef.current.set(cardId, merged);
    const patch = (c: CommunicationCard) => (c.id === cardId ? { ...c, messages: merged } : c);
    summaryCardsRef.current = summaryCardsRef.current.map(patch);
    setCommunicationCards(prev => {
      const current = prev.find(c => c.id === cardId);
      if (!current) return prev;
      if (current.messages.length === merged.length && current.messages.every((m, i) => m.id === merged[i]?.id && m.read === merged[i]?.read)) return prev;
      return prev.map(patch);
    });
  }, []);

  const reloadCoreInbox = useCallback(async () => {
    if (!selectedBusiness?.id || !coreMessagesRef.current) return;
    try {
      const rows = await fetchCoreInbox(selectedBusiness.id, { tab: 'recent_or_unread', limit: 200 });
      const sCards = withCoreMessages(rows.map(summaryToCard) as CommunicationCard[]);
      summaryCardsRef.current = sCards;
      setCommunicationCards(sCards);
      cacheConversationSummaries(sCards, selectedBusiness.id);
      setCoreThreadVersion(v => v + 1);
    } catch (e) {
      console.warn('[messages] core inbox reload failed:', e);
    }
  }, [selectedBusiness?.id, withCoreMessages]);

  const markMessagesAsRead = async (messageIds: string[]) => {
    if (messageIds.length === 0) return;
    
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      console.log('[MARK THREAD READ] Marking thread emails as read:', messageIds);

      // Immediately add to locally read set for instant UI update
      setLocallyReadMessages(prev => {
        const newSet = new Set(prev);
        messageIds.forEach(id => newSet.add(id));
        return newSet;
      });

      // Update database
      const { error } = coreMessagesRef.current && selectedBusiness?.id
        ? await coreMarkItemsRead(selectedBusiness.id, messageIds).then(() => ({ error: null })).catch((e) => ({ error: e }))
        : await supabase
            .from('all_messages')
            .update({ read: 'read' })
            .in('id', messageIds);

      if (error) {
        console.error('Database update failed, reverting local state:', error);
        // Remove from locally read set if database update fails
        setLocallyReadMessages(prev => {
          const newSet = new Set(prev);
          messageIds.forEach(id => newSet.delete(id));
          return newSet;
        });
        throw error;
      }

      console.log('[MARK THREAD READ] Successfully marked thread emails as read');
      
      // Dispatch custom event to notify navigation badge
      window.dispatchEvent(new CustomEvent('unreadMessagesChanged'));
    } catch (error) {
      console.error('Error marking thread emails as read:', error);
    }
  };

  // Mark messages as unread (e.g. from email preview)
  const markMessagesAsUnread = async (messageIds: string[]) => {
    if (messageIds.length === 0) return;
    try {
      if (!supabase) return;
      setLocallyReadMessages(prev => {
        const next = new Set(prev);
        messageIds.forEach(id => next.delete(id));
        return next;
      });
      if (coreMessagesRef.current && selectedBusiness?.id) {
        await coreMarkItemsUnread(selectedBusiness.id, messageIds);
      } else {
        const { error } = await supabase
          .from('all_messages')
          .update({ read: 'unread' })
          .in('id', messageIds);
        if (error) throw error;
      }
      window.dispatchEvent(new CustomEvent('unreadMessagesChanged'));
    } catch (e) {
      console.error('Error marking messages as unread:', e);
      setLocallyReadMessages(prev => {
        const next = new Set(prev);
        messageIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  // Mark all messages in a conversation as read. Summary cards no longer carry
  // the message array, so we mark read directly in the DB by conversation
  // (linked customer_id + unlinked phone/email matches) and mark missed calls
  // viewed. The card is cleared from the list optimistically.
  //
  // `kinds` narrows what gets cleared. The green Mark Read button passes nothing
  // (everything). A text reply passes PHONE_READ_KINDS so unread email threads
  // from the same customer stay unread.
  const markAllMessagesAsRead = async (card: CommunicationCard, kinds?: CoreReadKind[]) => {
    try {
      if (!supabase || !selectedBusiness?.id) return;
      const businessId = selectedBusiness.id;
      const kindSet = kinds && kinds.length ? new Set<string>(kinds) : null;
      const inKinds = (m: Message) => !kindSet || kindSet.has(m.message_type);

      // The heavy card-rebuild derives has_unread from the loaded messages' read
      // state, so clearCardUnread alone gets overwritten the next time it runs
      // (e.g. when the just-sent reply lands in `messages`). Mark the loaded
      // inbound messages read locally too so the rebuild keeps it read.
      const loadedUnreadIds = (card.messages || [])
        .filter((m) => m.direction !== 'outbound' && m.read === 'unread' && inKinds(m))
        .map((m) => m.id);
      if (loadedUnreadIds.length > 0) {
        setLocallyReadMessages((prev) => {
          const next = new Set(prev);
          loadedUnreadIds.forEach((id) => next.add(id));
          return next;
        });
      }

      // Optimistically update the unread pipeline. With a kind filter the card
      // may still hold unread items of other kinds (an email thread after a text
      // reply), so only clear it when nothing we can see is left.
      const remaining = kindSet
        ? (card.messages || []).filter((m) =>
            m.direction !== 'outbound' && m.read === 'unread' && !inKinds(m) && !locallyReadMessages.has(m.id)
          ).length
        : 0;
      if (remaining === 0) {
        clearCardUnread(card.id);
        readInSessionRef.current.add(card.id);
      } else {
        const patch = (c: CommunicationCard) => (c.id === card.id ? { ...c, has_unread: true, unread_count: remaining } : c);
        setCommunicationCards(prev => prev.map(patch));
        summaryCardsRef.current = summaryCardsRef.current.map(patch);
      }

      // Core tables: one call clears the conversation (or the requested kinds).
      if (coreMessagesRef.current) {
        await coreMarkConversationRead(businessId, conversationKeyForCard(card), kinds);
        if (!kindSet || kindSet.has('callback')) {
          setAgentCallbacks(prev => prev.map(cb => (
            cb.status === 'pending' && (cb.customer_id === card.customer_id) ? { ...cb, viewed: true, status: 'completed' } : cb
          )));
        }
        window.dispatchEvent(new CustomEvent('unreadMessagesChanged'));
        return;
      }

      // Legacy tables. all_messages holds texts and emails; calls and callbacks
      // live in their own tables, so the kind filter maps onto which updates run.
      const wantSms = !kindSet || kindSet.has('sms');
      const wantEmail = !kindSet || kindSet.has('email');
      const wantCall = !kindSet || kindSet.has('call');
      const wantCallback = !kindSet || kindSet.has('callback');
      const msgTypes = [wantSms ? 'sms' : null, wantEmail ? 'email' : null].filter((t): t is string => !!t);

      const phoneVariants = (phone?: string | null): string[] => {
        if (!phone) return [];
        const digits = phone.replace(/\D/g, '');
        const set = new Set<string>([phone]);
        if (digits.length === 10) { set.add(`+1${digits}`); set.add(`1${digits}`); set.add(digits); }
        else if (digits.length === 11 && digits.startsWith('1')) { set.add(`+${digits}`); set.add(digits); set.add(digits.slice(1)); }
        else if (digits) set.add(digits);
        return Array.from(set);
      };

      const updates: Promise<any>[] = [];

      if (card.type === 'customer' && card.customer_id) {
        // Linked messages
        if (msgTypes.length) {
          updates.push(
            supabase.from('all_messages').update({ read: 'read' })
              .eq('business_id', businessId).eq('direction', 'inbound').eq('read', 'unread')
              .eq('customer_id', card.customer_id).in('message_type', msgTypes)
          );
        }
        // Unlinked messages matched by the customer's phone / email
        const customer = customers.find(c => c.id === card.customer_id);
        const phones = phoneVariants(customer?.phone);
        if (phones.length && wantSms) {
          const orParts = phones.flatMap(p => [`from_number.eq.${p}`, `to_number.eq.${p}`]).join(',');
          updates.push(
            supabase.from('all_messages').update({ read: 'read' })
              .eq('business_id', businessId).eq('direction', 'inbound').eq('read', 'unread')
              .is('customer_id', null).eq('message_type', 'sms').or(orParts)
          );
        }
        if (customer?.email && wantEmail) {
          const email = customer.email.toLowerCase().trim();
          updates.push(
            supabase.from('all_messages').update({ read: 'read' })
              .eq('business_id', businessId).eq('direction', 'inbound').eq('read', 'unread')
              .is('customer_id', null).eq('message_type', 'email')
              .or(`from_number.ilike.%${email}%,to_number.ilike.%${email}%`)
          );
        }
        // Missed calls
        if (wantCall) {
          updates.push(
            supabase.from('call_history').update({ viewed: true })
              .eq('business_id', businessId).eq('direction', 'inbound').eq('status', 'missed').eq('viewed', false)
              .eq('customer_id', card.customer_id)
          );
          if (phones.length) {
            const orParts = phones.flatMap(p => [`from_number.eq.${p}`, `to_number.eq.${p}`]).join(',');
            updates.push(
              supabase.from('call_history').update({ viewed: true })
                .eq('business_id', businessId).eq('direction', 'inbound').eq('status', 'missed').eq('viewed', false)
                .is('customer_id', null).or(orParts)
            );
          }
        }
        // AI receptionist callback requests — read means handled, so pending
        // callbacks are completed (clears Actions Required + "Needs call back").
        const cbDone = {
          viewed: true,
          status: 'completed',
          completed_at: new Date().toISOString(),
          completed_by: user?.id || null,
        };
        if (wantCallback) {
          updates.push(
            supabase.from('agent_callbacks').update(cbDone)
              .eq('business_id', businessId).eq('status', 'pending')
              .eq('customer_id', card.customer_id)
          );
          if (phones.length) {
            const cbOrParts = phones.map(p => `from_number.eq.${p}`).join(',');
            updates.push(
              supabase.from('agent_callbacks').update(cbDone)
                .eq('business_id', businessId).eq('status', 'pending')
                .is('customer_id', null).or(cbOrParts)
            );
          }
        }
      } else if (card.type === 'phone') {
        const orParts = `from_number.eq.${card.contact_info},to_number.eq.${card.contact_info}`;
        if (wantSms) {
          updates.push(
            supabase.from('all_messages').update({ read: 'read' })
              .eq('business_id', businessId).eq('direction', 'inbound').eq('read', 'unread')
              .eq('message_type', 'sms').or(orParts)
          );
        }
        if (wantCall) {
          updates.push(
            supabase.from('call_history').update({ viewed: true })
              .eq('business_id', businessId).eq('direction', 'inbound').eq('status', 'missed').eq('viewed', false)
              .or(orParts)
          );
        }
        const cbPhones = phoneVariants(card.contact_info);
        if (cbPhones.length && wantCallback) {
          updates.push(
            supabase.from('agent_callbacks').update({
              viewed: true,
              status: 'completed',
              completed_at: new Date().toISOString(),
              completed_by: user?.id || null,
            })
              .eq('business_id', businessId).eq('status', 'pending')
              .or(cbPhones.map(p => `from_number.eq.${p}`).join(','))
          );
        }
      } else if (card.type === 'email' && wantEmail) {
        const email = (card.contact_info || '').toLowerCase().trim();
        updates.push(
          supabase.from('all_messages').update({ read: 'read' })
            .eq('business_id', businessId).eq('direction', 'inbound').eq('read', 'unread')
            .eq('message_type', 'email')
            .or(`from_number.ilike.%${email}%,to_number.ilike.%${email}%`)
        );
      }

      const results = await Promise.all(updates);
      const firstErr = results.find(r => r?.error)?.error;
      if (firstErr) console.error('[mark read] update error:', firstErr);

      window.dispatchEvent(new CustomEvent('unreadMessagesChanged'));
    } catch (error) {
      console.error('Error marking all messages as read:', error);
    }
  };

  // Mark ALL unread messages as read (across all conversations) AND mark all missed calls as viewed
  const markAllUnreadAsRead = async () => {
    try {
      if (!supabase || !selectedBusiness?.id) {
        throw new Error('Supabase client or business not initialized');
      }

      // Core tables: one call marks everything read (suppliers and muted senders stay).
      if (coreMessagesRef.current) {
        clearAllCardsUnread();
        await coreMarkAllRead(selectedBusiness.id, false);
        setAgentCallbacks(prev => prev.map(cb => (
          cb.status === 'pending' ? { ...cb, viewed: true, status: 'completed' } : cb
        )));
        await reloadCoreInbox();
        window.dispatchEvent(new CustomEvent('unreadMessagesChanged'));
        return;
      }

      // Fetch ALL unread inbound messages DB-wide — not just the loaded 30-day/500 window.
      // Previously this only marked messages already loaded into the pipeline, so older
      // unread that piled up beyond the window were never cleared and kept the badge count
      // out of sync. We now query the whole table so "Mark All Read" truly clears them.
      const { data: unreadRows, error: fetchError } = await supabase
        .from('all_messages')
        .select('id, message_type, from_number, customer_id')
        .eq('business_id', selectedBusiness.id)
        .eq('direction', 'inbound')
        .eq('read', 'unread');

      if (fetchError) {
        console.error('[MARK ALL READ] Failed to fetch unread messages:', fetchError);
        throw fetchError;
      }

      // Exclude suppliers (handled separately in the Unread Suppliers tab) and muted/spam senders.
      const allUnreadMessageIds = (unreadRows || [])
        .filter(row => {
          if (row.customer_id && supplierCustomerIds.has(row.customer_id)) return false;
          if (row.message_type === 'email' && isEmailMuted(row.from_number || '')) return false;
          return true;
        })
        .map(row => row.id);

      console.log(`[MARK ALL READ] Marking ${allUnreadMessageIds.length} unread message(s) and missed calls as read (DB-wide)`);

      // Clear all list dots immediately (summary-driven cards don't auto-recompute).
      clearAllCardsUnread();

      // Immediately add all to locally read set for instant UI update
      if (allUnreadMessageIds.length > 0) {
        setLocallyReadMessages(prev => new Set([...prev, ...allUnreadMessageIds]));
      }

      // Update database in chunks to avoid oversized requests when there are many unread.
      const CHUNK_SIZE = 200;
      for (let i = 0; i < allUnreadMessageIds.length; i += CHUNK_SIZE) {
        const chunk = allUnreadMessageIds.slice(i, i + CHUNK_SIZE);
        const { error: messagesError } = await supabase
          .from('all_messages')
          .update({ read: 'read' })
          .in('id', chunk);

        if (messagesError) {
          console.error('[MARK ALL READ] Chunk update failed, reverting local state:', messagesError);
          // Remove all from locally read set if database update fails
          setLocallyReadMessages(prev => {
            const newSet = new Set(prev);
            allUnreadMessageIds.forEach(id => newSet.delete(id));
            return newSet;
          });
          throw messagesError;
        }
      }

      // Also mark all missed calls as viewed (DB-wide)
      const { error: callsError } = await supabase
        .from('call_history')
        .update({ viewed: true })
        .eq('business_id', selectedBusiness.id)
        .eq('direction', 'inbound')
        .eq('status', 'missed')
        .eq('viewed', false);

      if (callsError) {
        console.error('Calls update failed:', callsError);
        // Don't throw - messages were already marked, just log the error
      }

      // And all AI receptionist callback requests (DB-wide). Read = handled,
      // so pending ones are completed too.
      const { error: callbacksReadError } = await supabase
        .from('agent_callbacks')
        .update({
          viewed: true,
          status: 'completed',
          completed_at: new Date().toISOString(),
          completed_by: user?.id || null,
        })
        .eq('business_id', selectedBusiness.id)
        .eq('status', 'pending');

      if (callbacksReadError) {
        console.error('Callbacks update failed:', callbacksReadError);
      } else {
        setAgentCallbacks(prev => prev.map(cb => (
          cb.status === 'pending' ? { ...cb, viewed: true, status: 'completed' } : cb
        )));
      }

      console.log('[MARK ALL READ] Successfully marked all messages and calls as read');
      
      // Refresh communication cards to reflect changes
      await fetchCommunicationCards();
      
      // Dispatch custom event to notify navigation badge
      window.dispatchEvent(new CustomEvent('unreadMessagesChanged'));
    } catch (error) {
      console.error('Error marking all messages as read:', error);
    }
  };

  // Handle view customer details button click (for existing customers)
  const handleEditCustomer = (customer: Customer) => {
    setSelectedCustomerForDetails(customer);
    setCustomerDetailsModalOpen(true);
  };

  // Handle create customer button click for phone/email
  const handleCreateCustomer = (card: CommunicationCard) => {
    const initialData: { phone?: string; email?: string } = {};
    
    if (card.type === 'phone') {
      initialData.phone = card.contact_info;
    } else if (card.type === 'email') {
      initialData.email = card.contact_info;
    }
    
    setCreateCustomerInitialData(initialData);
    setCreateCustomerModalOpen(true);
  };

  // Debounced customer lookup
  const lookupTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Lookup customer by phone or email
  const lookupCustomer = async (contact: string, type: 'sms' | 'email') => {
    if (!selectedBusiness?.id || !contact || contact.length < 3) {
      setMatchedCustomer(null);
      return;
    }

    setIsLookingUpCustomer(true);
    try {
      const normalizedContact = contact.trim();
      let customer: Customer | null = null;

      if (type === 'sms') {
        // Normalize phone number (remove non-digits)
        const normalizedPhone = normalizedContact.replace(/\D/g, '');
        if (normalizedPhone.length >= 10) {
          // Try different phone formats
          const phoneFormats = [
            normalizedPhone,
            `+1${normalizedPhone}`,
            `+${normalizedPhone}`,
            `1${normalizedPhone}`
          ];

          for (const phoneFormat of phoneFormats) {
            customer = await findExistingCustomer(selectedBusiness.id, null, phoneFormat);
            if (customer) break;
          }

          // Also check against all customers with normalized comparison
          if (!customer) {
            const allCustomers = customers.filter(c => c.phone);
            customer = allCustomers.find(c => {
              const customerPhone = c.phone?.replace(/\D/g, '') || '';
              return phoneFormats.some(format => {
                const formatNormalized = format.replace(/\D/g, '');
                return customerPhone === formatNormalized || 
                       customerPhone === formatNormalized.substring(1) ||
                       `1${customerPhone}` === formatNormalized;
              });
            }) || null;
          }
        }
      } else {
        // Email lookup (case-insensitive)
        customer = await findExistingCustomer(selectedBusiness.id, normalizedContact.toLowerCase(), null);
      }

      setMatchedCustomer(customer);
    } catch (error) {
      console.error('Error looking up customer:', error);
      setMatchedCustomer(null);
    } finally {
      setIsLookingUpCustomer(false);
    }
  };

  // Search customers as user types (for email compose)
  const searchCustomers = (query: string, type: 'sms' | 'email') => {
    if (!selectedBusiness?.id || !query || query.trim().length < 2) {
      setMatchingCustomers([]);
      setShowCustomerDropdown(false);
      setMatchedCustomer(null);
      return;
    }

    const searchLower = query.toLowerCase().trim();
    const matches: Customer[] = [];

    customers.forEach(customer => {
      let isMatch = false;

      if (type === 'email') {
        // Search by email, name, or phone
        const emailMatch = customer.email?.toLowerCase().includes(searchLower);
        const nameMatch = `${customer.first_name} ${customer.last_name}`.toLowerCase().includes(searchLower);
        const phoneMatch = customer.phone?.replace(/\D/g, '').includes(searchLower.replace(/\D/g, ''));
        isMatch = emailMatch || nameMatch || phoneMatch;
      } else {
        // For SMS, search by phone or name
        const phoneMatch = customer.phone?.replace(/\D/g, '').includes(searchLower.replace(/\D/g, ''));
        const nameMatch = `${customer.first_name} ${customer.last_name}`.toLowerCase().includes(searchLower);
        isMatch = phoneMatch || nameMatch;
      }

      if (isMatch) {
        matches.push(customer);
      }
    });

    // Sort matches: exact email/phone matches first, then name matches
    matches.sort((a, b) => {
      const aEmail = a.email?.toLowerCase() || '';
      const bEmail = b.email?.toLowerCase() || '';
      const aPhone = a.phone?.replace(/\D/g, '') || '';
      const bPhone = b.phone?.replace(/\D/g, '') || '';
      const searchNormalized = searchLower.replace(/\D/g, '');

      // Exact email match
      if (type === 'email') {
        if (aEmail === searchLower && bEmail !== searchLower) return -1;
        if (bEmail === searchLower && aEmail !== searchLower) return 1;
        if (aEmail.startsWith(searchLower) && !bEmail.startsWith(searchLower)) return -1;
        if (bEmail.startsWith(searchLower) && !aEmail.startsWith(searchLower)) return 1;
      }

      // Exact phone match
      if (aPhone === searchNormalized && bPhone !== searchNormalized) return -1;
      if (bPhone === searchNormalized && aPhone !== searchNormalized) return 1;

      // Name match
      const aName = `${a.first_name} ${a.last_name}`.toLowerCase();
      const bName = `${b.first_name} ${b.last_name}`.toLowerCase();
      if (aName.startsWith(searchLower) && !bName.startsWith(searchLower)) return -1;
      if (bName.startsWith(searchLower) && !aName.startsWith(searchLower)) return 1;

      return 0;
    });

    setMatchingCustomers(matches.slice(0, 5)); // Limit to 5 results
    setShowCustomerDropdown(matches.length > 0);
    
    // Also check for exact match to set matchedCustomer
    if (type === 'email') {
      const exactMatch = matches.find(c => c.email?.toLowerCase() === searchLower);
      setMatchedCustomer(exactMatch || null);
    } else {
      const normalizedQuery = query.replace(/\D/g, '');
      const exactMatch = matches.find(c => {
        const customerPhone = c.phone?.replace(/\D/g, '') || '';
        return customerPhone === normalizedQuery || 
               customerPhone === normalizedQuery.substring(1) ||
               `1${customerPhone}` === normalizedQuery;
      });
      setMatchedCustomer(exactMatch || null);
    }
  };

  // Handle compose contact input change with real-time search
  const handleComposeContactChange = (value: string, type: 'sms' | 'email') => {
    setComposeContact(value);
    
    // Clear existing timeout
    if (lookupTimeoutRef.current) {
      clearTimeout(lookupTimeoutRef.current);
    }
    
    // Use real-time search for both email and SMS
    searchCustomers(value, type);
  };

  // Handle customer selection from dropdown
  const handleSelectCustomer = (customer: Customer, type: 'sms' | 'email') => {
    if (type === 'email' && customer.email) {
      setComposeContact(customer.email);
      setMatchedCustomer(customer);
    } else if (type === 'sms' && customer.phone) {
      setComposeContact(customer.phone);
      setMatchedCustomer(customer);
    }
    setMatchingCustomers([]);
    setShowCustomerDropdown(false);
  };

  // Handle customer modal success.
  // `customer` is optional: create/edit pass the saved customer, while in-place
  // updates (e.g. tag changes) call this with no argument just to refresh data.
  const handleCustomerSuccess = (customer?: Customer) => {
    // Refresh data to show updated customer info
    if (selectedBusiness?.id) {
      const fetchData = async () => {
        try {
          if (!supabase) {
            throw new Error('Supabase client not initialized');
          }

          // Link unlinked call_history and all_messages records to the new/updated customer.
          // When a customer is created from a phone number, older records still have
          // customer_id = NULL in the DB. Update them so future queries find them.
          if (customer?.phone) {
            const phone = customer.phone;
            const digits = phone.replace(/\D/g, '');
            const phoneCandidates = new Set([phone, digits]);
            if (digits.length === 10) {
              phoneCandidates.add(`+1${digits}`);
              phoneCandidates.add(`1${digits}`);
            } else if (digits.length === 11 && digits.startsWith('1')) {
              phoneCandidates.add(`+${digits}`);
              phoneCandidates.add(digits.slice(1));
            }
            const phoneOr = Array.from(phoneCandidates)
              .flatMap(p => [`from_number.eq.${p}`, `to_number.eq.${p}`])
              .join(',');

            const [callLink, msgLink] = await Promise.all([
              supabase
                .from('call_history')
                .update({ customer_id: customer.id })
                .eq('business_id', selectedBusiness.id)
                .is('customer_id', null)
                .or(phoneOr),
              supabase
                .from('all_messages')
                .update({ customer_id: customer.id })
                .eq('business_id', selectedBusiness.id)
                .is('customer_id', null)
                .or(phoneOr),
            ]);
            if (callLink.error) console.error('Error linking calls to customer:', callLink.error);
            if (msgLink.error) console.error('Error linking messages to customer:', msgLink.error);
          }

          if (customer?.email) {
            const email = customer.email.toLowerCase().trim();
            const { error: emailLinkError } = await supabase
              .from('all_messages')
              .update({ customer_id: customer.id })
              .eq('business_id', selectedBusiness.id)
              .is('customer_id', null)
              .or(`from_number.ilike.%${email}%,to_number.ilike.%${email}%`);
            if (emailLinkError) console.error('Error linking emails to customer:', emailLinkError);
          }

          // Fetch customers
          const { data: customersData, error: customersError } = await supabase
            .from('customers')
            .select('*')
            .eq('business_id', selectedBusiness.id);

          if (customersError) throw customersError;
          setCustomers(customersData || []);

          // Refresh call history so the card builder picks up updated customer_id values
          const { data: callData } = await supabase
            .from('call_history')
            .select('*')
            .eq('business_id', selectedBusiness.id)
            .order('call_time', { ascending: false });
          if (callData) setCallHistory(callData);

          // Refresh messages so card builder picks up updated customer_id values
          await refreshMessages();
        } catch (error) {
          console.error('Error fetching customers:', error);
        }
      };
      fetchData();
    }

    // Only close modals on a full create/edit success (a customer is provided).
    // In-place updates like tag changes keep the details modal open.
    if (customer) {
      setEditCustomerModalOpen(false);
      setCreateCustomerModalOpen(false);
      setCustomerDetailsModalOpen(false);
      setSelectedCustomerForEdit(null);
      setSelectedCustomerForDetails(null);
      setCreateCustomerInitialData({});
    }
  };

  // Handle milestone click to open booking info or proposal details
  const handleMilestoneClick = async (milestone: BookingMilestone) => {
    try {
      // For milestones, the booking_id is stored in the booking_id field
      // But since we're using it for both bookings and proposals, we need to check the metadata
      const entityId = milestone.booking_id;
      
      // Check if this is a proposal milestone
      if (milestone.milestone.metadata?.entity_type === 'proposal') {
        console.log('Loading proposal for milestone:', entityId);
        const proposal = await getProposal(entityId);
        if (proposal) {
          setSelectedProposal(proposal);
          setProposalDetailsModalOpen(true);
        } else {
          console.error('Proposal not found:', entityId);
        }
        return;
      }
      
      // Handle booking milestones
      if (entityId) {
        console.log('Loading booking for milestone:', entityId);
        const booking = await getBooking(entityId);
        if (booking) {
          setSelectedBooking(booking);
          setBookingInfoModalOpen(true);
        } else {
          console.error('Booking not found:', entityId);
        }
      }
    } catch (error) {
      console.error('Error loading entity from milestone:', error);
    }
  };

  // Fetch milestones for a specific customer (lazy loading)
  const fetchCustomerMilestones = async (customerId: string, setLoading?: (loading: boolean) => void) => {
    try {
      console.log('Fetching milestones for customer:', customerId);
      if (setLoading) setLoading(true);
      
      const milestones = await getCustomerMilestones(customerId);
      setCustomerMilestones(prev => ({
        ...prev,
        [customerId]: milestones
      }));
    } catch (error) {
      console.error(`Error fetching milestones for customer ${customerId}:`, error);
    } finally {
      if (setLoading) setLoading(false);
    }
  };

  // Handle booking info modal update
  const handleBookingUpdate = () => {
    // Refresh milestones for the selected customer only
    if (selectedCard?.customer_id) {
      fetchCustomerMilestones(selectedCard.customer_id);
    }
  };

  // Handle proposal details modal update
  const handleProposalUpdate = () => {
    // Refresh milestones for the selected customer only
    if (selectedCard?.customer_id) {
      fetchCustomerMilestones(selectedCard.customer_id);
    }
  };

  // Handle back to CRM navigation
  const handleBackToCRM = () => {
    navigate('/crm');
  };

  // Handle back to booking navigation
  const handleBackToBooking = async () => {
    if (sourcePage === 'calendar' && bookingId) {
      // For calendar, open the modal immediately using a portal
      try {
        // Import the required modules
        const [{ getBooking }, { createPortal }, BookingInfoModalModule] = await Promise.all([
          import('../lib/bookings'),
          import('react-dom'),
          import('../components/BookingInfoModal')
        ]);
        
        const BookingInfoModal = BookingInfoModalModule.default;
        
        // Fetch booking data
        const booking = await getBooking(bookingId);
        
        // Create a modal container
        const modalContainer = document.createElement('div');
        modalContainer.id = 'instant-booking-modal';
        document.body.appendChild(modalContainer);
        
        // Render the modal immediately
        const { createRoot } = await import('react-dom/client');
        const root = createRoot(modalContainer);
        
        root.render(
          React.createElement(BookingInfoModal, {
            isOpen: true,
            onClose: () => {
              root.unmount();
              document.body.removeChild(modalContainer);
              // Navigate to calendar after closing
              navigate('/calendarV2');
            },
            booking: booking,
            onUpdate: () => {},
            readOnly: false
          })
        );
        
        // Navigate to calendar in background
        setTimeout(() => {
          navigate('/calendarV2', { 
            state: { selectedBookingId: bookingId, skipModalOpen: true } 
          });
        }, 100);
        
      } catch (error) {
        console.error('Error opening instant modal:', error);
        // Fallback to normal navigation
        navigate('/calendarV2', { 
          state: { selectedBookingId: bookingId } 
        });
      }
    } else if (sourcePage === 'crm') {
      // Navigate back to CRM
      navigate('/crm');
    } else {
      // Default: Navigate back to bookings page
      navigate('/bookings');
    }
  };

  // Reset pipeline pagination when switching tabs
  useEffect(() => {
    setPipelineOffset(CARDS_PAGE_SIZE);
  }, [activeTab]);
  
  // Handle window resize for mobile detection (width only — keyboard height
  // changes must not re-render this 7k-line page).
  useEffect(() => {
    const handleResize = () => {
      const next = window.innerWidth < 768;
      setIsMobile((prev) => (prev === next ? prev : next));
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Hard-lock document scroll on native/PWA. CSS :has() alone loses to
  // body.pwa-mode { overflow: visible !important } + min-h-screen growth.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const appContent = document.querySelector('[data-app-content]');
    const scrollY = window.scrollY || window.pageYOffset || 0;

    html.classList.add('messages-scroll-lock');
    body.classList.add('messages-scroll-lock');
    root?.classList.add('messages-scroll-lock');
    appContent?.classList.add('messages-scroll-lock');

    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      bodyHeight: body.style.height,
    };

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    body.style.height = '100%';
    window.scrollTo(0, 0);

    return () => {
      html.classList.remove('messages-scroll-lock');
      body.classList.remove('messages-scroll-lock');
      root?.classList.remove('messages-scroll-lock');
      appContent?.classList.remove('messages-scroll-lock');
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.width = prev.bodyWidth;
      body.style.height = prev.bodyHeight;
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Close the mobile conversation modal when a call button dispatches closeAllModals
  useEffect(() => {
    const handleCloseAll = () => {
      setMobileModalOpen(false);
      setSelectedCard(null);
    };
    window.addEventListener('closeAllModals', handleCloseAll);
    return () => window.removeEventListener('closeAllModals', handleCloseAll);
  }, []);

  // Close actions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target as Node)) {
        setActionsDropdownOpen(false);
      }
    };

    if (actionsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [actionsDropdownOpen]);


  // Load more conversations function
  const loadMoreCards = () => {
    if (loadingMoreCards) return;
    
    setLoadingMoreCards(true);
    
    // Simulate brief loading for better UX
    setTimeout(() => {
      setPipelineOffset(prev => prev + CARDS_PAGE_SIZE);
      setLoadingMoreCards(false);
    }, 300);
  };

  // Function to refresh messages data
  const refreshMessages = async () => {
    if (!selectedBusiness?.id || !supabase) return;

    // Core tables: the inbox call is the refresh.
    if (coreMessagesRef.current) {
      await reloadCoreInbox();
      return;
    }

    try {
      // Load recent messages (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - INITIAL_MESSAGE_DAYS);
      const thirtyDaysAgoISO = thirtyDaysAgo.toISOString();
      
      const messagesResult = await supabase
        .from('all_messages')
        .select('id, customer_id, message_type, direction, from_number, to_number, body, subject, read, created_at, media_urls, twilio_message_sid')
        .eq('business_id', selectedBusiness.id)
        .gte('created_at', thirtyDaysAgoISO)
        .order('created_at', { ascending: false })
        .limit(MESSAGES_PAGE_SIZE);

      if (!messagesResult.error) {
        const loadedMessages = messagesResult.data || [];
        setMessages(loadedMessages);
        
        if (loadedMessages.length === MESSAGES_PAGE_SIZE) {
          setHasMoreMessages(true);
          const oldestMessage = loadedMessages[loadedMessages.length - 1];
          if (oldestMessage) {
            setMessagesLoadedUntil(new Date(oldestMessage.created_at));
          }
        } else {
          setHasMoreMessages(false);
          setMessagesLoadedUntil(null);
        }
      }
    } catch (error) {
      console.error('Error refreshing messages:', error);
    }
  };

  // Cache-first loading removed: the parallelized network fetch is fast enough,
  // and pre-populating with stale cached data caused visible flashes of old
  // unread messages before the fresh data arrived. The auto-sync effect below
  // still writes to cache for potential future use (e.g., offline support).

  // Optimized loading: integration check + data fetch run in parallel
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;

    let cancelled = false;

    const fetchData = async () => {
      try {
        // ── 0) Instant paint from cache (native) — show the last-known inbox
        //    list immediately, before any network round-trip. Reconciled by the
        //    live summary RPC below. This is what makes re-opening feel instant. ──
        try {
          const [cachedSummaries, cachedIntegration] = await Promise.all([
            getCachedConversationSummaries(selectedBusiness.id),
            getCachedIntegrationStatus(selectedBusiness.id),
          ]);
          if (!cancelled) {
            if (cachedIntegration) {
              setCanUseMessaging(cachedIntegration.hasTwilio || cachedIntegration.hasGmail);
              setMessagingIntegrationsStatus('ready');
            }
            if (cachedSummaries?.length) {
              summaryCardsRef.current = cachedSummaries as CommunicationCard[];
              setCommunicationCards(cachedSummaries as CommunicationCard[]);
              setCardsLoading(false);
            }
          }
        } catch { /* cache miss — fall through to network */ }

        // ── Kick off the integration check IN PARALLEL with the conversation
        //    summaries. The list no longer waits on the Twilio/Gmail check —
        //    that only gates the "Connect messaging" screen + the heavy fetch. ──
        const integrationPromise = Promise.all([
          supabase
            .from('businesses')
            .select('twilio_account_sid, twilio_auth_token, VITE_TWILIO_PHONE_NUMBER, twilio_enabled')
            .eq('id', selectedBusiness.id)
            .single(),
          supabase
            .from('gmail_connections')
            .select('id')
            .eq('business_id', selectedBusiness.id)
            .eq('is_active', true)
            .limit(1),
        ]);

        // ── Live conversation summaries (small payload → fast first paint). The
        // message-organization setting is fetched alongside and applied BEFORE
        // painting so the first render is already ordered (sms_first/email_first).
        try {
          const useCore = await isCoreMessagesEnabled(selectedBusiness.id);
          if (cancelled) return;
          coreMessagesRef.current = useCore;
          setCoreMessages(useCore);

          const [summaries, unreadSummaries, orgSettingRes] = await Promise.all([
            useCore
              ? fetchCoreInbox(selectedBusiness.id, { tab: 'recent_or_unread', limit: 200 })
              : getMessageConversations(selectedBusiness.id, { limit: 200, offset: 0 }),
            useCore
              ? Promise.resolve([] as ConversationSummary[])
              : getUnreadMessageConversations(selectedBusiness.id, { limit: 500, offset: 0 }),
            supabase.rpc('get_app_setting', {
              p_business_id: selectedBusiness.id,
              p_app_slug: 'messages',
              p_setting_key: 'message_organization',
              p_default_value: '{"value":"chronological"}',
            }),
          ]);
          if (!cancelled) {
            if (!orgSettingRes.error && orgSettingRes.data) {
              const d: any = orgSettingRes.data;
              const val = (typeof d === 'object' && d !== null && 'value' in d)
                ? String(d.value ?? 'chronological')
                : 'chronological';
              if (['chronological', 'sms_first', 'email_first'].includes(val)) {
                setMessageOrganization(val as 'chronological' | 'sms_first' | 'email_first');
              }
            }
            const merged = mergeRecentAndUnreadConversations(summaries, unreadSummaries);
            if (merged.length) {
              const sCards = withCoreMessages(merged.map(summaryToCard) as CommunicationCard[]);
              summaryCardsRef.current = sCards;
              setCommunicationCards(sCards);
              setCardsLoading(false);
              // Cache the inbox list for the next instant open (native, fire-and-forget).
              cacheConversationSummaries(sCards, selectedBusiness.id);
            }
          }
        } catch (e) {
          console.warn('[messages] summary first-paint failed (non-fatal):', e);
        }

        // ── Resolve integration status (already running in parallel above). ──
        const [businessRes, gmailRes] = await integrationPromise;
        if (cancelled) return;
        const b = businessRes.data;
        const twilioOk = !!(b?.twilio_account_sid && b?.twilio_auth_token && b?.VITE_TWILIO_PHONE_NUMBER && b?.twilio_enabled);
        const gmailOk = (gmailRes.data?.length ?? 0) > 0;
        const hasMessaging = twilioOk || gmailOk;
        setCanUseMessaging(hasMessaging);
        setMessagingIntegrationsStatus('ready');
        cacheIntegrationStatus({ hasTwilio: twilioOk, hasGmail: gmailOk }, selectedBusiness.id);

        if (!hasMessaging) {
          setCardsLoading(false);
          return;
        }

        // ── Background enrichment ONLY (never blocks the list, which is fully
        //    driven by the fast summary RPC above). We deliberately DO NOT bulk-load
        //    messages or call history here: that was a multi-MB payload (500 email
        //    bodies) + a main-thread O(messages×customers) rebuild that caused the
        //    ~30s cellular load. Per-conversation messages/calls load lazily when a
        //    conversation is opened. We still load customers (compose + lead
        //    detection + phone matching) and open-opportunity badges. ──
        const [customersResult, openOpportunityCustomerIds, callbacksResult] = await Promise.all([
          supabase
            .from('customers')
            .select('id, first_name, last_name, phone, email, is_supplier')
            .eq('business_id', selectedBusiness.id),
          getCustomerIdsWithOpenOpportunities(selectedBusiness.id),
          supabase
            .from('agent_callbacks')
            .select('*')
            .eq('business_id', selectedBusiness.id)
            .order('created_at', { ascending: false })
            .limit(300),
        ]);

        if (cancelled) return;

        if (!customersResult.error) {
          setCustomers(customersResult.data || []);
          setCustomersWithOpenOpportunities(openOpportunityCustomerIds);
        }
        if (!callbacksResult.error) {
          setAgentCallbacks(callbacksResult.data || []);
        }

        setInitialDataLoaded(true);
        setCardsLoading(false);
        setPipelineOffset(CARDS_PAGE_SIZE);
        setLocallyReadMessages(new Set());
        
      } catch (error) {
        console.error('Error fetching messages:', error);
        setCanUseMessaging(true);
        setMessagingIntegrationsStatus('ready');
        setCardsLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [selectedBusiness?.id]);

  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;

    // Prime the business-wide open-opportunities cache immediately so the
    // Lead banner renders instantly when a conversation opens.
    void primeOpenOpportunitiesCache(selectedBusiness.id);

    const refreshLeadBadges = async () => {
      const customerIds = await getCustomerIdsWithOpenOpportunities(selectedBusiness.id);
      setCustomersWithOpenOpportunities(customerIds);
      // Keep the banner cache fresh on every opportunity change too.
      void primeOpenOpportunitiesCache(selectedBusiness.id);
    };

    const subscription = supabase
      .channel(`messages_open_opportunities_${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pipeline_opportunities',
          filter: `business_id=eq.${selectedBusiness.id}`,
        },
        () => {
          void refreshLeadBadges();
        },
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [selectedBusiness?.id]);

  // ── Auto-sync state → persistent cache ──
  // Keeps the cache in sync with the current state at all times so the next
  // app open shows exactly what the user last saw (no stale unread flash).
  // locallyReadMessages is baked into the cached messages so the read status
  // is always correct even before the Realtime UPDATE event fires.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const locallyReadRef = useRef(locallyReadMessages);
  locallyReadRef.current = locallyReadMessages;
  const callHistoryRef = useRef(callHistory);
  callHistoryRef.current = callHistory;
  const customersRef = useRef(customers);
  customersRef.current = customers;

  useEffect(() => {
    if (!selectedBusiness?.id) return;
    if (messages.length === 0 && customers.length === 0) return;
    const bid = selectedBusiness.id;

    const writeCache = () => {
      const localRead = locallyReadRef.current;
      const msgs = messagesRef.current;
      if (msgs.length > 0) {
        const withReads = localRead.size > 0
          ? msgs.map(m => localRead.has(m.id) ? { ...m, read: 'read' } : m)
          : msgs;
        cacheMessages(withReads, bid);
      }
      if (customersRef.current.length > 0) cacheCustomers(customersRef.current, bid);
      if (callHistoryRef.current.length > 0) cacheCallHistory(callHistoryRef.current, bid);
    };

    const timer = setTimeout(writeCache, 800);
    return () => clearTimeout(timer);
  }, [messages, customers, callHistory, locallyReadMessages, selectedBusiness?.id]);

  // Load muted email senders
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;

    const loadMutedSenders = async () => {
      try {
        const { data, error } = await supabase
          .from('muted_email_senders')
          .select('email_address')
          .eq('business_id', selectedBusiness.id);

        if (error) {
          console.error('Error loading muted senders:', error);
          return;
        }

        const mutedSet = new Set<string>((data || []).map(item => item.email_address.toLowerCase().trim()));
        setMutedSenders(mutedSet);

        const { data: mutedPhones, error: phoneError } = await supabase
          .from('muted_sms_senders')
          .select('phone_key')
          .eq('business_id', selectedBusiness.id);
        if (!phoneError) {
          setMutedPhones(new Set((mutedPhones || []).map((item: { phone_key: string }) => item.phone_key).filter(Boolean)));
        }
      } catch (error) {
        console.error('Error loading muted senders:', error);
      }
    };

    loadMutedSenders();
  }, [selectedBusiness?.id]);

  // Function to check if an email address is muted
  const isEmailMuted = (email: string): boolean => {
    if (!email) return false;
    const normalized = normalizeEmail(email);
    return mutedSenders.has(normalized);
  };

  const isPhoneMuted = (contact: string): boolean => {
    const key = phoneKey(contact);
    return !!key && mutedPhones.has(key);
  };

  const isSpamCard = (card: CommunicationCard): boolean => (
    (card.type === 'email' && isEmailMuted(card.contact_info)) || isPhoneMuted(card.contact_info)
  );

  // Function to mute an email sender
  const muteEmailSender = async (emailAddress: string) => {
    if (!selectedBusiness?.id || !supabase || !user?.id) return;

    const normalizedEmail = normalizeEmail(emailAddress);
    
    try {
      const { error } = await supabase
        .from('muted_email_senders')
        .insert({
          business_id: selectedBusiness.id,
          email_address: normalizedEmail,
          muted_by: user.id,
          reason: 'junk'
        });

      if (error) {
        // If already muted, that's fine
        if (error.code !== '23505') { // Unique constraint violation
          console.error('Error muting sender:', error);
          throw error;
        }
      }

      // Update local state
      setMutedSenders(prev => new Set([...prev, normalizedEmail]));
      
      console.log('✅ Muted email sender:', normalizedEmail);
    } catch (error) {
      console.error('Error muting email sender:', error);
      throw error;
    }
  };

  // Function to unmute an email sender
  const unmuteEmailSender = async (emailAddress: string) => {
    if (!selectedBusiness?.id || !supabase) return;

    const normalizedEmail = normalizeEmail(emailAddress);
    
    try {
      const { error } = await supabase
        .from('muted_email_senders')
        .delete()
        .eq('business_id', selectedBusiness.id)
        .eq('email_address', normalizedEmail);

      if (error) {
        console.error('Error unmuting sender:', error);
        throw error;
      }

      // Update local state
      setMutedSenders(prev => {
        const newSet = new Set(prev);
        newSet.delete(normalizedEmail);
        return newSet;
      });
      
      console.log('✅ Unmuted email sender:', normalizedEmail);
    } catch (error) {
      console.error('Error unmuting email sender:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;
    const gmailIds = Array.from(
      new Set(
        messages
          .filter((msg) => msg.message_type === 'email' && msg.twilio_message_sid)
          .map((msg) => msg.twilio_message_sid as string)
      )
    );

    if (gmailIds.length === 0) {
      setEmailAttachmentsByGmailId({});
      return;
    }

    const loadAttachments = async () => {
      try {
        const { data, error } = await supabase
          .from('email_attachments')
          .select('id, gmail_id, filename, content_type, size_bytes, public_url, is_inline')
          .eq('business_id', selectedBusiness.id)
          .in('gmail_id', gmailIds);

        if (error) throw error;

        const grouped = (data || []).reduce<Record<string, EmailAttachment[]>>((acc, attachment) => {
          if (!acc[attachment.gmail_id]) acc[attachment.gmail_id] = [];
          acc[attachment.gmail_id].push(attachment);
          return acc;
        }, {});

        setEmailAttachmentsByGmailId(grouped);
      } catch (err) {
        console.error('Error loading email attachments:', err);
      }
    };

    loadAttachments();
  }, [messages, selectedBusiness?.id]);

  // Function to load more messages (older than currently loaded)
  const loadMoreMessages = async () => {
    if (!selectedBusiness?.id || !supabase || loadingMoreMessages || !hasMoreMessages) return;
    
    setLoadingMoreMessages(true);
    try {
      const cutoffDate = messagesLoadedUntil || new Date();
      const cutoffISO = cutoffDate.toISOString();
      
      const { data: olderMessages, error } = await supabase
        .from('all_messages')
        .select('id, customer_id, message_type, direction, from_number, to_number, body, subject, read, created_at, media_urls, twilio_message_sid')
        .eq('business_id', selectedBusiness.id)
        .lt('created_at', cutoffISO) // Load messages older than current cutoff
        .order('created_at', { ascending: false })
        .limit(MESSAGES_PAGE_SIZE);
      
      if (error) throw error;
      
      if (olderMessages && olderMessages.length > 0) {
        // Append older messages to existing messages
        setMessages(prev => [...prev, ...olderMessages]);
        
        // Update cutoff date
        const oldestMessage = olderMessages[olderMessages.length - 1];
        if (oldestMessage) {
          setMessagesLoadedUntil(new Date(oldestMessage.created_at));
        }
        
        // Check if there are more messages
        if (olderMessages.length < MESSAGES_PAGE_SIZE) {
          setHasMoreMessages(false);
        }
      } else {
        setHasMoreMessages(false);
      }
    } catch (error) {
      console.error('Error loading more messages:', error);
    } finally {
      setLoadingMoreMessages(false);
    }
  };

  // Handle URL parameters for customer selection (push deep link or CRM/booking link)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const customerParam = urlParams.get('customer');
    const customerIdParam = urlParams.get('customerId');
    const bookingIdParam = urlParams.get('bookingId');
    const sourceParam = urlParams.get('source');
    
    if (customerParam) {
      setUrlCustomerName(decodeURIComponent(customerParam));
    }
    
    if (customerIdParam) {
      setUrlCustomerId(customerIdParam);
      // Only set "Back to X" when URL has explicit source (booking or crm). Push notifications use only customerId (no source) – never set cameFromCRM/cameFromBooking for that.
      if (bookingIdParam) {
        setBookingId(bookingIdParam);
        setSourcePage(sourceParam || 'bookings');
        setCameFromBooking(true);
      } else if (sourceParam === 'crm') {
        setSourcePage('crm');
        setCameFromCRM(true);
      }
    }
  }, []);

  // Normalize phone for comparison (strip non-digits, optional leading 1)
  const normalizePhone = (s: string) => {
    const digits = (s || '').replace(/\D/g, '');
    return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  };

  // Clears consumed push/CRM deep-link selection state so it isn't re-applied later
  // (e.g. when the call button's closeAllModals clears selectedCard).
  const clearDeepLinkSelection = useCallback(() => {
    setUrlCustomerId(null);
    setUrlContact(null);
    setUrlCustomerName(null);
  }, []);

  // Auto-select customer/conversation from URL parameter when communication cards are loaded
  useEffect(() => {
    if ((urlCustomerName || urlCustomerId || urlContact) && communicationCards.length > 0 && !selectedCard) {
      let customerCard = null;
      
      // First try to find by customer ID (more precise)
      if (urlCustomerId) {
        customerCard = communicationCards.find(card => 
          card.customer_id === urlCustomerId
        );
        
        if (customerCard) {
          console.log('Auto-selecting customer from URL by ID:', urlCustomerId);
          setSelectedCard(customerCard);
          if (isMobile) setMobileModalOpen(true); // Open conversation modal on mobile (e.g. push deep link)
          const url = new URL(window.location.href);
          url.searchParams.delete('customerId');
          window.history.replaceState({}, '', url.toString());
          // Clear deep-link state now that it's consumed. Otherwise closing the thread
          // (e.g. the call button's closeAllModals → selectedCard=null) would re-trigger
          // this effect and re-open the conversation over the dialler.
          clearDeepLinkSelection();
          return;
        }
      }
      
      // Fallback to customer name search
      if (urlCustomerName && !customerCard) {
        customerCard = communicationCards.find(card => 
          card.customer_name && card.customer_name.toLowerCase().includes(urlCustomerName.toLowerCase())
        );
        
        if (customerCard) {
          console.log('Auto-selecting customer from URL by name:', urlCustomerName);
          setSelectedCard(customerCard);
          if (isMobile) setMobileModalOpen(true);
          const url = new URL(window.location.href);
          url.searchParams.delete('customer');
          window.history.replaceState({}, '', url.toString());
          clearDeepLinkSelection();
          return;
        }
      }

      // Fallback to contact (phone or email) - e.g. from push notification deep link
      if (urlContact && !customerCard) {
        const contactTrimmed = urlContact.trim();
        const isLikelyEmail = contactTrimmed.includes('@');
        customerCard = communicationCards.find(card => {
          const info = (card.contact_info || '').trim();
          if (isLikelyEmail) {
            return info.toLowerCase() === contactTrimmed.toLowerCase();
          }
          return normalizePhone(info) === normalizePhone(contactTrimmed);
        });
        if (customerCard) {
          console.log('Auto-selecting conversation from URL by contact:', urlContact);
          setSelectedCard(customerCard);
          if (isMobile) setMobileModalOpen(true); // Open conversation modal on mobile (e.g. push deep link)
          const url = new URL(window.location.href);
          url.searchParams.delete('contact');
          window.history.replaceState({}, '', url.toString());
          clearDeepLinkSelection();
        }
      }
    }
  }, [urlCustomerName, urlCustomerId, urlContact, communicationCards, selectedCard, isMobile, clearDeepLinkSelection]);

  // Reconnect trigger — incremented when app returns to foreground so the
  // subscription useEffect re-runs (cleanup kills the stale channel, setup
  // creates a fresh one).
  const [realtimeReconnectKey, setRealtimeReconnectKey] = useState(0);
  const refreshMessagesRef = useRef(refreshMessages);
  refreshMessagesRef.current = refreshMessages;

  // App lifecycle: reconnect realtime + refresh data when returning from background.
  // IMPORTANT: only reload after a GENUINE background→foreground transition. On iOS
  // (WKWebView) visibility/appState events can fire spuriously (e.g. when the soft
  // keyboard opens/closes while composing), and a full reload mid-typing tears down
  // the conversation and disrupts the keyboard/draft. Gating on an actual background
  // ensures we still catch up after real app switches, but never while just typing.
  const wasBackgroundedRef = useRef(false);
  useEffect(() => {
    const handleBackground = () => { wasBackgroundedRef.current = true; };

    const handleForeground = () => {
      if (!wasBackgroundedRef.current) return; // ignore spurious foreground events (keyboard, overlays)
      wasBackgroundedRef.current = false;
      console.log('[REALTIME] 🔄 App returned to foreground after background — reconnect & refresh');
      setRealtimeReconnectKey(k => k + 1);
      refreshMessagesRef.current();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) handleBackground();
      else handleForeground();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    let removeNativeListener: (() => void) | null = null;
    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform()) {
          const { App: CapacitorApp } = await import('@capacitor/app');
          const listener = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
            if (isActive) handleForeground();
            else handleBackground();
          });
          removeNativeListener = () => listener.remove();
        }
      } catch (e) {
        console.warn('[REALTIME] Capacitor not available:', e);
      }
    })();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      removeNativeListener?.();
    };
  }, []);

  // Set up realtime subscription for live updates
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;
    if (messagingIntegrationsStatus !== 'ready' || !canUseMessaging) return;

    console.log('[REALTIME] Setting up subscription for business:', selectedBusiness.id);

    // Core tables: any change on sms_messages / received_emails / email_message_state /
    // call_history / agent_callbacks re-pulls the inbox (one call, debounced).
    let coreReloadTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleCoreInboxReload = () => {
      if (!coreMessagesRef.current) return;
      if (coreReloadTimer) clearTimeout(coreReloadTimer);
      coreReloadTimer = setTimeout(() => {
        coreReloadTimer = null;
        reloadCoreInbox();
      }, 400);
    };

    // Create the subscription for messages.
    // Include reconnectKey in the channel name so Supabase creates a fresh
    // WebSocket channel instead of reusing a stale/disconnected one.
    const subscription = supabase
      .channel(`communications_${selectedBusiness.id}_${realtimeReconnectKey}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'sms_messages', filter: `business_id=eq.${selectedBusiness.id}` },
        () => scheduleCoreInboxReload()
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'received_emails', filter: `business_id=eq.${selectedBusiness.id}` },
        () => scheduleCoreInboxReload()
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'email_message_state', filter: `business_id=eq.${selectedBusiness.id}` },
        () => scheduleCoreInboxReload()
      )
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'all_messages',
          filter: `business_id=eq.${selectedBusiness.id}` // Only listen to messages for this business
        },
        (payload) => {
          console.log('[REALTIME] Received event:', payload.eventType, payload);

          if (payload.eventType === 'INSERT') {
            const newMessage = payload.new as Message;
            console.log('[REALTIME] 📥 New message received:', {
              id: newMessage.id,
              type: newMessage.message_type,
              direction: newMessage.direction,
              from: newMessage.from_number,
              to: newMessage.to_number,
              body: newMessage.body.substring(0, 50) + '...',
              timestamp: newMessage.created_at
            });
            
            // Push is sent only by DB trigger (trigger_notify_new_message on all_messages).
            // Do NOT call notifyNewMessage() here — it caused duplicate notifications.
            
            setMessages(prevMessages => {
              // Check if message already exists (prevent duplicates).
              // Also match by twilio_message_sid to catch optimistic messages
              // that were added with a Gmail ID before the real DB record arrived.
              const existingMsg = prevMessages.find(msg =>
                msg.id === newMessage.id ||
                (newMessage.twilio_message_sid && msg.id === newMessage.twilio_message_sid) ||
                (newMessage.twilio_message_sid && msg.twilio_message_sid === newMessage.twilio_message_sid)
              );
              if (existingMsg) {
                console.log('[REALTIME] ⚠️ Message already exists, replacing optimistic:', newMessage.id);
                // Preserve customer_id from the optimistic message if the real
                // DB record doesn't have one (edge function doesn't set it).
                const merged = (!newMessage.customer_id && existingMsg.customer_id)
                  ? { ...newMessage, customer_id: existingMsg.customer_id }
                  : newMessage;
                return prevMessages.map(msg =>
                  (msg.id === newMessage.id ||
                   (newMessage.twilio_message_sid && msg.id === newMessage.twilio_message_sid) ||
                   (newMessage.twilio_message_sid && msg.twilio_message_sid === newMessage.twilio_message_sid))
                    ? merged : msg
                );
              }
              
              console.log('[REALTIME] ✅ Adding message to state, total messages will be:', prevMessages.length + 1);
              
              const updatedMessages = [newMessage, ...prevMessages].sort(
                (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
              );
              
              return updatedMessages;
            });
          } else if (payload.eventType === 'UPDATE') {
            const updatedMessage = payload.new as Message;
            console.log('[REALTIME] Updating message:', updatedMessage.id);
            
            setMessages(prevMessages =>
              prevMessages.map(msg =>
                msg.id === updatedMessage.id ? updatedMessage : msg
              )
            );
          } else if (payload.eventType === 'DELETE') {
            const deletedMessage = payload.old as Message;
            console.log('[REALTIME] Removing message:', deletedMessage.id);
            
            setMessages(prevMessages =>
              prevMessages.filter(msg => msg.id !== deletedMessage.id)
            );
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'call_history',
          filter: `business_id=eq.${selectedBusiness.id}` // Only listen to calls for this business
        },
        (payload) => {
          console.log('[REALTIME] Call history event:', payload.eventType, payload);
          scheduleCoreInboxReload();

          if (payload.eventType === 'INSERT') {
            const newCall = payload.new;
            console.log('[REALTIME] 📞 New call received:', newCall);
            
            setCallHistory(prevCalls => {
              // Check if call already exists (prevent duplicates)
              if (prevCalls.some(call => call.id === newCall.id)) {
                console.log('[REALTIME] ⚠️ Call already exists, skipping:', newCall.id);
                return prevCalls;
              }
              
              // Add new call and sort by call_time (newest first)
              const updatedCalls = [newCall, ...prevCalls].sort(
                (a, b) => new Date(b.call_time).getTime() - new Date(a.call_time).getTime()
              );
              
              return updatedCalls;
            });
          } else if (payload.eventType === 'UPDATE') {
            const updatedCall = payload.new;
            console.log('[REALTIME] 📞 Updating call:', updatedCall.call_sid);
            
            setCallHistory(prevCalls =>
              prevCalls.map(call =>
                call.id === updatedCall.id ? updatedCall : call
              )
            );
          } else if (payload.eventType === 'DELETE') {
            const deletedCall = payload.old;
            console.log('[REALTIME] 📞 Removing call:', deletedCall.call_sid);
            
            setCallHistory(prevCalls =>
              prevCalls.filter(call => call.id !== deletedCall.id)
            );
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'agent_callbacks',
          filter: `business_id=eq.${selectedBusiness.id}`
        },
        (payload) => {
          console.log('[REALTIME] Agent callback event:', payload.eventType, payload);
          scheduleCoreInboxReload();

          if (payload.eventType === 'INSERT') {
            const newCb = payload.new;
            setAgentCallbacks(prev => {
              if (prev.some(cb => cb.id === newCb.id)) return prev;
              return [newCb, ...prev].sort(
                (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
              );
            });
          } else if (payload.eventType === 'UPDATE') {
            const updatedCb = payload.new;
            setAgentCallbacks(prev =>
              prev.map(cb => (cb.id === updatedCb.id ? updatedCb : cb))
            );
          } else if (payload.eventType === 'DELETE') {
            const deletedCb = payload.old;
            setAgentCallbacks(prev => prev.filter(cb => cb.id !== deletedCb.id));
          }
        }
      )
      .subscribe((status) => {
        console.log('[REALTIME] Subscription status:', status);
      });

    // Cleanup subscription on unmount, business change, or reconnect trigger
    return () => {
      console.log('[REALTIME] Cleaning up subscription');
      if (coreReloadTimer) clearTimeout(coreReloadTimer);
      subscription.unsubscribe();
    };
  }, [selectedBusiness?.id, realtimeReconnectKey, messagingIntegrationsStatus, canUseMessaging, reloadCoreInbox]);



  // Optimized communication cards processing
  useEffect(() => {
    // Core tables: the list is exactly what messages_inbox returned. Realtime events
    // re-pull that list (see the subscription below) instead of rebuilding it here.
    if (coreMessages) {
      setCommunicationCards(summaryCardsRef.current);
      return;
    }
    if (!messages.length && !callHistory.length && !agentCallbacks.length) {
      // Keep the instant-paint summary cards if the heavy window hasn't loaded yet.
      setCommunicationCards(summaryCardsRef.current.length ? summaryCardsRef.current : []);
      return;
    }

    // Use Map for O(1) lookups instead of object
    const cards = new Map<string, CommunicationCard>();
    
    // Create customer lookup map for fast access
    const customerMap = new Map(customers.map(c => [c.id, c]));
    
    console.log('📋 Customer Map Created:', {
      totalCustomers: customers.length,
      customerIds: Array.from(customerMap.keys()).slice(0, 5), // First 5 customer IDs
      sampleCustomers: customers.slice(0, 3).map(c => ({ id: c.id, name: `${c.first_name} ${c.last_name}`, phone: c.phone }))
    });

    // Convert calls to message format for unified processing
    const callMessages: Message[] = callHistory.map((call) => {
      // Match customer by phone number if customer_id is not set
      let matchedCustomerId = call.customer_id;
      
      if (!matchedCustomerId) {
        // Try to find customer by phone number
        const callPhoneNumber = call.direction === 'inbound' ? call.from_number : call.to_number;
        const matchedCustomer = customers.find(customer => {
          if (!customer.phone) return false;
          // Normalize phone numbers for comparison
          const customerPhone = customer.phone.replace(/\D/g, '');
          const callPhone = callPhoneNumber.replace(/\D/g, '');
          return customerPhone === callPhone || 
                 customerPhone === callPhone.substring(1) || // Remove country code
                 `1${customerPhone}` === callPhone; // Add country code
        });
        
        if (matchedCustomer) {
          matchedCustomerId = matchedCustomer.id;
          console.log(`📞 Matched call to customer: ${matchedCustomer.first_name} ${matchedCustomer.last_name}`);
        }
      }
      
      return {
        id: call.id,
        business_id: call.business_id,
        customer_id: matchedCustomerId,
        message_type: 'call' as const,
        direction: call.direction,
        from_number: call.from_number,
        to_number: call.to_number,
        body: call.direction === 'outbound' ? 'Outbound call' : 
              (call.status === 'answered' ? 'Call answered' : 'Missed call'),
        read: 'read' as const, // Calls are always considered "read"
        created_at: call.call_time,
        updated_at: call.updated_at || call.call_time,
        call_status: call.status,
        duration_seconds: call.duration_seconds,
        call_sid: call.call_sid,
        viewed: call.viewed,
        recording_url: call.recording_url || null,
        transcription_text: call.transcription_text || null,
        transcription_status: call.transcription_status || null,
        agent_id: call.agent_id || null,
        agent_session_id: call.agent_session_id || null
      };
    });

    // AI receptionist callback requests, matched to customers like calls
    const callbackMessages: Message[] = agentCallbacks.map((cb) => {
      let matchedCustomerId = cb.customer_id;
      if (!matchedCustomerId && cb.from_number) {
        const cbPhone = String(cb.from_number).replace(/\D/g, '');
        const matched = customers.find(customer => {
          if (!customer.phone) return false;
          const customerPhone = customer.phone.replace(/\D/g, '');
          return customerPhone === cbPhone ||
                 customerPhone === cbPhone.substring(1) ||
                 `1${customerPhone}` === cbPhone;
        });
        if (matched) matchedCustomerId = matched.id;
      }
      return callbackToMessage(cb, matchedCustomerId);
    });

    // Combine messages, calls, and callbacks
    const allCommunications = [...messages, ...callMessages, ...callbackMessages];

    allCommunications.forEach((message) => {
      let cardId: string;
      let cardType: 'customer' | 'phone' | 'email';
      let contactInfo: string;
      let customerName: string | undefined;

      let effectiveCustomerId = message.customer_id;

      // Client-side phone matching for unlinked SMS/call/callback messages
      if (!effectiveCustomerId && (message.message_type === 'sms' || message.message_type === 'call' || message.message_type === 'callback')) {
        const rawPhone = message.direction === 'inbound' ? message.from_number : message.to_number;
        if (rawPhone) {
          const msgDigits = rawPhone.replace(/\D/g, '');
          const matched = customers.find(c => {
            if (!c.phone) return false;
            const cDigits = c.phone.replace(/\D/g, '');
            return cDigits === msgDigits ||
                   cDigits === msgDigits.substring(1) ||
                   `1${cDigits}` === msgDigits;
          });
          if (matched) effectiveCustomerId = matched.id;
        }
      }

      if (effectiveCustomerId) {
        cardId = `customer_${effectiveCustomerId}`;
        cardType = 'customer';
        const customer = customerMap.get(effectiveCustomerId);
        customerName = customer ? `${customer.first_name} ${customer.last_name}`.trim() : undefined;
        const rawContact = message.direction === 'inbound' ? message.from_number : message.to_number;
        contactInfo = message.message_type === 'email' ? normalizeEmail(rawContact) : rawContact;
      } else {
        const rawContact = message.direction === 'inbound' ? message.from_number : message.to_number;
        const contact = message.message_type === 'email'
          ? normalizeEmail(rawContact)
          : rawContact;
        // Normalize phone for card key to prevent duplicate threads from format differences
        const isPhoneKind = message.message_type === 'sms' || message.message_type === 'call' || message.message_type === 'callback';
        const cardKey = isPhoneKind ? normalizePhone(contact) : contact;
        // Callbacks group into the same phone thread as sms/calls
        cardId = `${message.message_type === 'callback' ? 'sms' : message.message_type}_${cardKey}`;
        cardType = isPhoneKind ? 'phone' : 'email';
        contactInfo = contact;
        customerName = undefined;
      }

      let card = cards.get(cardId);
      if (!card) {
        card = {
          id: cardId,
          type: cardType,
          customer_id: effectiveCustomerId || message.customer_id || undefined,
          customer_name: customerName,
          contact_info: contactInfo,
          messages: [],
          last_message: message,
          unread_count: 0,
          has_unread: false,
        };
        cards.set(cardId, card);
      } else if (effectiveCustomerId) {
        card.customer_id = effectiveCustomerId;
        card.type = 'customer';
        if (customerName) {
          card.customer_name = customerName;
        }
      }

      card.messages.push(message);
      
      // Update last message if this one is newer (using timestamp comparison for speed)
      const messageTime = new Date(message.created_at).getTime();
      const lastMessageTime = new Date(card.last_message.created_at).getTime();
      if (messageTime > lastMessageTime) {
        card.last_message = message;
      }

      // Count unread efficiently
      if (message.read === 'unread' && !locallyReadMessages.has(message.id)) {
        card.unread_count++;
        card.has_unread = true;
      } else if (message.message_type === 'call' && message.call_status === 'missed' && !message.viewed) {
        card.unread_count++;
        card.has_unread = true;
      } else if (message.message_type === 'callback' && message.callback_status === 'pending' && !message.viewed && !locallyReadMessages.has(message.id)) {
        card.unread_count++;
        card.has_unread = true;
      }
    });

    // Filter out the business's own Twilio number from appearing as a card.
    // This can happen if duplicate records in all_messages have mismatched data.
    const businessTwilioNumber = (selectedBusiness as any)?.VITE_TWILIO_PHONE_NUMBER
      ? String((selectedBusiness as any).VITE_TWILIO_PHONE_NUMBER).replace(/\D/g, '')
      : '';

    let cardsArray = Array.from(cards.values())
      .filter(card => {
        if (card.type !== 'phone' || !businessTwilioNumber) return true;
        const contactDigits = (card.contact_info || '').replace(/\D/g, '');
        return contactDigits !== businessTwilioNumber &&
               contactDigits !== `1${businessTwilioNumber}` &&
               `1${contactDigits}` !== businessTwilioNumber;
      })
      .sort(
        (a, b) => new Date(b.last_message.created_at).getTime() - new Date(a.last_message.created_at).getTime()
      );

    // Merge server summaries into the local rebuild. Summaries still carry
    // unread SMS/email the client never bulk-loaded; callback-only cards must
    // not discard that. Same identity → reconcile; summary-only → append.
    if (summaryCardsRef.current.length) {
      const builtByKey = new Map(cardsArray.map(c => [conversationIdentityKey(c), c]));
      const merged: CommunicationCard[] = [];
      const seen = new Set<string>();

      for (const sc of summaryCardsRef.current) {
        const key = conversationIdentityKey(sc);
        const built = builtByKey.get(key);
        if (built) {
          merged.push(mergeSummaryIntoBuiltCard(built, sc));
          seen.add(key);
        } else {
          merged.push(sc);
          seen.add(key);
        }
      }
      for (const built of cardsArray) {
        const key = conversationIdentityKey(built);
        if (!seen.has(key)) merged.push(built);
      }

      cardsArray = merged.sort(
        (a, b) => new Date(b.last_message.created_at).getTime() - new Date(a.last_message.created_at).getTime()
      );
    }

    console.log('[REALTIME] 📊 Communication cards updated:', {
      totalCards: cardsArray.length,
      selectedCardId: selectedCardIdRef.current,
      selectedCardExists: selectedCardIdRef.current ? cardsArray.some(card => card.id === selectedCardIdRef.current) : false,
      selectedCardMessages: selectedCardIdRef.current ? 
        cardsArray.find(card => card.id === selectedCardIdRef.current)?.messages.length : 'N/A'
    });

    setCommunicationCards(cardsArray);
    
    // Reset pipeline pagination when communication cards change significantly
    const previousCardsLength = communicationCards.length;
    if (Math.abs(cardsArray.length - previousCardsLength) > 5) {
      setPipelineOffset(CARDS_PAGE_SIZE);
    }
  }, [messages, callHistory, agentCallbacks, customers, locallyReadMessages, coreMessages]);

  // Keep the cached inbox list in sync with the latest computed state (especially
  // read/unread status). Without this the cache holds the unread counts from first
  // paint, so re-opening Messages flashes already-read conversations as unread
  // before the live RPC corrects them. Debounced + slimmed (no message bodies).
  useEffect(() => {
    const bid = selectedBusiness?.id;
    if (!bid || communicationCards.length === 0) return;
    const t = setTimeout(() => {
      const slim = communicationCards.slice(0, 200).map((c) => {
        // Conversations opened this session are read in the DB even though we keep
        // them visible in the list — persist them as read so reopen is accurate.
        const readThisSession = readInSessionRef.current.has(c.id);
        return {
          id: c.id,
          type: c.type,
          customer_id: c.customer_id,
          customer_name: c.customer_name,
          contact_info: c.contact_info,
          messages: [],
          last_message: c.last_message,
          unread_count: readThisSession ? 0 : c.unread_count,
          has_unread: readThisSession ? false : c.has_unread,
          is_supplier: c.is_supplier,
          is_summary: true,
        };
      });
      cacheConversationSummaries(slim, bid);
    }, 800);
    return () => clearTimeout(t);
  }, [communicationCards, selectedBusiness?.id]);

  // Keep track of selectedCard ID to avoid stale closure issues
  const selectedCardIdRef = useRef<string | null>(null);
  
  // Update selectedCard ID when it changes
  useEffect(() => {
    selectedCardIdRef.current = selectedCard?.id || null;

    // Opening a conversation reads it in the DB, but DON'T remove it from the
    // Unread pipeline mid-session (that yanks the card out when you close it).
    // Record it as read-in-session so the cache persists the read state for the
    // next app open; the live list still shows it until a reload.
    if (selectedCard?.id && selectedCard.has_unread) {
      readInSessionRef.current.add(selectedCard.id);
    }
    
    // Set localStorage flag for FAB hiding on mobile
    if (selectedCard) {
      localStorage.setItem('messages_conversation_open', 'true');
    } else {
      localStorage.removeItem('messages_conversation_open');
      // Reset so re-opening the same conversation fetches fresh data from DB
      lastFetchedCardIdRef.current = null;
      mergeWatermarkRef.current = null;
      // When returning to list view, clear "Back to CRM/Booking" state so list never shows that header (e.g. after opening from push)
      setCameFromCRM(false);
      setCameFromBooking(false);
      setSourcePage(null);
    }
    
    // Dispatch custom event for immediate reactivity
    window.dispatchEvent(new CustomEvent('messagesConversationChanged'));
  }, [selectedCard]);

  // Update selectedCard when communicationCards change (for real-time updates).
  // Uses functional form so `prev` is always current — avoids stale closure issues.
  // Only triggers a re-render when the card actually changed (new messages, read status, etc.).
  useEffect(() => {
    const selectedCardId = selectedCardIdRef.current;
    if (selectedCardId && communicationCards.length > 0) {
      const updatedCard = communicationCards.find(card => card.id === selectedCardId);
      if (updatedCard) {
        setSelectedCard(prev => {
          if (!prev) return updatedCard;
          if (prev.messages.length !== updatedCard.messages.length) return updatedCard;
          if (prev.last_message.id !== updatedCard.last_message.id) return updatedCard;
          if (prev.unread_count !== updatedCard.unread_count) return updatedCard;
          if (prev.has_unread !== updatedCard.has_unread) return updatedCard;
          return prev;
        });
      }
    }
  }, [communicationCards]);

  // Filter cards based on active tab, search query, and apply pagination
  const supplierCustomerIds = useMemo(() => {
    return new Set(customers.filter(c => c.is_supplier).map(c => c.id));
  }, [customers]);

  const isSupplierCard = (card: CommunicationCard) => {
    if (card.is_supplier) return true;
    return Boolean(card.customer_id && supplierCustomerIds.has(card.customer_id));
  };

  const allFilteredCards = useMemo(() => {
    const filtered = communicationCards.filter(card => {
      const isMuted = isSpamCard(card);
      
      if (showMutedView) {
        if (!isMuted || !card.has_unread) return false;
      } else {
        if (isMuted) return false;
      }
      
      if (pipelineSearchQuery) {
        const searchLower = pipelineSearchQuery.toLowerCase();
        const matchesCustomerName = card.customer_name?.toLowerCase().includes(searchLower);
        const matchesContactInfo = card.contact_info.toLowerCase().includes(searchLower);
        // Last-message preview (works for summary-only cards too).
        const matchesPreview = card.last_message?.body?.toLowerCase().includes(searchLower);
        const matchesMessageContent = card.messages.some(msg => 
          msg.body?.toLowerCase().includes(searchLower) || 
          msg.subject?.toLowerCase().includes(searchLower)
        );
        return matchesCustomerName || matchesContactInfo || matchesPreview || matchesMessageContent;
      }
      
      const matchesTab = activeTab === 'unread'
        ? (card.has_unread && !isSupplierCard(card))
        : activeTab === 'unread_suppliers'
          ? (card.has_unread && isSupplierCard(card))
          : !card.has_unread;
      
      return matchesTab;
    });

    // Apply message organization sorting only to unread pipelines
    const isUnreadPipeline = (activeTab === 'unread' || activeTab === 'unread_suppliers') && !pipelineSearchQuery && !showMutedView;
    if (isUnreadPipeline && messageOrganization !== 'chronological') {
      // Determine group by the most recent message type, not the card type
      // sms/call → SMS group, email → Email group
      const isEmailGroup = (c: CommunicationCard) =>
        c.last_message.message_type === 'email';

      filtered.sort((a, b) => {
        const aIsEmail = isEmailGroup(a);
        const bIsEmail = isEmailGroup(b);

        if (aIsEmail !== bIsEmail) {
          if (messageOrganization === 'sms_first') {
            return aIsEmail ? 1 : -1;
          } else {
            return aIsEmail ? -1 : 1;
          }
        }
        // Within the same group, keep chronological order (newest first)
        return new Date(b.last_message.created_at).getTime() - new Date(a.last_message.created_at).getTime();
      });
    }

    return filtered;
  }, [communicationCards, activeTab, pipelineSearchQuery, showMutedView, messageOrganization, supplierCustomerIds, mutedSenders, mutedPhones]);
  
  const filteredCards = allFilteredCards.slice(0, pipelineOffset);
  const hasMoreCards = allFilteredCards.length > pipelineOffset;
  
  // Debug logging for Load More button
  console.log('📊 Pipeline pagination debug:', {
    totalCards: communicationCards.length,
    allFilteredCards: allFilteredCards.length,
    filteredCards: filteredCards.length,
    pipelineOffset,
    hasMoreCards,
    activeTab
  });

  const unreadCount = communicationCards.reduce((total, card) => {
    if (isSupplierCard(card)) return total;
    // Exclude muted email senders from unread count
    if (isSpamCard(card)) return total;
    return total + card.unread_count;
  }, 0);

  const unreadSuppliersCount = communicationCards.reduce((total, card) => {
    if (!isSupplierCard(card)) return total;
    return total + card.unread_count;
  }, 0);

  // Inline composer component for new messages
  const ComposeMessageComposer = ({ 
    type, 
    contact, 
    customer, 
    businessId, 
    onSendSuccess, 
    onSendError 
  }: { 
    type: 'sms' | 'email'; 
    contact: string; 
    customer: Customer | null; 
    businessId: string; 
    onSendSuccess: () => void; 
    onSendError: (error: string) => void;
  }) => {
    const [smsValue, setSmsValue] = useState('');
    const [isSending, setIsSending] = useState(false);

    const handleSendSMS = async (aiGenerated: boolean, mediaFiles?: File[]) => {
      if (!businessId || !contact.trim() || (!smsValue.trim() && (!mediaFiles || mediaFiles.length === 0))) return;
      
      setIsSending(true);
      try {
        const result = await sendSMS({
          to: contact.trim(),
          message: smsValue,
          businessId: businessId,
          aiGenerated: aiGenerated,
          mediaFiles: mediaFiles
        });
        
        if (result.error) {
          throw new Error(result.error);
        }
        
        onSendSuccess();
      } catch (error) {
        console.error('Error sending SMS:', error);
        onSendError(error instanceof Error ? error.message : 'Failed to send SMS');
      } finally {
        setIsSending(false);
      }
    };

    if (type === 'sms') {
      return (
        <div className="border-t border-gray-200 pt-4">
          <SMSReplyComposer
            businessId={businessId}
            value={smsValue}
            onChange={setSmsValue}
            onSend={handleSendSMS}
            isSending={isSending}
            error={null}
            phoneNumber={contact}
            customerId={customer?.id}
            shouldFetchAI={false}
            className="w-full rounded-xl border border-gray-200"
          />
        </div>
      );
    } else {
      return (
        <div className="border-t border-gray-200 pt-4">
          <EmailReplyComposer
            businessId={businessId}
            to={contact}
            defaultSubject=""
            placeholder="Write your email message..."
            onSendSuccess={(messageId) => {
              onSendSuccess();
            }}
            onSendError={(error) => {
              onSendError(error);
            }}
            disabled={false}
            className="w-full"
            variant="gmail"
            customerId={customer?.id}
            shouldFetchAI={false}
          />
        </div>
      );
    }
  };

  // Strip HTML for search snippet display
  const stripHtmlForSnippet = (html: string, maxLen = 140): string => {
    if (!html || typeof html !== 'string') return '';
    const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    return text.length > maxLen ? text.slice(0, maxLen).trim() + '…' : text;
  };

  const formatConversationListTime = (iso: string): string => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfMsg = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const dayDiff = Math.round((startOfToday - startOfMsg) / 86400000);

    if (dayDiff === 0) {
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    if (dayDiff === 1) {
      return 'Yesterday';
    }
    if (date.getFullYear() === now.getFullYear()) {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const CommunicationCardComponent = ({ card, searchQuery, onSnippetClick }: { card: CommunicationCard; searchQuery?: string; onSnippetClick?: (card: CommunicationCard, messageId: string) => void }) => {
    // For communication cards, show just the email address for email contacts, full info for others
    const displayName = card.customer_name || 
      (card.type === 'email' ? extractEmailAddress(card.contact_info) : card.contact_info);
    const messagePreview = card.last_message.message_type === 'email'
      ? (card.last_message.subject ? decodeEmailSubject(card.last_message.subject).replace(/^(re:|fwd:|fw:)\s*/gi, '').trim() : '')
      : (card.last_message.subject || card.last_message.body);
    const timeAgo = formatConversationListTime(card.last_message.created_at);

    const isSelected = selectedCard?.id === card.id;

    // When searching, find messages that match the query so we can show the actual matching content nested under the card
    const matchingMessageSnippets = useMemo(() => {
      if (!searchQuery || !searchQuery.trim()) return [];
      const q = searchQuery.toLowerCase().trim();
      return card.messages
        .filter(msg => {
          const bodyText = msg.body ? stripHtmlForSnippet(msg.body, 5000) : '';
          const subj = (msg.subject || '').toLowerCase();
          return bodyText.toLowerCase().includes(q) || subj.includes(q);
        })
        .map(msg => {
          const bodyText = msg.body ? stripHtmlForSnippet(msg.body, 5000) : '';
          const subj = (msg.subject || '').trim();
          const bodyMatch = bodyText.toLowerCase().includes(q);
          const subjectMatch = subj.toLowerCase().includes(q);
          if (bodyMatch) {
            const idx = bodyText.toLowerCase().indexOf(q);
            const start = Math.max(0, idx - 40);
            const end = Math.min(bodyText.length, idx + q.length + 80);
            let snippet = (start > 0 ? '…' : '') + bodyText.slice(start, end).trim() + (end < bodyText.length ? '…' : '');
            snippet = snippet.replace(/\s+/g, ' ').trim();
            return { snippet, subject: subj || null, created_at: msg.created_at, messageId: msg.id, message_type: msg.message_type };
          }
          if (subjectMatch) return { snippet: subj, subject: null, created_at: msg.created_at, messageId: msg.id, message_type: msg.message_type };
          return null;
        })
        .filter((s): s is { snippet: string; subject: string | null; created_at: string; messageId: string; message_type: string } => s != null);
    }, [card.messages, searchQuery]);
    
    const resolvedCustomerId = useMemo(() => {
      if (card.customer_id) return card.customer_id;
      const contactDigits = (card.contact_info || '').replace(/\D/g, '');
      if (contactDigits.length < 10) return null;
      const matched = customers.find((c) => {
        if (!c.phone) return false;
        const cDigits = c.phone.replace(/\D/g, '');
        return (
          cDigits === contactDigits ||
          cDigits === contactDigits.substring(1) ||
          `1${cDigits}` === contactDigits
        );
      });
      return matched?.id ?? null;
    }, [card.customer_id, card.contact_info, customers]);

    const customer = resolvedCustomerId ? customers.find(c => c.id === resolvedCustomerId) : null;
    const isSupplier = customer?.is_supplier || false;
    const isLead = useMemo(() => {
      if (resolvedCustomerId && customersWithOpenOpportunities.has(resolvedCustomerId)) {
        return true;
      }

      const contactDigits = (card.contact_info || '').replace(/\D/g, '');
      if (contactDigits.length < 10) return false;

      return customers.some((c) => {
        if (!customersWithOpenOpportunities.has(c.id) || !c.phone) return false;
        const cDigits = c.phone.replace(/\D/g, '');
        return (
          cDigits === contactDigits ||
          cDigits === contactDigits.substring(1) ||
          `1${cDigits}` === contactDigits
        );
      });
    }, [resolvedCustomerId, customersWithOpenOpportunities, card.contact_info, customers]);

    const handleMarkAllRead = (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent card selection when clicking the button
      markAllMessagesAsRead(card);
    };

    // Press-and-hold an email row to junk the sender without opening the thread.
    // Same effect as the Ban button in the conversation header.
    const isEmailCard = card.type === 'email';
    // A long press is followed by a click; swallow that one so the row doesn't
    // also open the conversation behind the confirm.
    const suppressClickRef = useRef(false);

    const markSenderAsJunk = async () => {
      const address = extractEmailAddress(card.contact_info);
      if (
        !window.confirm(
          `Mark ${address} as junk? All future emails from this sender will be hidden from your unread pipeline.`
        )
      ) {
        return;
      }
      try {
        await muteEmailSender(card.contact_info);
        toast.success(`${address} marked as junk`);
      } catch (error) {
        console.error('Error marking sender as junk:', error);
        toast.error('Could not mark this sender as junk');
      }
    };

    const junkLongPressHandlers = useLongPress({
      onLongPress: () => {
        if (!isEmailCard) return;
        suppressClickRef.current = true;
        void hapticMedium();
        void markSenderAsJunk();
      },
      threshold: 500,
    });

    return (
      <div 
        className={`cursor-pointer transition-all duration-150 active:scale-[0.98] touch-manipulation ${
          isSelected ? 'bg-blue-50/50' : 'hover:bg-gray-50 active:bg-gray-100'
        }`}
        {...(isEmailCard ? junkLongPressHandlers : {})}
        onContextMenu={
          isEmailCard
            ? (e) => {
                e.preventDefault();
                suppressClickRef.current = true;
                void markSenderAsJunk();
              }
            : undefined
        }
        onClick={() => {
          if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return;
          }
          // Unread pipeline + latest is email → open conversation with the newest email thread expanded.
          // Use OPEN_LATEST_EMAIL (not last_message.id): summary cards use synthetic `summary_…` ids
          // that never match timeline message UUIDs.
          const shouldAutoOpenEmail =
            activeTab !== 'read' && card.last_message.message_type === 'email';
          setOpenMessageIdWhenSelected(shouldAutoOpenEmail ? OPEN_LATEST_EMAIL : null);
          setSelectedCard(card);
          if (isMobile) {
            setMobileModalOpen(true);
          }
        }}
        style={{
          WebkitTapHighlightColor: 'transparent',
          ...(isEmailCard
            ? {
                userSelect: 'none' as const,
                WebkitUserSelect: 'none' as const,
                WebkitTouchCallout: 'none' as const,
              }
            : {}),
        }}
      >
        <div className={`px-4 py-3 ${isSelected ? 'border-l-4 border-blue-500' : ''}`}>
          <div className="flex items-center space-x-3">
            {/* Avatar - iOS Style */}
            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-xs ${
              card.type === 'customer' || card.customer_name ? 'bg-gradient-to-b from-gray-300 to-gray-400 shadow-sm' :
              card.type === 'phone' ? (
                card.last_message.message_type === 'call' ? (
                  card.last_message.direction === 'outbound' ? 'bg-blue-500' : 
                  (card.last_message.call_status === 'answered' ? 'bg-green-500' : 'bg-red-500')
                ) : 'bg-green-500'
              ) : 'bg-red-500'
            }`}>
              {card.type === 'customer' || card.customer_name ? (
                // Show initials for customers
                <span className="uppercase">
                  {card.customer_name ? 
                    card.customer_name.split(' ').map(n => n[0]).join('').slice(0, 2) :
                    displayName.split(' ').map(n => n[0]).join('').slice(0, 2)
                  }
                </span>
              ) : card.type === 'phone' ? (
                card.last_message.message_type === 'call' ? (
                  <Phone className="h-5 w-5" />
                ) : (
                  <MessageCircle className="h-5 w-5" />
                )
              ) : (
                <Mail className="h-5 w-5" />
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 min-w-0 flex-1">
                  <h3 className="font-semibold text-gray-900 truncate">
                    {displayName}
                  </h3>
                  {isLead && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      <Sparkles className="h-3 w-3 mr-1" />
                      Lead
                    </span>
                  )}
                  {isSupplier && (
                    <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                      <Building2 className="h-3 w-3 mr-1" />
                      Supplier
                    </span>
                  )}
                  {showMutedView && isSpamCard(card) && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                      <Ban className="h-3 w-3 mr-1" />
                      Muted
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <span className="text-sm text-gray-500 font-medium">{timeAgo}</span>
                  {card.unread_count > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[1.25rem] text-center">
                      {card.unread_count}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Message preview */}
              <p className="text-gray-600 truncate mt-1 text-sm">
                {card.last_message?.message_type === 'email' 
                  ? (card.last_message.subject || '(No subject)')
                  : messagePreview
                }
              </p>

              {/* Message type indicators */}
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    card.last_message.message_type === 'sms' ? 'bg-green-100 text-green-700' :
                    card.last_message.message_type === 'callback' ? 'bg-teal-100 text-teal-700' :
                    card.last_message.message_type === 'call' ? (
                      card.last_message.direction === 'outbound' ? 'bg-blue-100 text-blue-700' : 
                      (card.last_message.call_status === 'answered' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')
                    ) : 'bg-red-100 text-red-700'
                  }`}>
                    {card.last_message.message_type === 'sms' ? (
                      <>
                        <MessageCircle className="h-3 w-3 mr-1" />
                        SMS
                      </>
                    ) : card.last_message.message_type === 'callback' ? (
                      <>
                        <PhoneCall className="h-3 w-3 mr-1" />
                        Callback
                      </>
                    ) : card.last_message.message_type === 'call' ? (
                      <>
                        <Phone className="h-3 w-3 mr-1" />
                        {card.last_message.call_status === 'answered' ? 'Call' : 'Missed call'}
                      </>
                    ) : (
                      <>
                        <Mail className="h-3 w-3 mr-1" />
                        Email
                      </>
                    )}
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Mark Read Button */}
                  {card.unread_count > 0 && (
                    <button
                      onClick={handleMarkAllRead}
                      className="text-xs bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-semibold px-3 py-1.5 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                      title={`Mark all ${card.unread_count} message${card.unread_count !== 1 ? 's' : ''} as read`}
                    >
                      Mark Read
                    </button>
                  )}
                </div>
              </div>

              {/* Search results: show actual matching message(s) nested under the card - each clickable to open that message */}
              {searchQuery && matchingMessageSnippets.length > 0 && (
                <div className="mt-2 pl-0 pt-2 border-t border-gray-100 space-y-1.5">
                  {matchingMessageSnippets.slice(0, 3).map((item, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCard(card);
                        if (isMobile) setMobileModalOpen(true);
                        onSnippetClick?.(card, item.messageId);
                      }}
                      className="w-full text-left text-xs text-gray-600 bg-gray-50 hover:bg-blue-50 rounded-lg px-3 py-2 border border-gray-100 hover:border-blue-200 transition-colors"
                    >
                      <p className="line-clamp-2">{item.snippet}</p>
                      {item.subject && (
                        <p className="text-gray-500 mt-1 truncate">Subject: {item.subject}</p>
                      )}
                    </button>
                  ))}
                  {matchingMessageSnippets.length > 3 && (
                    <p className="text-xs text-gray-500 px-1">+{matchingMessageSnippets.length - 3} more match{matchingMessageSnippets.length - 3 !== 1 ? 'es' : ''}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Cache timeline data per conversation so remounts (caused by parent re-renders
  // redefining ConversationView) don't flash the skeleton loader.
  const conversationTimelineCacheRef = useRef<Map<string, { items: TimelineItem[], offset: number, hasMore: boolean }>>(new Map());

  // Track which card.id was last fetched so remounts of the same conversation skip the DB round-trip
  const lastFetchedCardIdRef = useRef<string | null>(null);

  // Watermark: the created_at of the newest message when the timeline was initially loaded.
  // The merge effect will only add messages NEWER than this, preventing old paginated
  // messages from card.messages (which has the full 30-day set) from being injected.
  const mergeWatermarkRef = useRef<string | null>(null);

  // Cache conversation UI state so remounts don't lose typed text or open modals
  const conversationUICacheRef = useRef<Map<string, {
    messageInput: string;
    replyMethod: 'sms' | 'email';
    emailComposeModalOpen: boolean;
    replySubject: string;
    replyToGmailId: string | null;
    replyThreadId: string | null;
    replyInReplyTo: string | null;
    replyReferences: string | null;
    isReplyMode: boolean;
  }>>(new Map());

  // Mobile conversation modal component
  const MobileConversationModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    card: CommunicationCard | null;
    markMessageAsRead: (messageId: string) => Promise<void>;
    locallyReadMessages: Set<string>;
    fetchMilestones: (customerId: string) => Promise<void>;
  }> = ({ isOpen, onClose, card, markMessageAsRead, locallyReadMessages, fetchMilestones }) => {
    const shellRef = useRef<HTMLDivElement>(null);
    useKeyboardBottomInset(shellRef);

    if (!isOpen || !card) return null;

    return createPortal(
      <div className="fixed inset-0 z-[9999] md:hidden">
        {/* Full screen modal — keyboard inset lifts composer without resizing the whole page */}
        <div
          ref={shellRef}
          className="messages-mobile-conversation-shell fixed inset-0 bg-white flex flex-col h-full"
          style={{
            top: 'env(safe-area-inset-top, 0px)',
            bottom: 'env(safe-area-inset-bottom, 0px)',
            left: 'env(safe-area-inset-left, 0px)',
            right: 'env(safe-area-inset-right, 0px)',
            height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
          }}
        >
          <MessagesConversationView 
            key={card.id}
            card={card} 
            markMessageAsRead={markMessageAsRead}
            markMessagesAsRead={markMessagesAsRead}
            markMessagesAsUnread={markMessagesAsUnread}
            locallyReadMessages={locallyReadMessages}
            fetchMilestones={fetchMilestones}
            onMobileBack={onClose}
            openMessageId={openMessageIdWhenSelected}
            onOpenMessageConsumed={() => setOpenMessageIdWhenSelected(null)}
          />
        </div>
      </div>,
      document.body
    );
  };

  // ⚠️ HOOKS MUST ALL LIVE ABOVE THIS LINE. The returns below are conditional
  // early-exits (loading spinner / "Connect messaging" gate) — declaring any
  // hook after them changes the hook count between renders and crashes React
  // with "Rendered more hooks than during the previous render".
  const messagesConversationOutlet = useMemo(
    () => ({
      selectedBusiness,
      user,
      customers,
      agentCallbacks,
      setMessages,
      setCommunicationCards,
      conversationTimelineCacheRef,
      mergeWatermarkRef,
      conversationUICacheRef,
      lastFetchedCardIdRef,
      setSelectedCard,
      setMobileModalOpen,
      setActiveTab,
      activeTab,
      unreadSuppliersCount,
      emailAttachmentsByGmailId,
      handleEditCustomer,
      handleCreateCustomer,
      muteEmailSender,
      handleMilestoneClick,
      setShowTokenPurchaseModal,
      markAllMessagesAsRead,
      coreMessages,
      coreThreadVersion,
      hydrateCoreCardMessages,
    }),
    [
      selectedBusiness,
      user,
      customers,
      agentCallbacks,
      activeTab,
      unreadSuppliersCount,
      emailAttachmentsByGmailId,
      handleEditCustomer,
      handleCreateCustomer,
      muteEmailSender,
      handleMilestoneClick,
      markAllMessagesAsRead,
      coreMessages,
      coreThreadVersion,
      hydrateCoreCardMessages,
    ],
  );

  if (loading || (selectedBusiness?.id && messagingIntegrationsStatus === 'loading')) {
    return (
      <div 
        className="messages-container bg-gray-100 flex items-center justify-center overflow-hidden p-0 sm:p-6 h-full min-h-0" 
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        }}
      >
        <div className="bg-gray-50 rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 p-6 sm:p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 border-b-2 border-purple-600 mx-auto mb-4 sm:mb-6"></div>
          <p className="text-gray-500 font-medium text-sm sm:text-base">Loading messages...</p>
        </div>
      </div>
    );
  }

  if (selectedBusiness?.id && !canUseMessaging) {
    return (
      <div
        className="messages-container bg-gray-100 flex flex-col items-center justify-center overflow-hidden px-4 py-8 h-full min-h-0 w-full max-w-full"
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        }}
      >
        <div className="text-center max-w-md bg-gray-50 rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 p-8 sm:p-12">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-sm border border-gray-200">
            <MessageCircle className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400" />
          </div>
          <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
            Connect messaging
          </h3>
          <p className="text-gray-600 font-medium text-sm sm:text-base leading-relaxed">
            Please enable Twilio integration or Gmail integration to use messaging.
          </p>
          <button
            type="button"
            onClick={() => navigate('/app-store?tab=integrations')}
            className="mt-6 w-full sm:w-auto inline-flex items-center justify-center rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Go to Integrations
          </button>
        </div>
      </div>
    );
  }

  // Get back button text and navigation based on source
  const getBackButtonInfo = () => {
    switch (sourcePage) {
      case 'proposals':
        return { text: 'Back to Proposals', path: '/proposals' };
      case 'crm':
        return { text: 'Back to CRM', path: '/crm' };
      case 'calendar':
        return { text: 'Back to Calendar', path: '/calendarV2' };
      case 'bookings':
        return { text: 'Back to Bookings', path: '/bookings' };
      default:
        return { text: 'Back to CRM', path: '/crm' };
    }
  };

  const backButtonInfo = getBackButtonInfo();

  return (
    <MessagesConversationOutletContext.Provider value={messagesConversationOutlet}>
    <div 
      className="messages-container bg-gray-100 flex flex-col h-full min-h-0 w-full max-w-full overflow-hidden" 
      style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      {showGmailReauthBanner && (
        <GmailReauthBanner
          onRefresh={() => setShowGmailReauthModal(true)}
          onClose={closeGmailReauthBanner}
        />
      )}

      {/* Main Content - Two Column Layout */}
      <div className="flex-1 flex min-h-0 overflow-hidden w-full max-w-full">
        {/* Left Sidebar - Message Pipeline */}
        <div className={`${
          selectedCard ? 'hidden md:flex' : 'flex'
        } w-full max-w-full md:w-96 lg:w-[500px] bg-white flex-col h-full min-h-0 overflow-hidden`}>
          {/* Pipeline Header - Only show back-to-source when a conversation is open (never on list view; avoids "Back to CRM" after opening from push then going back) */}
          {selectedCard && ((sourcePage && !cameFromCRM && !cameFromBooking) || cameFromCRM || cameFromBooking) ? (
            <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-white">
              {/* Back Arrow - Show based on where we came from */}
              {sourcePage && !cameFromCRM && !cameFromBooking && (
                <button
                  onClick={() => navigate(backButtonInfo.path)}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors duration-150 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span className="text-sm font-medium">{backButtonInfo.text}</span>
                </button>
              )}
              {cameFromCRM && (
                <button
                  onClick={handleBackToCRM}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors duration-150 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span className="text-sm font-medium">Back to CRM</span>
                </button>
              )}
              {cameFromBooking && (
                <button
                  onClick={handleBackToBooking}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors duration-150 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span className="text-sm font-medium">Back to Booking</span>
                </button>
              )}
            </div>
          ) : null}

          {/* Sticky chrome: search + compose + tabs stay put; only cards scroll. */}
          <div className="flex-shrink-0 p-4 bg-white border-b border-gray-200">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={pipelineSearchQuery}
                  onChange={(e) => setPipelineSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation"
                  style={{ 
                    fontSize: '16px',
                    minHeight: '44px',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                />
                {pipelineSearchQuery && (
                  <button
                    onClick={() => setPipelineSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-100 active:bg-gray-200 rounded-full transition-colors touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <X className="h-4 w-4 text-gray-400" />
                  </button>
                )}
              </div>
              <button
                onClick={() => setComposeModalOpen(true)}
                className="flex-shrink-0 w-11 h-11 flex items-center justify-center bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white rounded-xl transition-all duration-150 active:scale-[0.95] touch-manipulation shadow-sm"
                style={{ WebkitTapHighlightColor: 'transparent' }}
                title="Compose new message"
              >
                {/* Compose (square + pen) — the iMessage "new message" glyph */}
                <SquarePen className="h-5 w-5" />
              </button>
            </div>
            <NexusPhoneBalanceNotice businessId={selectedBusiness?.id} />
          </div>

          {/* Pipeline Tabs - iOS design (hidden when searching) */}
          {!pipelineSearchQuery && (
          <div className="flex-shrink-0 p-4 bg-white border-b border-gray-200">
            <div className="flex bg-gray-100 rounded-xl p-1 gap-1" data-messages-pipeline-tabs>
              <button
                onClick={() => setActiveTab('unread')}
                className={`flex-1 px-3 py-2 text-sm font-semibold rounded-lg transition-all duration-150 touch-manipulation ${
                  activeTab === 'unread'
                    ? 'bg-white text-gray-900'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
                style={{ 
                  minWidth: 0,
                  flexShrink: 1,
                  flexBasis: 0,
                  WebkitTapHighlightColor: 'transparent'
                }}
              >
                <div className="flex items-center justify-center gap-2">
                  <MessageCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="flex-shrink-0">Unread</span>
                  {/* Always reserve space for badge to prevent layout shift */}
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full min-w-[1.25rem] text-center flex-shrink-0 ${
                    unreadCount > 0 
                      ? 'bg-red-500 text-white' 
                      : 'bg-transparent text-transparent pointer-events-none'
                  }`}>
                    {unreadCount > 0 ? unreadCount : '0'}
                  </span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('read')}
                className={`flex-1 px-3 py-2 text-sm font-semibold rounded-lg transition-all duration-150 touch-manipulation ${
                  activeTab === 'read'
                    ? 'bg-white text-gray-900'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
                style={{ 
                  minWidth: 0,
                  flexShrink: 1,
                  flexBasis: 0,
                  WebkitTapHighlightColor: 'transparent'
                }}
              >
                <div className="flex items-center justify-center">
                  <span>Read</span>
                </div>
              </button>
            </div>
            {unreadSuppliersCount > 0 && (
              <div className="mt-2">
                <button
                  onClick={() => setActiveTab('unread_suppliers')}
                  className={`w-full px-3 py-2 text-sm font-semibold rounded-lg transition-all duration-150 touch-manipulation ${
                    activeTab === 'unread_suppliers'
                      ? 'bg-white text-gray-900 border border-gray-200'
                      : 'bg-gray-100 text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <div className="flex items-center justify-center gap-2">
                    <span className="flex-shrink-0">Unread Suppliers</span>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full min-w-[1.25rem] text-center flex-shrink-0 bg-red-500 text-white">
                      {unreadSuppliersCount}
                    </span>
                  </div>
                </button>
              </div>
            )}
          </div>
          )}

          {/* Actions — sticky with search/tabs (Unread tab only) */}
          {activeTab === 'unread' && !pipelineSearchQuery && (
            <div className="flex-shrink-0 px-4 py-2 border-b border-gray-200 bg-white relative" ref={actionsDropdownRef}>
              <button
                onClick={() => setActionsDropdownOpen(!actionsDropdownOpen)}
                className="w-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-700 font-medium text-sm px-3 py-1.5 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation flex items-center justify-center space-x-1.5 border border-gray-200"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <span>Actions</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${actionsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {actionsDropdownOpen && (
                <div className="absolute top-full left-4 right-4 mt-1 z-50 bg-white rounded-xl shadow-lg border border-gray-200 py-1 overflow-hidden">
                  {!showMutedView && unreadCount > 0 ? (
                    <button
                      onClick={() => {
                        markAllUnreadAsRead();
                        setActionsDropdownOpen(false);
                      }}
                      className="w-full px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors flex items-center gap-2"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Mark All Read ({unreadCount})</span>
                    </button>
                  ) : null}
                  <button
                    onClick={() => {
                      setShowMutedView(!showMutedView);
                      setActionsDropdownOpen(false);
                    }}
                    className="w-full px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors flex items-center gap-2"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Ban className="h-4 w-4 text-gray-600" />
                    <span>{showMutedView ? 'Hide Spam' : 'Show Spam'}</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Conversation cards only */}
          <div
            className="messages-pipeline-list flex-1 min-h-0 overflow-y-auto overflow-x-hidden bg-white ios-momentum-scroll"
            style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}
          >
            {/* Spam Banner */}
            {showMutedView && (
              <div className="mx-3 mt-2 mb-1 px-3 py-2.5 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2">
                <Ban className="h-4 w-4 text-red-500 flex-shrink-0" />
                <span className="text-sm font-semibold text-red-700">Viewing Spam</span>
                <span className="text-xs text-red-500 ml-auto">{filteredCards.length} sender{filteredCards.length !== 1 ? 's' : ''}</span>
              </div>
            )}

            {filteredCards.length > 0 ? (
              <>
                
                <div className="p-2 space-y-1">
                  {filteredCards.map((card) => (
                    <CommunicationCardComponent
                      key={card.id}
                      card={card}
                      searchQuery={pipelineSearchQuery || undefined}
                      onSnippetClick={(_, messageId) => setOpenMessageIdWhenSelected(messageId)}
                    />
                  ))}
                </div>
                
                {/* Load More */}
                <div className="flex flex-col gap-2 p-4 pb-20 sm:pb-4 border-t border-gray-200 bg-white">
                
                {/* Load More Cards Button */}
                {hasMoreCards && (
                    <button
                      onClick={loadMoreCards}
                      disabled={loadingMoreCards}
                      className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold px-4 py-2.5 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm flex items-center justify-center space-x-2"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      {loadingMoreCards ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-white/30 border-t-white"></div>
                          <span>Loading...</span>
                        </>
                      ) : (
                        <span>Load More ({allFilteredCards.length - pipelineOffset} remaining)</span>
                      )}
                    </button>
                )}
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full px-4 py-16">
                <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mb-4 border border-gray-200">
                  {activeTab === 'unread' || activeTab === 'unread_suppliers' ? (
                    <Mail className="h-8 w-8 text-gray-400" />
                  ) : (
                    <MessageCircle className="h-8 w-8 text-gray-400" />
                  )}
                </div>
                <p className="text-gray-500 text-center font-medium text-base">
                  No {activeTab} messages
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Conversation View */}
        <div className={`${
          selectedCard && !isMobile ? 'flex' : 'hidden md:flex'
        } flex-1 flex-col h-full min-h-0 w-full max-w-full overflow-hidden`}>
          {selectedCard && (
            <MessagesConversationView 
              key={selectedCard.id}
              card={selectedCard} 
              markMessageAsRead={markMessageAsRead}
              markMessagesAsRead={markMessagesAsRead}
              markMessagesAsUnread={markMessagesAsUnread}
              locallyReadMessages={locallyReadMessages}
              fetchMilestones={fetchCustomerMilestones}
              openMessageId={openMessageIdWhenSelected}
              onOpenMessageConsumed={() => setOpenMessageIdWhenSelected(null)}
            />
          )}
          {!selectedCard && (
            /* No Conversation Selected State */
            <div className="flex-1 flex items-center justify-center px-3 py-0 sm:px-6">
              <div className="text-center max-w-md bg-gray-50 rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 p-6 sm:p-12">
                <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-100 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-sm border border-gray-200">
                  <MessageCircle className="h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
                </div>
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                  No Conversation Selected
                </h3>
                <p className="text-gray-500 font-medium text-sm sm:text-lg">
                  Select a conversation from the pipeline to view messages here
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Customer Modals */}
      {selectedBusiness && (
        <>
          <EditCustomerModal
            isOpen={editCustomerModalOpen}
            onClose={() => {
              setEditCustomerModalOpen(false);
              setSelectedCustomerForEdit(null);
            }}
            onSuccess={handleCustomerSuccess}
            customer={selectedCustomerForEdit}
          />
          
          <CustomerModal
            isOpen={createCustomerModalOpen}
            onClose={() => {
              setCreateCustomerModalOpen(false);
              setCreateCustomerInitialData({});
            }}
            onSuccess={handleCustomerSuccess}
            businessId={selectedBusiness.id}
            initialData={createCustomerInitialData}
          />
          
          <CustomerDetailsModal
            isOpen={customerDetailsModalOpen}
            onClose={() => {
              setCustomerDetailsModalOpen(false);
              setSelectedCustomerForDetails(null);
            }}
            customer={selectedCustomerForDetails}
            onUpdate={handleCustomerSuccess}
          />
          
          <BookingInfoModal
            isOpen={bookingInfoModalOpen}
            onClose={() => {
              setBookingInfoModalOpen(false);
              setSelectedBooking(null);
            }}
            booking={selectedBooking}
            onUpdate={handleBookingUpdate}
            readOnly={false}
          />
          
          {/* Beta Payments Modal */}
          {selectedBookingForPayments && (
            <BetaPaymentsModal
              isOpen={betaPaymentsModalOpen}
              onClose={() => {
                setBetaPaymentsModalOpen(false);
                setSelectedBookingForPayments(null);
              }}
              bookingId={selectedBookingForPayments.id}
              bookingNumber={typeof selectedBookingForPayments.booking_number === 'number' 
                ? selectedBookingForPayments.booking_number 
                : parseInt(String(selectedBookingForPayments.booking_number)) || 0}
              bookingData={{
                total_amount: selectedBookingForPayments.total_amount || 0,
                customer: selectedBookingForPayments.customer ? {
                  id: selectedBookingForPayments.customer.id,
                  first_name: selectedBookingForPayments.customer.first_name || '',
                  last_name: selectedBookingForPayments.customer.last_name || '',
                  email: selectedBookingForPayments.customer.email || undefined,
                  phone: selectedBookingForPayments.customer.phone || undefined,
                  full_name: `${selectedBookingForPayments.customer.first_name || ''} ${selectedBookingForPayments.customer.last_name || ''}`.trim() || undefined
                } : undefined,
                payments: selectedBookingForPayments.payments || []
              }}
              booking={selectedBookingForPayments}
              onUpdate={() => {
                // Refresh messages if needed
                console.log('Payments updated');
              }}
            />
          )}
          
          {selectedProposal && (
            <ProposalDetailsModal
              isOpen={proposalDetailsModalOpen}
              onClose={() => {
                setProposalDetailsModalOpen(false);
                setSelectedProposal(null);
              }}
              proposal={selectedProposal}
              onUpdate={handleProposalUpdate}
            />
          )}
        </>
      )}
      
      {/* Mobile Conversation Modal */}
      <MobileConversationModal
        isOpen={mobileModalOpen}
        onClose={() => {
          setMobileModalOpen(false);
          setSelectedCard(null);
          // Clear URL deep-link params so the auto-open effect doesn't re-open the modal
          setUrlCustomerId(null);
          setUrlContact(null);
          setUrlCustomerName(null);
          // Clear "Back to CRM" / "Back to Booking" so messages list doesn't show that header (e.g. after opening from push)
          setCameFromCRM(false);
          setCameFromBooking(false);
          setSourcePage(null);
        }}
        card={selectedCard}
        markMessageAsRead={markMessageAsRead}
        locallyReadMessages={locallyReadMessages}
        fetchMilestones={fetchCustomerMilestones}
      />

      {/* Compose New Message Modal */}
      {composeModalOpen && createPortal(
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 p-4" style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}>
          <div
            className="absolute inset-0"
            onClick={() => {
              // Clear timeout if modal is closed
              if (lookupTimeoutRef.current) {
                clearTimeout(lookupTimeoutRef.current);
                lookupTimeoutRef.current = null;
              }
              setComposeModalOpen(false);
              setComposeType(null);
              setComposeContact('');
              setMatchedCustomer(null);
              setIsLookingUpCustomer(false);
              setMatchingCustomers([]);
              setShowCustomerDropdown(false);
            }}
          />
          <div
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            style={{ overflow: 'visible' }}
          >
            {!composeType ? (
              // Step 1: Choose SMS or Email
              <>
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50/60">
                  <h3 className="text-lg font-semibold text-gray-900">Compose New Message</h3>
                  <button
                    onClick={() => {
                      setComposeModalOpen(false);
                      setComposeType(null);
                      setComposeContact('');
                      setMatchedCustomer(null);
                      setMatchingCustomers([]);
                      setShowCustomerDropdown(false);
                    }}
                    className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="p-6 space-y-3">
                  <button
                    onClick={() => setComposeType('sms')}
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-left bg-white border border-gray-200 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <MessageCircle className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900">SMS</div>
                      <div className="text-xs text-gray-500 mt-0.5">Send a text message</div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  </button>
                  
                  <button
                    onClick={() => setComposeType('email')}
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-left bg-white border border-gray-200 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <Mail className="h-5 w-5 text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900">Email</div>
                      <div className="text-xs text-gray-500 mt-0.5">Send an email</div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  </button>
                </div>
              </>
            ) : (
              // Step 2: Enter contact and compose
              <>
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50/60">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setComposeType(null);
                        setComposeContact('');
                        setMatchedCustomer(null);
                        setMatchingCustomers([]);
                        setShowCustomerDropdown(false);
                      }}
                      className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <ArrowLeft className="h-5 w-5 text-gray-600" />
                    </button>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {composeType === 'sms' ? 'New SMS' : 'New Email'}
                    </h3>
                  </div>
                  <button
                    onClick={() => {
                      setComposeModalOpen(false);
                      setComposeType(null);
                      setComposeContact('');
                      setMatchedCustomer(null);
                    }}
                    className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="p-6 space-y-4" style={{ overflow: 'visible' }}>
                  {/* Contact Input */}
                  <div className="relative" style={{ overflow: 'visible', zIndex: 1 }}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {composeType === 'sms' ? 'Phone Number' : 'Email Address'}
                    </label>
                    <div className="relative">
                      <input
                        type={composeType === 'sms' ? 'tel' : 'email'}
                        value={composeContact}
                        onChange={(e) => handleComposeContactChange(e.target.value, composeType)}
                        onFocus={() => {
                          if (composeContact.trim().length >= 2) {
                            setShowCustomerDropdown(matchingCustomers.length > 0);
                          }
                        }}
                        onBlur={() => {
                          // Delay hiding dropdown to allow click on dropdown items
                          setTimeout(() => setShowCustomerDropdown(false), 200);
                        }}
                        placeholder={composeType === 'sms' ? 'Enter phone number or customer name' : 'Enter email address or customer name'}
                        className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                        style={{ 
                          fontSize: '16px',
                          minHeight: '44px',
                          WebkitTapHighlightColor: 'transparent'
                        }}
                      />
                      {isLookingUpCustomer && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                        </div>
                      )}
                      
                      {/* Customer Dropdown (for both email and SMS compose) */}
                      {showCustomerDropdown && matchingCustomers.length > 0 && (
                        <div className="absolute z-[100001] w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-60 overflow-y-auto">
                          {matchingCustomers.map((customer) => (
                            <button
                              key={customer.id}
                              type="button"
                              onClick={() => handleSelectCustomer(customer, composeType)}
                              onMouseDown={(e) => e.preventDefault()}
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 border-b border-gray-100 last:border-b-0"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <User className="h-4 w-4 text-blue-600" />
                    </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {customer.first_name} {customer.last_name}
                                </div>
                                {composeType === 'email' && customer.email && (
                                  <div className="text-xs text-gray-500 truncate mt-0.5">
                                    {customer.email}
                                  </div>
                                )}
                                {composeType === 'sms' && customer.phone && (
                                  <div className="text-xs text-gray-500 truncate mt-0.5">
                                    {customer.phone}
                                  </div>
                                )}
                                {composeType === 'email' && customer.phone && (
                                  <div className="text-xs text-gray-400 truncate">
                                    {customer.phone}
                                  </div>
                                )}
                                {composeType === 'sms' && customer.email && (
                                  <div className="text-xs text-gray-400 truncate">
                                    {customer.email}
                                  </div>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {matchedCustomer && !showCustomerDropdown && (
                      <div className="mt-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                        <User className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-900">
                          {matchedCustomer.first_name} {matchedCustomer.last_name}
                        </span>
                        <span className="text-xs text-blue-600">(Existing Customer)</span>
                      </div>
                    )}
                  </div>

                  {/* Composer */}
                  {composeContact.trim() && composeType === 'sms' && (
                    <ComposeMessageComposer
                      type={composeType}
                      contact={composeContact.trim()}
                      customer={matchedCustomer}
                      businessId={selectedBusiness?.id || ''}
                      onSendSuccess={async () => {
                        setComposeModalOpen(false);
                        setComposeType(null);
                        setComposeContact('');
                        setMatchedCustomer(null);
                        setMatchingCustomers([]);
                        setShowCustomerDropdown(false);
                        // Refresh messages after a short delay to allow message to be saved
                        setTimeout(() => {
                          refreshMessages();
                        }, 500);
                      }}
                      onSendError={(error) => {
                        console.error('Error sending message:', error);
                        alert(`Failed to send SMS. Please try again.`);
                      }}
                    />
                  )}
                  {composeContact.trim() && composeType === 'email' && (
                    <div className="pt-4">
                      <button
                        onClick={() => {
                          setStandaloneEmailAddress(composeContact.trim());
                          setStandaloneEmailCustomer(matchedCustomer);
                          setComposeModalOpen(false);
                          setComposeType(null);
                          setComposeContact('');
                          setMatchingCustomers([]);
                          setShowCustomerDropdown(false);
                          setStandaloneEmailComposeOpen(true);
                        }}
                        className="w-full px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-colors font-medium"
                      >
                        Continue to Email Composer
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
      
      {/* Standalone Email Compose Modal */}
      {standaloneEmailComposeOpen && createPortal(
        <div
          className="fixed inset-0 z-[100060] transition-opacity duration-200 bg-black/50"
          style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
          onClick={() => {
            setStandaloneEmailComposeOpen(false);
            setStandaloneEmailAddress('');
            setStandaloneEmailCustomer(null);
            setStandaloneEmailBody('');
            setStandaloneEmailSubject('');
            setStandaloneShowAIPrompt(false);
            setStandaloneAIPrompt('');
            setStandaloneAIMode(null);
            setStandaloneAIError(null);
            setStandaloneIsGeneratingAI(false);
          }}
        >
          <div
            className="bg-white transform transition-all duration-200 fixed inset-y-0 right-0 w-full sm:w-[560px] shadow-2xl flex flex-col translate-x-0"
            style={{
              top: 'env(safe-area-inset-top, 0px)',
              // Rise above the iOS keyboard (resize:none) so long drafts stay
              // scrollable — the keyboard otherwise covers the editor bottom.
              bottom: 'max(env(safe-area-inset-bottom, 0px), var(--keyboard-inset, 0px))',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50/60 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="text-sm font-semibold text-gray-800">New Message</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setStandaloneSnippetsModalOpen(true);
                  }}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all duration-200 active:scale-[0.98]"
                  title="Insert snippet"
                >
                  <Plus className="h-4 w-4" />
                  <span>Snippets</span>
                </button>
                <button
                  onClick={() => {
                    setStandaloneEmailComposeOpen(false);
                    setStandaloneEmailAddress('');
                    setStandaloneEmailCustomer(null);
                    setStandaloneEmailBody('');
                    setStandaloneEmailSubject('');
                    setStandaloneShowAIPrompt(false);
                    setStandaloneAIPrompt('');
                    setStandaloneAIMode(null);
                    setStandaloneAIError(null);
                    setStandaloneIsGeneratingAI(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-1.5 rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="px-4 flex-shrink-0">
              <div className="flex items-center gap-2 border-b border-gray-200 py-2">
                <div className="text-base text-gray-500 w-10">To</div>
                <input
                  className="flex-1 text-base text-gray-800 bg-transparent outline-none"
                  style={{ fontSize: '16px' }}
                  value={standaloneEmailAddress}
                  readOnly
                />
              </div>
            </div>
            <div className="flex-1 min-h-0 flex flex-col relative">
              <EmailReplyComposer
                businessId={selectedBusiness?.id || ''}
                to={standaloneEmailAddress}
                defaultSubject=""
                placeholder="Compose"
                onSendSuccess={(data) => {
                  setStandaloneEmailComposeOpen(false);
                  setStandaloneEmailAddress('');
                  setStandaloneEmailCustomer(null);
                  setStandaloneEmailBody('');
                  setStandaloneEmailSubject('');
                  setStandaloneShowAIPrompt(false);
                  setStandaloneAIPrompt('');
                  setStandaloneAIMode(null);
                  setStandaloneAIError(null);
                  setStandaloneIsGeneratingAI(false);
                  setTimeout(() => { refreshMessages(); }, 500);
                }}
                onSendError={(error) => {
                  console.error('Error sending email:', error);
                  alert('Failed to send email. Please try again.');
                }}
                disabled={false}
                className="flex-1 min-h-0"
                variant="gmail"
                conversationId={undefined}
                customerId={standaloneEmailCustomer?.id || undefined}
                shouldFetchAI={false}
                showSubjectInput={true}
                subjectReadOnly={false}
                toolbarPosition="bottom"
                onInsertContent={(insertFn) => {
                  standaloneEmailInsertRef.current = insertFn;
                }}
                onSubjectInsert={(insertFn) => {
                  standaloneEmailSubjectInsertRef.current = insertFn;
                }}
                onAICompose={(currentBody, currentSubject) => {
                  setStandaloneEmailBody(currentBody);
                  setStandaloneEmailSubject(currentSubject);
                  setStandaloneShowAIPrompt(true);
                }}
                isAIGenerating={standaloneIsGeneratingAI}
              />

              {standaloneShowAIPrompt && standaloneEmailBody.trim() && !standaloneAIMode && (
                <div className="absolute bottom-16 right-4 z-50">
                  <div className="bg-gray-800 rounded-2xl shadow-2xl border border-gray-700 py-2 min-w-[200px]">
                    <button type="button" onClick={() => setStandaloneAIMode('compose')} className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-gray-700 flex items-center gap-2 transition-colors">
                      <Sparkles className="h-4 w-4 text-blue-400" /><span>Compose</span>
                    </button>
                    <button type="button" onClick={() => { setStandaloneShowAIPrompt(false); handleStandaloneGenerateAIEmail('autocomplete'); }} disabled={standaloneIsGeneratingAI} className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-gray-700 flex items-center gap-2 transition-colors disabled:opacity-50">
                      <svg className="h-4 w-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg><span>Auto Complete</span>
                    </button>
                    <button type="button" onClick={() => setStandaloneAIMode('polish')} className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-gray-700 flex items-center gap-2 transition-colors">
                      <svg className="h-4 w-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg><span>Polish</span>
                    </button>
                    <div className="border-t border-gray-700 my-1"></div>
                    <button type="button" onClick={() => { setStandaloneShowAIPrompt(false); setStandaloneAIPrompt(''); setStandaloneAIMode(null); setStandaloneAIError(null); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-400 hover:bg-gray-700 hover:text-gray-300 transition-colors">Cancel</button>
                  </div>
                </div>
              )}

              {standaloneShowAIPrompt && (standaloneAIMode === 'compose' || standaloneAIMode === 'polish' || !standaloneEmailBody.trim()) && (
                <div className="absolute bottom-16 left-3 right-3 z-50 p-4 bg-gray-800 rounded-2xl shadow-2xl">
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      value={standaloneAIPrompt}
                      onChange={(e) => setStandaloneAIPrompt(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !standaloneIsGeneratingAI) {
                          if ((standaloneAIMode === 'compose' || !standaloneEmailBody.trim()) && !standaloneAIPrompt.trim()) return;
                          e.preventDefault();
                          handleStandaloneGenerateAIEmail(standaloneAIMode || 'compose');
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          setStandaloneShowAIPrompt(false); setStandaloneAIPrompt(''); setStandaloneAIMode(null); setStandaloneAIError(null);
                        }
                      }}
                      placeholder={standaloneAIMode === 'polish' ? 'Optional: e.g. make it longer, more formal...' : 'E.g. write a thank you email...'}
                      className="flex-1 bg-transparent text-white placeholder-gray-400 text-base focus:outline-none py-1"
                      style={{ fontSize: '16px' }}
                      autoFocus
                      autoComplete="off"
                      disabled={standaloneIsGeneratingAI}
                    />
                    {!standaloneIsGeneratingAI ? (
                      <button
                        type="button"
                        onClick={() => { const mode = standaloneAIMode || 'compose'; if ((mode === 'compose' || !standaloneEmailBody.trim()) && !standaloneAIPrompt.trim()) return; handleStandaloneGenerateAIEmail(mode); }}
                        disabled={(standaloneAIMode === 'compose' || !standaloneEmailBody.trim()) && !standaloneAIPrompt.trim()}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >Generate</button>
                    ) : (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    )}
                  </div>
                  {standaloneAIError && (
                    <div className="mt-3 px-3 py-2 bg-red-500/20 border border-red-400/30 text-red-200 text-sm rounded-lg">{standaloneAIError}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Snippets Modal for Standalone Email Compose */}
      {selectedBusiness && (
        <SnippetsModal
          isOpen={standaloneSnippetsModalOpen}
          onClose={() => {
            setStandaloneSnippetsModalOpen(false);
          }}
          onSelectSnippet={async (snippet) => {
            // Substitute variables here too (this composer used to insert raw
            // tokens). Lead data is fetched on demand only when the snippet
            // actually uses lead variables.
            let lead = null;
            if (contentHasLeadVariables(snippet.content) && standaloneEmailCustomer?.id && selectedBusiness?.id) {
              try {
                const opps = await getOpenOpportunitiesForCustomerIds(selectedBusiness.id, [standaloneEmailCustomer.id]);
                lead = opps[0] || null;
              } catch (err) {
                console.error('Error loading lead for snippet variables:', err);
              }
            }
            const { text, missingLeadVariables } = substituteSnippetVariables(snippet.content, {
              customer: standaloneEmailCustomer,
              lead,
            });
            if (missingLeadVariables.length > 0) {
              toast(
                standaloneEmailCustomer
                  ? `No open lead for this customer — ${missingLeadVariables.join(', ')} left blank`
                  : `Pick a customer recipient to fill lead variables — ${missingLeadVariables.join(', ')} left blank`,
                { icon: '⚠️' }
              );
            }
            if (standaloneEmailInsertRef.current) {
              standaloneEmailInsertRef.current(text, false);
            }
            setStandaloneSnippetsModalOpen(false);
          }}
          businessId={selectedBusiness.id}
        />
      )}
      
      {/* Token Purchase Modal */}
      <TokenPurchaseModal
        isOpen={showTokenPurchaseModal}
        onClose={() => setShowTokenPurchaseModal(false)}
        businessId={selectedBusiness?.id || ''}
        onSuccess={() => {
          setShowTokenPurchaseModal(false);
          alert('Tokens added successfully! You can now use AI features.');
        }}
      />
      
      {showGmailReauthModal && (
        <GmailReauthModal
          email={gmailReauthEmail}
          connecting={gmailReauthConnecting}
          onRefresh={refreshGmailConnection}
          onDismiss={dismissGmailReauth}
        />
      )}
    </div>
    </MessagesConversationOutletContext.Provider>
  );
} 